import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./context/AuthContext";
import SubscriptionExpiredModal from "./components/SubscriptionExpiredModal";
import NotificationBell from "./components/NotificationBell";
import SubscriptionBanner from "./components/SubscriptionBanner";
import LanguageSwitcher from "./components/LanguageSwitcher";
import MobileBottomSheet from "./components/MobileBottomSheet";
import { AdBanner, AdCardInline, AdCardInlineSlot, AdSidebarLeft, AdSidebarRight, AdPageBanner, AdInterstitial, AudioAdPlayer, useGeoLocation } from "./components/AdComponents";
import { DetectAdblock } from "@scthakuri/adblock-detector";
import { 
  fetchTracks, 
  fetchCategories,
  fetchMoodCategories,
  fetchSectionTracks,
  fetchTopTracksByStreams,
  fetchPlaylists, 
  fetchLikedTracksPersonalized, 
  fetchRecommendations, 
  fetchRecentlyPlayed,
  fetchHomeHistory,
  likeTrackPersonalized,
  unlikeTrack,
  recordPlay,
  fetchUserLibrary,
  createPlaylist,
  createFolder,
  addTrackToPlaylist,
  removeTrackFromPlaylist,
  fetchAdminPlaylists,
  trackAdPlayed,
  fetchStories,
  fetchStoryStatus,
  reportStory,
  deleteStory
} from "./services/api";
import AuthModal from "./components/AuthModal";
import CreateMenu from "./components/CreateMenu";
import TrackContextMenu from "./components/TrackContextMenu";
import CreatorStudio from "./components/CreatorStudio";
import StorySection from "./components/StorySection";
import StoryViewer from "./components/StoryViewer";
import NativeAudioPlayer from "./plugins/nativeAudioPlayer";

// Icône couronne pour les pistes premium
const PremiumCrown = ({ className = "" }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={`text-yellow-500 ${className}`}
    style={{ filter: 'drop-shadow(0 0 2px rgba(234, 179, 8, 0.5))' }}
  >
    <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
  </svg>
);

const shuffleArray = (array) => {
  if (!Array.isArray(array)) return [];
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_AVATAR = '/default-avatar.svg';
const DEFAULT_COVER = '/default-cover.svg';

// Composant Equalizer animé pour la piste en cours de lecture
function PlayingEqualizer({ isPlaying }) {
  return (
    <div className="flex items-end gap-0.5 h-4 w-4">
      <span 
        className={`w-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-sm ${isPlaying ? 'animate-equalizer-1' : 'h-1'}`}
        style={{ animationPlayState: isPlaying ? 'running' : 'paused' }}
      />
      <span 
        className={`w-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-sm ${isPlaying ? 'animate-equalizer-2' : 'h-2'}`}
        style={{ animationPlayState: isPlaying ? 'running' : 'paused' }}
      />
      <span 
        className={`w-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-sm ${isPlaying ? 'animate-equalizer-3' : 'h-1'}`}
        style={{ animationPlayState: isPlaying ? 'running' : 'paused' }}
      />
    </div>
  );
}


// Fonction utilitaire pour obtenir l'URL d'une image (CDN ou locale)
function getImageUrl(imagePath, type = 'images') {
  if (!imagePath) return null;
  // Si c'est déjà une URL complète (CDN), l'utiliser directement
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // Sinon, construire l'URL locale
  const filename = imagePath.split('/').pop();
  if (type === 'playlist') {
    return `${BACKEND_URL}/api/playlists/cover/${filename}`;
  }
  return `${BACKEND_URL}/api/images/${filename}`;
}

function getUserImageUrl(imagePath) {
  if (!imagePath) return DEFAULT_AVATAR;
  return getImageUrl(imagePath) || DEFAULT_AVATAR;
}

function getUserCoverUrl(imagePath) {
  if (!imagePath) return DEFAULT_COVER;
  return getImageUrl(imagePath) || DEFAULT_COVER;
}

// Helpers
function formatDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function ProgressBar({ progress }) {
  return (
    <div className="w-full h-1.5 bg-white/15 rounded-full overflow-hidden">
      <div
        className="h-full bg-white rounded-full transition-all duration-150"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

// Toast notification component
function Toast({ message, isVisible, onClose }) {
  const timerRef = useRef(null);
  
  useEffect(() => {
    if (isVisible) {
      // Nettoyer le timer existant
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // Créer un nouveau timer
      timerRef.current = setTimeout(() => {
        onClose();
      }, 3000);
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isVisible]); // Ne pas inclure onClose dans les dépendances

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-[30000] pointer-events-none">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg shadow-2xl flex items-center gap-1 animate-fade-in">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <span className="font-medium">{message}</span>
      </div>
    </div>
  );
}

// Modale de confirmation de suppression
function DeleteConfirmModal({ isOpen, onClose, onConfirm, playlistName, isLoading }) {
  const { t } = useTranslation();
if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 modal-text">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-zinc-900 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-white/10 animate-scale-in">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-red-500/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        
        {/* Title */}
        <h3 className="text-xl font-bold text-white text-center mb-2">
          {t('playlist.deleteConfirmTitle') || 'Supprimer la playlist ?'}
        </h3>
        
        {/* Message */}
        <p className="text-white/60 text-center mb-3">
          {t('playlist.deleteConfirmMessage') || 'Êtes-vous sûr de vouloir supprimer'} <span className="text-white font-semibold">"{playlistName}"</span> ? 
          <br />
          <span className="text-sm">{t('playlist.deleteConfirmWarning') || 'Cette action est irréversible et supprimera tous les titres de cette playlist.'}</span>
        </p>
        
        {/* Buttons */}
        <div className="flex gap-1">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium transition-colors disabled:opacity-50"
          >
            {t('common.cancel') || 'Annuler'}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
          >
            {isLoading ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span>{t('common.deleting') || 'Suppression...'}</span>
              </>
            ) : (
              <span>{t('common.delete') || 'Supprimer'}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal d'information (succès)
function InfoModal({ isOpen, onClose, title, message }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 modal-text">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-white/10 animate-scale-in">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-r from-purple-600/30 to-pink-600/30 flex items-center justify-center">
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
        {title && <h3 className="text-lg font-bold text-white text-center mb-2">{title}</h3>}
        <p className="text-white/70 text-center text-sm mb-4">{message}</p>
        <button
          onClick={onClose}
          className="w-full py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-700 hover:to-pink-700 transition"
        >
          OK
        </button>
      </div>
    </div>
  );
}

// Modal Premium - Affichée lorsqu'un utilisateur gratuit essaie de lire une piste premium
function PremiumModal({ isOpen, onClose, track }) {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 modal-text">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-2xl shadow-2xl max-w-md w-full p-8 border border-yellow-500/30 animate-scale-in">
        {/* Crown Icon */}
        <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 flex items-center justify-center">
          <PremiumCrown className="w-10 h-10" />
        </div>
        
        {/* Title */}
        <h3 className="text-2xl font-bold text-white text-center mb-3">
          {t('premium.contentLocked') || 'Contenu Premium'}
        </h3>
        
        {/* Track info */}
        {track && (
          <div className="flex items-center gap-1 bg-white/5 rounded-lg p-3 mb-2">
            {track.cover_image ? (
              <img 
                src={track.cover_image.startsWith('http') ? track.cover_image : `${process.env.REACT_APP_BACKEND_URL}${track.cover_image}`}
                alt={track.title}
                className="w-12 h-12 rounded object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xl">
                {track.cover_emoji || '🎵'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-white truncate">{track.title}</div>
              <div className="text-sm text-white/60 truncate">{track.artist}</div>
            </div>
          </div>
        )}
        
        {/* Message */}
        <p className="text-white/70 text-center mb-3">
          {t('premium.upgradeMessage') || 'Cette piste est réservée aux abonnés Premium. Passez à Premium pour profiter de tous les contenus exclusifs sans limitation.'}
        </p>
        
        {/* Benefits */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-1 text-sm text-white/80">
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{t('premium.benefit1') || 'Accès illimité à toutes les pistes'}</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-white/80">
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{t('premium.benefit2') || 'Qualité audio supérieure'}</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-white/80">
            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{t('premium.benefit3') || 'Sans publicité'}</span>
          </div>
        </div>
        
        {/* Buttons */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => {
              // TODO: Rediriger vers la page d'abonnement
              onClose();
            }}
            className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-1"
          >
            <PremiumCrown className="w-5 h-5 text-black" />
            {t('premium.upgrade') || 'Passer à Premium'}
          </button>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            {t('common.later') || 'Plus tard'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal AdBlock - Affichée lorsqu'un utilisateur gratuit a un bloqueur de pub actif
function AdBlockModal({ isOpen, onClose, onCheckAgain }) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 modal-text" data-adblock-allow="true">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-adblock-allow="true"
      />

      {/* Modal */}
      <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-2xl shadow-2xl max-w-md w-full p-8 border border-purple-500/30 animate-scale-in">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-purple-600/20 flex items-center justify-center">
          <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 6h18v2H3V6zm2 4h14v8H5v-8zm2 2v4h10v-4H7z" />
          </svg>
        </div>

        <h3 className="text-2xl font-bold text-white text-center mb-3">
          {t('ads.blockedTitle') || 'Bloqueur de publicité détecté'}
        </h3>

        <p className="text-white/70 text-center mb-4">
          {t('ads.blockedMessage') ||
            'Pour continuer à utiliser Vinisys Music gratuitement, merci de désactiver votre bloqueur de publicités. Les pubs financent le service.'}
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={onCheckAgain}
            className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white font-bold transition-all hover:scale-[1.02]"
            data-adblock-allow="true"
          >
            {t('ads.checkAgain') || "J'ai désactivé, vérifier"}
          </button>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
            data-adblock-allow="true"
          >
            {t('common.close') || 'Fermer'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---- Left library sidebar ----
function LeftSidebar({ onSelectCollection, currentCollectionId, onPlayTrack, currentTrack, user, onCreateClick, likedTracks, userPlaylists, recentlyPlayed, isPlaying, onOpenArtist, onDeletePlaylist, userIsPremium = false, mobileBottomPadding = '' }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState(null); // null = aucun filtre actif
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, playlist: null });
  const [playlistMenu, setPlaylistMenu] = useState(null); // Menu contextuel des 3 points
  const [isDeleting, setIsDeleting] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Créer la liste des éléments à afficher selon le filtre
  const getLibraryItems = () => {
    const allItems = [];
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    // 1. Titres likés
    if (likedTracks && likedTracks.length > 0) {
      allItems.push({
        id: 'liked-tracks',
        type: 'collection',
        category: 'playlist',
        title: t('library.likedTracks'),
        subtitle: `${likedTracks.length} ${t('artist.tracks')}`,
        emoji: '💚',
        color: 'from-green-500 to-green-600'
      });
    }

    // 2. Playlists utilisateur
    if (userPlaylists && userPlaylists.length > 0) {
      userPlaylists.forEach(playlist => {
        allItems.push({
          id: `user-playlist-${playlist.id}`,
          type: 'user-playlist',
          category: 'playlist',
          playlistId: playlist.id,
          title: playlist.name,
          subtitle: `${t('playlist.title')} • ${user?.email?.split('@')[0] || t('playlist.by')}`,
          emoji: playlist.cover_emoji || '🎵',
          color: 'from-purple-600 to-blue-600'
        });
      });
    }

    // 3. Artistes uniques (extraits des pistes récemment écoutées)
    // Les artistes ne sont ajoutés que si le filtre "Artistes" est actif
    if (recentlyPlayed && recentlyPlayed.length > 0 && filter === "artists") {
      const uniqueArtists = {};
      recentlyPlayed.forEach(track => {
        // Récupérer le nom de l'artiste depuis track.artist
        const artistName = track.artist;
        if (artistName && !uniqueArtists[artistName]) {
          uniqueArtists[artistName] = {
            id: `artist-${artistName.toLowerCase().replace(/\s+/g, '-')}`,
            type: 'artist',
            category: 'artist',
            name: artistName,
            title: artistName,
            subtitle: '',
            // Utiliser profile_image de l'artiste si disponible, sinon couverture de la piste
            coverImage: track.artist_profile_image || track.cover_image || null,
            emoji: '🎤',
            track: track
          };
        }
      });
      
      // Ajouter les artistes uniques (maximum 10)
      Object.values(uniqueArtists).slice(0, 10).forEach(artist => {
        allItems.push(artist);
      });
    }
    
    // 4. Singles les plus récents écoutés (pistes individuelles) - limité à 10
    if (recentlyPlayed && recentlyPlayed.length > 0) {
      recentlyPlayed.slice(0, 10).forEach(track => {
        allItems.push({
          id: `recent-${track.id}`,
          type: 'track',
          category: 'album',
          track: track,
          title: track.title,
        subtitle: `${track.artist || t('artist.title')}`,
          coverImage: track.cover_image,
          emoji: track.cover_emoji || '🎵'
        });
      });
    }

    // Filtrer selon le filtre actif
    if (filter) {
      if (filter === "playlists") {
        return allItems.filter(item => item.category === 'playlist');
      } else if (filter === "artists") {
        return allItems.filter(item => item.category === 'artist');
      } else if (filter === "albums") {
        return allItems.filter(item => item.category === 'album');
      }
    }

    // Si aucun filtre, retourner tout
    return allItems;
  };
  
  const handleFilterClick = (filterName) => {
    // Toggle : si on clique sur le même filtre, on le désactive
    if (filter === filterName) {
      setFilter(null);
    } else {
      setFilter(filterName);
    }
  };
  
  const clearFilter = () => {
    setFilter(null);
  };

  const libraryItems = getLibraryItems();


  const handleItemClick = (item) => {
    if (item.type === 'track') {
      // Jouer la piste directement
      onPlayTrack(item.track, null);
    } else if (item.type === 'artist') {
      // Pour un artiste, ouvrir la page artiste
      if (onOpenArtist && item.track) {
        onOpenArtist(item.track);
      }
    } else if (item.type === 'collection' || item.type === 'user-playlist') {
      // Ouvrir la collection ou la playlist
      onSelectCollection(item);
    }
  };

  // Filter labels for translation
  const filterLabels = {
    playlists: t('library.playlists'),
    artists: t('library.artists'),
    albums: t('library.albums')
  };

  return (
    <aside className="w-full lg:w-80 bg-black border-r border-white/10 flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="p-4 flex items-center justify-between flex-shrink-0">
        <button className="flex items-center gap-1 text-white/70 hover:text-white">
          <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
            <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
              <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
            </svg>
          </div>
          <span className="font-semibold text-base">{t('library.title')}</span>
        </button>
        <div className="flex items-center gap-1">
          <button 
            onClick={onCreateClick}
            className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-sm"
          >
            <span className="text-lg">+</span>
            <span>{t('nav.create')}</span>
          </button>
          {/* Bouton fermer visible uniquement sur mobile */}
          <button 
            onClick={() => window.dispatchEvent(new CustomEvent('closeMobileLibrary'))}
            className="lg:hidden p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 pb-3 flex gap-1 items-center flex-shrink-0">
        {["playlists", "artists", "albums"].map((tab) => {
          const active = filter === tab;
          return (
            <button
              key={tab}
              onClick={() => handleFilterClick(tab)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                active
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md"
                  : "bg-white/10 text-white/70 hover:text-white hover:bg-white/15"
              }`}
            >
              {filterLabels[tab]}
            </button>
          );
        })}
        
        {/* Croix pour réinitialiser le filtre */}
        {filter && (
          <button
            onClick={clearFilter}
            className="ml-auto p-1.5 rounded-full hover:bg-white/10 transition-colors"
            title={t('filters.clearFilter')}
          >
            <svg className="w-4 h-4 text-white/70 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search + sort */}
      <div className="px-4 pb-3 flex items-center justify-between text-white/60 flex-shrink-0">
        <button className="hover:text-white">
 
        </button>
        <button className="flex items-center gap-1 text-sm hover:text-white">
          <span>{t('library.recents')}</span>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
          </svg>
        </button>
      </div>

      {/* List of items - Scrollable */}
      <div
        className="flex-1 overflow-y-auto px-2 min-h-0"
        style={mobileBottomPadding ? { paddingBottom: mobileBottomPadding } : undefined}
      >
        {libraryItems.length === 0 ? (
          <div className="text-center text-white/60 text-sm py-8 px-4">
            {filter === "playlists" ? t('library.createFirstPlaylist') : t('library.noItems')}
          </div>
        ) : (
          <>
            {/* Publicité sidebar gauche - tout en haut de la liste */}
            <div className="mb-2">
              <AdSidebarLeft userIsPremium={userIsPremium} />
            </div>
            {libraryItems.map((item, index) => {
            const isActive = item.type === 'collection' && item.id === currentCollectionId;
            const isCurrentTrack = item.type === 'track' && currentTrack?.id === item.track?.id;
            const isTrackPlaying = isCurrentTrack && isPlaying;
            const isPlaylist = item.type === 'user-playlist';
            const showAdAfter = (index + 1) % 5 === 0;
            
            return (
              <React.Fragment key={item.id}>
                <div
                  className={`w-full p-1 rounded flex items-center gap-1 mb-1 hover:bg-white/10 transition-colors group ${
                    isActive || isCurrentTrack ? "bg-white/10" : ""
                  }`}
                >
                {/* Cover image or emoji - Clickable */}
                <button
                  onClick={() => handleItemClick(item)}
                  className="w-12 h-12 rounded flex-shrink-0 overflow-hidden relative"
                >
                  {item.coverImage ? (
                    <img loading="lazy" decoding="async"
                      src={getImageUrl(item.coverImage)}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${item.color || 'from-gray-600 to-gray-800'} flex items-center justify-center text-xl`}>
                      {item.emoji}
                    </div>
                  )}
                  
                  {/* Play/Pause indicator for tracks */}
                  {item.type === 'track' && isCurrentTrack && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <svg className="w-6 h-6 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                        {isPlaying ? (
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        ) : (
                          <path d="M8 5v14l11-7z"/>
                        )}
                      </svg>
                    </div>
                  )}
                </button>
                
                {/* Info - Clickable */}
                <button
                  onClick={() => handleItemClick(item)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className={`text-sm font-medium truncate ${isCurrentTrack ? 'text-pink-400' : 'text-white'}`}>
                    {item.title}
                  </div>
                  <div className="text-xs text-white/60 truncate flex items-center gap-1">
                    {item.type === 'collection' && (
                      <svg className="w-3 h-3 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                      </svg>
                    )}
                    <span>{item.subtitle}</span>
                  </div>
                </button>

                {/* 3 dots menu for playlists - always visible on mobile, visible on hover on desktop */}
                {isPlaylist && (
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Toggle le menu contextuel
                        if (playlistMenu?.id === item.playlistId) {
                          setPlaylistMenu(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setPlaylistMenu({
                            id: item.playlistId,
                            playlist: item,
                            position: {
                              x: rect.left - 160, // Ouvrir à gauche
                              y: rect.bottom + 4
                            }
                          });
                        }
                      }}
                      className="hidden md:flex md:opacity-0 md:group-hover:opacity-100 p-1 hover:bg-white/20 active:bg-white/30 rounded-full transition-all"
                      title={t('playlist.delete') || 'Supprimer la playlist'}
                    >
                      <svg className="w-5 h-5 text-white/70 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                  </div>
                )}
                </div>
                {showAdAfter && (
                  <div className="my-2">
                    <AdSidebarLeft userIsPremium={userIsPremium} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
          </>
        )}
      </div>

      {/* Menu contextuel des playlists */}
      {playlistMenu && (
        <>
          {/* Overlay pour fermer le menu */}
          <div 
            className="fixed inset-0 z-[60]"
            onClick={() => setPlaylistMenu(null)}
          />
          {/* Menu */}
          <div
            className="fixed bg-zinc-800 rounded-lg shadow-2xl border border-white/10 py-1 min-w-[160px] z-[61]"
            style={{
              top: playlistMenu.position.y,
              left: Math.max(8, playlistMenu.position.x) // Éviter de sortir de l'écran
            }}
          >
            <button
              onClick={() => {
                setDeleteModal({ isOpen: true, playlist: playlistMenu.playlist });
                setPlaylistMenu(null);
              }}
              className="w-full px-4 py-3 hover:bg-white/10 text-left flex items-center gap-1 text-red-400 hover:text-red-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>{t('playlist.deletePlaylist') || 'Supprimer la playlist'}</span>
            </button>
          </div>
        </>
      )}

      {/* Modal de confirmation de suppression */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, playlist: null })}
        onConfirm={async () => {
          if (deleteModal.playlist && onDeletePlaylist) {
            setIsDeleting(true);
            try {
              await onDeletePlaylist(deleteModal.playlist.playlistId);
              setDeleteModal({ isOpen: false, playlist: null });
            } catch (error) {
              console.error('Error deleting playlist:', error);
            } finally {
              setIsDeleting(false);
            }
          }
        }}
        playlistName={deleteModal.playlist?.title}
        isLoading={isDeleting}
      />
    </aside>
  );
}

// ---- Top bar ----
function TopBar({ searchQuery, onSearchChange, canGoBack, onBack, user, onLogin, onLogout, isAdmin, onAdminClick, onProfileClick, onAccountClick, notifications, onMarkNotificationAsRead, onNotificationClick, onGoHome, navigate, onLanguageClick, onCreatorStudioClick, showCreatorStudio }) {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showMobileSearch, setShowMobileSearch] = React.useState(false);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const isIOS = useMemo(() => {
    if (Capacitor?.isNativePlatform?.()) {
      return Capacitor.getPlatform?.() === 'ios';
    }
    return /iPad|iPhone|iPod/.test(navigator.userAgent || '');
  }, []);

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    if (isMobile) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showCreatorStudio && showMobileSearch) {
      setShowMobileSearch(false);
    }
  }, [showCreatorStudio, showMobileSearch]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      if (!headerRef.current) return;
      const height = headerRef.current.getBoundingClientRect().height;
      if (height) {
        document.documentElement.style.setProperty('--topbar-height', `${height}px`);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);
  
  const userMenuContent = isMobile ? (
    <div className="flex flex-col" style={{ minHeight: 'calc(45vh - 24px)' }}>
      <div className="flex flex-col">
        <button
          onClick={() => {
            setShowUserMenu(false);
            onAccountClick();
          }}
          className="w-full px-4 py-3 text-left text-base hover:bg-white/10 transition-colors flex items-center gap-3"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          {t('profile.personalInfo')}
        </button>
        <button
          onClick={() => {
            setShowUserMenu(false);
            onProfileClick();
          }}
          className="w-full px-4 py-3 text-left text-base hover:bg-white/10 transition-colors flex items-center gap-3"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
          </svg>
          {t('nav.profile')}
        </button>
        <button
          onClick={() => {
            setShowUserMenu(false);
            onLanguageClick();
          }}
          className="w-full px-4 py-3 text-left text-base hover:bg-white/10 transition-colors flex items-center gap-3"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
          </svg>
          {t('nav.language')}
        </button>
      </div>

      <div className="mt-auto pt-2">
        <button
          onClick={() => {
            setShowUserMenu(false);
            onLogout();
          }}
          className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold transition-all"
        >
          {t('auth.logout')}
        </button>
      </div>
    </div>
  ) : (
    <>
      <button
        onClick={() => {
          setShowUserMenu(false);
          onAccountClick();
        }}
        className="w-full px-4 py-3 text-left text-base md:text-sm hover:bg-white/10 transition-colors flex items-center gap-3"
      >
        <svg className="w-6 h-6 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
        {t('profile.personalInfo')}
      </button>
      <button
        onClick={() => {
          setShowUserMenu(false);
          onProfileClick();
        }}
        className="w-full px-4 py-3 text-left text-base md:text-sm hover:bg-white/10 transition-colors flex items-center gap-3"
      >
        <svg className="w-6 h-6 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
        </svg>
        {t('nav.profile')}
      </button>
      <button
        onClick={() => {
          setShowUserMenu(false);
          onLanguageClick();
        }}
        className="w-full px-4 py-3 text-left text-base md:text-sm hover:bg-white/10 transition-colors flex items-center gap-3"
      >
        <svg className="w-6 h-6 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
        </svg>
        {t('nav.language')}
      </button>
      <div className="border-t border-white/10 my-1"></div>
      <button
        onClick={() => {
          setShowUserMenu(false);
          onLogout();
        }}
        className="w-full px-4 py-3 text-left text-base md:text-sm hover:bg-white/10 transition-colors flex items-center gap-3"
      >
        <svg className="w-6 h-6 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
        </svg>
        {t('auth.logout')}
      </button>
    </>
  );

  return (
    <>
      <header
        ref={headerRef}
        className="flex items-center justify-between px-4 md:px-6 bg-black relative z-50"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          minHeight: '64px'
        }}
      >
        {/* Logo seul à gauche */}
        <div className="flex items-center">
          <button 
            onClick={onGoHome}
            className="flex items-center justify-center"
          >
            <img loading="lazy" decoding="async" src="/logo.png" alt="Vinisys Music" className="h-14 w-14 object-contain" />
          </button>
        </div>

        {/* Bouton Home + Barre de recherche au centre - Desktop uniquement */}
        {!showCreatorStudio && (
          <div className="hidden lg:flex flex-1 items-center justify-center gap-1 px-4 max-w-3xl">
            {/* Bouton Home */}
            <button 
              onClick={onGoHome}
              className="w-[50px] h-[50px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </button>

            {/* Barre de recherche */}
            <div className="w-full h-11 bg-[#242424] rounded-full flex items-center px-4 gap-1">
              <svg className="w-5 h-5 flex-shrink-0" fill="white" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('search.placeholder')}
                className="flex-1 bg-transparent outline-none text-base placeholder:text-white/60 text-white"
              />
              {searchQuery && (
                <button onClick={() => onSearchChange("")} className="text-white/60 hover:text-white">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bouton loupe - Mobile uniquement */}
        {!showCreatorStudio && (
        <div className="lg:hidden flex-1 flex justify-center">
          <button 
            onClick={() => setShowMobileSearch(!showMobileSearch)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </button>
        </div>
        )}

      {/* Icônes à droite */}
      <div className="flex items-center gap-1">
        {/* Creator Studio button */}
        {user && !showCreatorStudio && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onCreatorStudioClick) onCreatorStudioClick();
            }}
            className="hidden md:flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full text-sm font-semibold transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3l1.09 3.26L16.5 7l-2.91 1.74L12 12l-1.59-3.26L7.5 7l3.41-.74L12 3z" fill="currentColor"/>
              <path d="M18 12l.73 2.17L21 15l-1.93 1.16.73 2.17L18 17l-1.8 1.33.73-2.17L15 15l2.27-.83L18 12z" fill="currentColor"/>
              <path d="M6 14l.55 1.64L8 16.27l-1.27.85.55 1.63L6 18l-1.28.75.55-1.63L4 16.27l1.45-.63L6 14z" fill="currentColor"/>
            </svg>
            Creator Studio
          </button>
        )}

        {isAdmin && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onAdminClick) onAdminClick();
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-full text-base font-semibold transition-colors cursor-pointer"
          >
            Admin
          </button>
        )}
        
        {user && notifications && (
          <NotificationBell 
            notifications={notifications}
            onMarkAsRead={onMarkNotificationAsRead}
            onNotificationClick={onNotificationClick}
          />
        )}
        
        {user ? (
          <div className="relative" ref={menuRef}>
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all flex items-center justify-center"
              title="Menu utilisateur"
            >
              <img
                loading="lazy"
                decoding="async"
                src={getUserImageUrl(user.profile_image)}
                alt={user.email}
                className="w-full h-full object-cover"
              />
            </button>

            {showUserMenu && (
              isMobile ? (
                <MobileBottomSheet isOpen={showUserMenu} onClose={() => setShowUserMenu(false)}>
                  {userMenuContent}
                </MobileBottomSheet>
              ) : (
                <div className="absolute right-0 top-12 w-48 bg-[#282828] rounded-md shadow-2xl py-1 z-50 border border-white/10">
                  {userMenuContent}
                </div>
              )
            )}
          </div>
        ) : (
          <button 
            onClick={onLogin}
            className="px-4 py-2 bg-white text-black rounded-full text-base font-semibold hover:scale-105 transition-transform"
          >
            {t('auth.login')}
          </button>
        )}
      </div>
    </header>

      {/* Barre de recherche mobile flottante */}
      {showMobileSearch && !showCreatorStudio && (
        <div
          className="lg:hidden fixed left-0 right-0 bg-black border-b border-white/10 p-4 z-40"
          style={{ top: "var(--topbar-height, 72px)" }}
        >
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setShowMobileSearch(false)}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 h-11 bg-[#242424] rounded-full flex items-center px-4 gap-1">
              <svg className="w-5 h-5 flex-shrink-0" fill="white" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t('search.placeholder')}
                className="flex-1 bg-transparent outline-none text-base placeholder:text-white/60 text-white"
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => onSearchChange("")} className="text-white/60 hover:text-white">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- Home row - Latest Tracks ----
function HomeRow({ title, subtitle, tracks, onPlayTrack, onOpenArtist, onToggleLike, currentTrack, likedTrackIds = [], isPlaying, userIsPremium = false, adEvery = 3, showScrollArrows = false, onReachEnd }) {
  const { t } = useTranslation();
  const trackKey = (tracks || []).map((track) => track.id).join('|');
  const shuffledTracks = useMemo(() => shuffleArray(tracks || []), [trackKey]);
  const adOffset = useMemo(() => Math.floor(Math.random() * 4), [trackKey]);

  const [hasScrolledRight, setHasScrolledRight] = useState(false);
  const homeRowScrollRef = useRef(null);
  const hasRequestedAllRef = useRef(false);

  const scrollHomeRow = useCallback((direction) => {
    const el = homeRowScrollRef.current;
    if (!el) return;

    const cardWidth = 200; // approx card width
    const gap = 12;
    const delta = (cardWidth + gap) * 3; // ~3 cards

    if (direction === 'right') setHasScrolledRight(true);
    el.scrollBy({ left: direction === 'left' ? -delta : delta, behavior: 'smooth' });
  }, []);
  const handleRowScroll = useCallback(() => {
    const el = homeRowScrollRef.current;
    if (!el || !onReachEnd || hasRequestedAllRef.current) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 120;
    if (nearEnd) {
      hasRequestedAllRef.current = true;
      onReachEnd();
    }
  }, [onReachEnd]);
  if (!shuffledTracks || shuffledTracks.length === 0) return null;

  // Insérer une pub après l'index spécifié
  const renderItems = () => {
    const items = [];
    const adFrequency = Math.max(1, Number(adEvery) || 0);
    shuffledTracks.forEach((track, index) => {
      items.push(
        <article
          key={track.id}
          className={`min-w-[180px] max-w-[200px] bg-transparent rounded-lg flex flex-col hover:bg-white/10 cursor-pointer transition-all group relative ${
            currentTrack?.id === track.id ? 'ring-2 ring-pink-500/70' : ''
          }`}
          onClick={() => onPlayTrack(track)}
        >
          <div className="w-full aspect-square rounded-t-lg overflow-hidden relative">
            {track.cover_image ? (
              <img loading="lazy" decoding="async"
                src={getImageUrl(track.cover_image)}
                alt={track.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">
                {track.cover_emoji || '🎵'}
              </div>
            )}
            
            {/* Icône Premium - visible uniquement pour les comptes gratuits */}
            {!!track.is_premium && !userIsPremium && (
              <div className="absolute top-1 right-2 z-10">
                <PremiumCrown className="w-6 h-6" />
              </div>
            )}

            {/* Like button - toujours visible (mobile inclus), en haut à gauche */}
            {onToggleLike && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLike(track.id);
                }}
                className={`absolute top-2 left-2 z-10 text-xl transition-transform hover:scale-110 ${
                  likedTrackIds.includes(track.id) ? 'text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-white/70 hover:text-white'
                }`}
                title={likedTrackIds.includes(track.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              >
                {likedTrackIds.includes(track.id) ? '💚' : '🤍'}
              </button>
            )}
            
            {/* Play button - bas à droite, masqué sur mobile, visible au survol sur desktop */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlayTrack(track);
              }}
              className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-black shadow-lg hidden md:flex md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
            >
              <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                {currentTrack?.id === track.id && isPlaying ? (
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                ) : (
                  <path d="M8 5v14l11-7z" />
                )}
              </svg>
            </button>
          </div>
          
          <div className="text-left flex-1 flex flex-col min-w-0 p-3">
            <h3 className="text-base font-semibold truncate flex items-center gap-1">
              {track.title}
              {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-4 h-4 inline-flex flex-shrink-0" />}
            </h3>
          </div>
        </article>
      );

      if (!userIsPremium && adFrequency > 0 && adOffset === 0 && index === 0) {
        items.unshift(
          <AdCardInline
            key={`ad-inline-before-${index}`}
            userIsPremium={userIsPremium}
            className="min-w-[180px] max-w-[200px]"
          />
        );
      }

      const shouldInsertAd =
        !userIsPremium &&
        adFrequency > 0 &&
        index + 1 >= adOffset &&
        (index + 1 - adOffset) % adFrequency === 0;
      if (shouldInsertAd) {
        items.push(
          <AdCardInline
            key={`ad-inline-${index}`}
            userIsPremium={userIsPremium}
            className="min-w-[180px] max-w-[200px]"
          />
        );
      }
    });
    return items;
  };

  return (
    <section className="mt-6">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          {subtitle && <p className="text-base text-white/60 mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="relative">
        {showScrollArrows && (
          <>
            <button
              type="button"
              onClick={() => scrollHomeRow('left')}
              aria-label="Défiler vers la gauche"
              className={`hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10 ${!hasScrolledRight ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => scrollHomeRow('right')}
              aria-label="Défiler vers la droite"
              className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
            >
              ›
            </button>
          </>
        )}

        <div
          ref={homeRowScrollRef}
          onScroll={handleRowScroll}
          className="flex gap-3 overflow-x-auto scroll-smooth pb-1 pr-2"
          style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
        >
          {renderItems()}
        </div>
      </div>
    </section>
  );
}

// ---- Dynamic Sections Row (horizontal scroll) ----
function DynamicSectionRow({ tracks, onPlayTrack, onOpenArtist, onToggleLike, likedTrackIds = [], currentTrack, isPlaying, userIsPremium = false, onReachEnd }) {
  const rowScrollRef = useRef(null);
  const [hasScrolledRight, setHasScrolledRight] = useState(false);
  const trackKey = (tracks || []).map((track) => track.id).join('|');
  const shuffledTracks = useMemo(() => shuffleArray(tracks || []), [trackKey]);
  const adOffset = useMemo(() => Math.floor(Math.random() * 4), [trackKey]);
  const hasRequestedAllRef = useRef(false);

  const scrollRow = (direction) => {
    const el = rowScrollRef.current;
    if (!el) return;
    const amount = Math.max(240, Math.floor(el.clientWidth * 0.8));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    if (direction === 'right') setHasScrolledRight(true);
  };
  const handleRowScroll = () => {
    const el = rowScrollRef.current;
    if (!el || !onReachEnd || hasRequestedAllRef.current) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 120;
    if (nearEnd) {
      hasRequestedAllRef.current = true;
      onReachEnd();
    }
  };

  if (!shuffledTracks || shuffledTracks.length === 0) return null;

  return (
    <div className="relative">
      {hasScrolledRight && (
        <button
          type="button"
          onClick={() => scrollRow('left')}
          aria-label="Défiler vers la gauche"
          className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
        >
          ‹
        </button>
      )}

      <div
        ref={rowScrollRef}
        onScroll={handleRowScroll}
        className="flex gap-4 overflow-x-auto scroll-smooth pb-2 pr-2"
        style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
      >
        {shuffledTracks.flatMap((track, trackIndex) => {
          const items = [
            <button
              key={`track-${track.id}`}
              onClick={() => onOpenArtist?.(track) || {}}
              className="group bg-transparent hover:bg-white/10 rounded-lg transition-all text-left flex flex-col flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
            >
              <div
                className="w-full aspect-square rounded-t-lg overflow-hidden relative"
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayTrack(track);
                }}
              >
                {track.cover_image ? (
                  <img loading="lazy" decoding="async"
                    src={getImageUrl(track.cover_image)}
                    alt={track.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">
                    {track.cover_emoji || '🎵'}
                  </div>
                )}
                {!!track.is_premium && !userIsPremium && (
                  <div className="absolute top-1 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1">
                    <PremiumCrown className="w-4 h-4" />
                  </div>
                )}
                {onToggleLike && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleLike(track.id);
                    }}
                    className={`absolute top-2 left-2 z-10 text-xl transition-transform hover:scale-110 ${
                      likedTrackIds.includes(track.id) ? 'text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-white/70 hover:text-white'
                    }`}
                    title={likedTrackIds.includes(track.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  >
                    {likedTrackIds.includes(track.id) ? '💚' : '🤍'}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlayTrack(track);
                  }}
                  className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hidden md:flex flex items-center justify-center text-black shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
                >
                  <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    {currentTrack?.id === track.id && isPlaying ? (
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    ) : (
                      <path d="M8 5v14l11-7z" />
                    )}
                  </svg>
                </button>
              </div>
              <div className="p-1">
                <h3 className="font-bold text-sm truncate mb-1 flex items-center gap-1">
                  {track.title}
                  {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-3 h-3 inline-flex flex-shrink-0" />}
                </h3>
              </div>
            </button>
          ];

          if (!userIsPremium && adOffset === 0 && trackIndex === 0) {
            items.unshift(
              <AdCardInlineSlot
                key={`ad-before-${trackIndex}`}
                userIsPremium={userIsPremium}
                wrapperClassName="flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                className="w-full"
              />
            );
          }

          const shouldInsertAd =
            !userIsPremium &&
            trackIndex + 1 >= adOffset &&
            (trackIndex + 1 - adOffset) % 3 === 0;
          if (shouldInsertAd) {
            items.push(
              <AdCardInlineSlot
                key={`ad-${trackIndex}`}
                userIsPremium={userIsPremium}
                wrapperClassName="flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                className="w-full"
              />
            );
          }

          return items;
        })}
      </div>

      <button
        type="button"
        onClick={() => scrollRow('right')}
        aria-label="Défiler vers la droite"
        className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
      >
        ›
      </button>
    </div>
  );
}

// ---- Quick strip - Latest Tracks ----
function QuickStrip({ tracks, onPlayTrack, onOpenArtist, currentTrack, isPlaying, userIsPremium = false }) {
  const trackKey = (tracks || []).map((track) => track.id).join('|');
  const shuffledTracks = useMemo(() => shuffleArray(tracks || []), [trackKey]);
  if (!tracks || tracks.length === 0) return null;

  return (
    <section className="mt-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
        {shuffledTracks.slice(0, 8).map((track) => (
          <button
            key={track.id}
            onClick={() => onPlayTrack(track)}
            className={`group flex items-center gap-1 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded-md px-2 py-2 text-left relative ${
              currentTrack?.id === track.id ? 'ring-1 ring-pink-500/70' : ''
            }`}
          >
            <div className="w-10 h-10 rounded overflow-hidden relative flex-shrink-0">
              {track.cover_image ? (
                <img loading="lazy" decoding="async"
                  src={getImageUrl(track.cover_image)}
                  alt={track.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-base">
                  {track.cover_emoji || '🎵'}
                </div>
              )}
              
              {/* Icône Premium */}
              {!!track.is_premium && !userIsPremium && (
                <div className="absolute top-0.5 right-0.5">
                  <PremiumCrown className="w-4 h-4" />
                </div>
              )}
              
              {/* Bouton play vert en bas à droite - masqué sur mobile */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayTrack(track);
                }}
                className="absolute bottom-0.5 right-0.5 w-6 h-6 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-black shadow-md hidden md:flex md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
              >
                <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  {currentTrack?.id === track.id && isPlaying ? (
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  ) : (
                    <path d="M8 5v14l11-7z" />
                  )}
                </svg>
              </button>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-base font-medium truncate flex items-center gap-1">
                {track.title}
                {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-3 h-3 inline-flex flex-shrink-0" />}
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ---- History Strip - Affiche l'historique d'écoute (tracks + playlists) ----
function HistoryStrip({ historyItems, onPlayTrack, onOpenPlaylist, currentTrack, isPlaying, userIsPremium = false }) {
  if (!historyItems || historyItems.length === 0) return null;

  return (
    <section className="mt-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
        {historyItems.slice(0, 8).map((item, index) => {
          if (item.type === 'playlist') {
            // Afficher une playlist
            return (
              <button
                key={`playlist-${item.id}-${index}`}
                onClick={() => onOpenPlaylist(item.id, item.is_admin)}
                className="group flex items-center gap-1 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded-md px-2 py-2 text-left relative"
              >
                <div className="w-10 h-10 rounded overflow-hidden relative flex-shrink-0">
                  {item.cover_image ? (
                    <img loading="lazy" decoding="async"
                      src={getImageUrl(item.cover_image)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${item.color || 'from-purple-600 to-blue-600'} flex items-center justify-center text-base`}>
                      {item.cover_emoji || '🎵'}
                    </div>
                  )}
                  
                  {/* Bouton play supprimé sur la grille d'historique */}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-base font-medium truncate">{item.name}</span>
                  <span className="text-sm text-white/60 truncate">Playlist</span>
                </div>
              </button>
            );
          } else {
            // Afficher une track
            const track = item;
            return (
              <button
                key={`track-${track.id}-${index}`}
                onClick={() => onPlayTrack(track)}
                className={`group flex items-center gap-1 bg-[#47404d] hover:bg-[#4a4a4a] rounded-md px-2 py-2 text-left relative ${
                  currentTrack?.id === track.id ? 'ring-1 ring-pink-500/70' : ''
                }`}
              >
                <div className="w-10 h-10 rounded overflow-hidden relative flex-shrink-0">
                  {track.cover_image ? (
                    <img loading="lazy" decoding="async"
                      src={getImageUrl(track.cover_image)}
                      alt={track.title}
                      className="w-full h-full object-cover"
                    />
                  ) : track.artist_profile_image ? (
                    <img loading="lazy" decoding="async"
                      src={getImageUrl(track.artist_profile_image)}
                      alt={track.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-base">
                      {track.cover_emoji || '🎵'}
                    </div>
                  )}
                  
                  {/* Icône Premium */}
                  {!!track.is_premium && !userIsPremium && (
                    <div className="absolute top-0.5 right-0.5">
                      <PremiumCrown className="w-4 h-4" />
                    </div>
                  )}
                  
                  {/* Bouton play supprimé sur la grille d'historique */}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-base font-medium truncate flex items-center gap-1">
                    {track.title}
                    {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-3 h-3 inline-flex flex-shrink-0" />}
                  </span>
                </div>
              </button>
            );
          }
        })}
      </div>
    </section>
  );
}

// ---- Home view ----
function HomeView({ 
  allTracks, 
  recommendations, 
  likedTracks, 
  recentlyPlayed,
  homeHistory,
  adminPlaylists,
  onPlayTrack,
  onOpenArtist,
  onToggleLike,
  onOpenPlaylist,
  currentTrack, 
  currentCollection,
  likedTrackIds,
  playCollectionFirstTrack,
  user,
  isPlaying,
  userIsPremium = false,
  userPlaylists,
  userFolders,
  onAddToPlaylist,
  onShowToast,
  onOpenAuthModal,
  onOpenSubscription,
  stories,
  storiesEnabled,
  onCreateStory,
  onReportStory,
  onDeleteStory,
  openStoryIndex,
  setOpenStoryIndex,
  shouldPauseStory,
  onStoryPlayRequest,
  onRefreshStories
}) {
  const { t } = useTranslation();
  const [isMobileScreen, setIsMobileScreen] = React.useState(window.innerWidth < 768);
  const [activeFilter, setActiveFilter] = React.useState('all');
  const [popularTracks, setPopularTracks] = React.useState([]);
  const [categoriesWithTracks, setCategoriesWithTracks] = React.useState([]);
  const [dynamicSections, setDynamicSections] = React.useState([]);
  const [moodCategories, setMoodCategories] = React.useState([]);
  const [selectedMoodCategory, setSelectedMoodCategory] = React.useState(null);
  const [musicCategoriesList, setMusicCategoriesList] = React.useState([]);
  const [selectedMusicCategoryIds, setSelectedMusicCategoryIds] = React.useState([]);
  const [homeMoodsExpanded, setHomeMoodsExpanded] = React.useState(false);
  const [recentlyExpanded, setRecentlyExpanded] = React.useState(false);
  const [newReleasesExpanded, setNewReleasesExpanded] = React.useState(false);
  const [popularExpanded, setPopularExpanded] = React.useState(false);
  const [storiesExpanded, setStoriesExpanded] = React.useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = React.useState(() => new Set());
  const [loadingCategoryIds, setLoadingCategoryIds] = React.useState(() => new Set());
  const [expandedDynamicSectionIds, setExpandedDynamicSectionIds] = React.useState(() => new Set());
  const [loadingDynamicSectionIds, setLoadingDynamicSectionIds] = React.useState(() => new Set());
  const moodsScrollRef = useRef(null);
  const moodTabScrollRef = useRef(null);
  const musicCategoryScrollRef = useRef(null);
  const [isDesktop, setIsDesktop] = React.useState(false);
  const [hasMoodsScrolledRight, setHasMoodsScrolledRight] = React.useState(false);
  // openStoryIndex is controlled by parent (SpotifyClone)

  const resolveProfileImage = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const filename = url.split('/').pop();
    return `${BACKEND_URL}/api/images/${filename}`;
  };

  React.useEffect(() => {
    if (activeFilter !== 'all' && activeFilter !== 'music') {
      setOpenStoryIndex(null);
    }
  }, [activeFilter]);

  React.useEffect(() => {
    if (activeFilter !== 'music' && selectedMusicCategoryIds.length > 0) {
      clearMusicCategories();
    }
  }, [activeFilter, selectedMusicCategoryIds.length]);

  React.useEffect(() => {
    if (openStoryIndex !== null && openStoryIndex >= stories.length) {
      setOpenStoryIndex(stories.length > 0 ? stories.length - 1 : null);
    }
  }, [stories, openStoryIndex]);

  // Détecter le desktop (PC) pour activer certains comportements UI (ex: scroll horizontal)
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const apply = () => setIsDesktop(!!mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);
  const scrollMoods = (direction) => {
    const el = moodsScrollRef.current;
    if (!el) return;
    const amount = Math.max(240, Math.floor(el.clientWidth * 0.8));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });

    // Afficher la flèche gauche uniquement après un premier clic sur la flèche droite
    if (direction === 'right') setHasMoodsScrolledRight(true);
  };

  const handleHomeMoodsScroll = (event) => {
    if (homeMoodsExpanded) return;
    const el = event.currentTarget;
    if (!el) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 120;
    if (nearEnd) {
      loadAllHomeMoods();
    }
  };

  const handlePopularScroll = (event) => {
    if (popularExpanded) return;
    const el = event.currentTarget;
    if (!el) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 120;
    if (nearEnd) {
      loadAllPopularTracks();
    }
  };

  const handleCategoryScroll = (categoryId, event) => {
    if (categoryId === undefined || categoryId === null) return;
    const key = String(categoryId);
    if (expandedCategoryIds.has(key)) return;
    const el = event.currentTarget;
    if (!el) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 120;
    if (nearEnd) {
      loadAllCategoryTracks(categoryId);
    }
  };

  const scrollMoodTab = (direction) => {
    const el = moodTabScrollRef.current;
    if (!el) return;
    const amount = Math.max(320, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const [shuffledMoodCategories, setShuffledMoodCategories] = React.useState([]);
  // Reset filtre Home (Tout) quand on clique sur le logo / Home dans la topbar
  React.useEffect(() => {
    const handleResetHomeFilter = () => {
      setActiveFilter('all');
      setSelectedMoodCategory(null);
    };
    window.addEventListener('resetHomeFilter', handleResetHomeFilter);
    return () => window.removeEventListener('resetHomeFilter', handleResetHomeFilter);
  }, []);

  const [moodCategoryTracks, setMoodCategoryTracks] = React.useState([]);
  const [moodViewMode, setMoodViewMode] = React.useState('list'); // 'list' or 'grid'
  const [moodContextMenu, setMoodContextMenu] = React.useState({ isOpen: false, track: null, position: { x: 0, y: 0 } });
  const [moodCategoryAdSeed, setMoodCategoryAdSeed] = React.useState(() => Math.random());

  React.useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mélanger les mood categories quand elles changent
  React.useEffect(() => {
    if (moodCategories.length > 0) {
      setShuffledMoodCategories(shuffleArray(moodCategories));
    } else {
      setShuffledMoodCategories([]);
    }
  }, [moodCategories]);

  // Gestionnaire du menu contextuel pour les moods
  const handleMoodContextMenu = (e, mood) => {
    e.preventDefault();
    e.stopPropagation();
    const buttonRect = e.currentTarget.getBoundingClientRect();
    setMoodContextMenu({
      isOpen: true,
      track: mood,
      position: {
        x: buttonRect.left - 210,
        y: buttonRect.top
      }
    });
  };
  // Wrapper pour ajouter la source 'home' aux appels de lecture
  const handleHomePlayTrack = (track) => {
    onPlayTrack(track, null, { type: 'home' });
  };

  const handleMoodPlayTrack = (mood) => {
    if (selectedMoodCategory && moodCategoryTracks.length > 0) {
      const enrichedMood = {
        ...mood,
        mood_category_id: mood.mood_category_id || selectedMoodCategory.id,
        mood_category_ids: Array.isArray(mood.mood_category_ids) && mood.mood_category_ids.length
          ? mood.mood_category_ids
          : [selectedMoodCategory.id],
        mood_category_name: mood.mood_category_name || selectedMoodCategory.name,
        category_name: mood.category_name || selectedMoodCategory.name
      };
      console.log('[NAV][MOOD] play in category', {
        selectedMoodCategory,
        tracksCount: moodCategoryTracks.length,
        mood
      });
      onPlayTrack(
        enrichedMood,
        { tracks: moodCategoryTracks },
        {
          type: 'mood-category',
          id: selectedMoodCategory.id,
          name: selectedMoodCategory.name
        }
      );
    } else {
      console.log('[NAV][MOOD] play without category', { mood });
      onPlayTrack(mood);
    }
  };

  const normalizeCategoryId = (value) => String(value);

  const selectedMusicCategorySet = useMemo(() => {
    return new Set(selectedMusicCategoryIds.map(normalizeCategoryId));
  }, [selectedMusicCategoryIds]);

  const getTrackCategoryIdsForFilter = (track) => {
    if (!track) return [];
    const ids = new Set();
    if (track.category_id !== undefined && track.category_id !== null) {
      ids.add(normalizeCategoryId(track.category_id));
    }
    if (Array.isArray(track.categories)) {
      track.categories.forEach((cat) => {
        if (cat && cat.id !== undefined && cat.id !== null) {
          ids.add(normalizeCategoryId(cat.id));
        }
      });
    }
    if (Array.isArray(track.category_ids)) {
      track.category_ids.forEach((id) => {
        if (id !== undefined && id !== null) ids.add(normalizeCategoryId(id));
      });
    } else if (typeof track.category_ids === 'string') {
      try {
        const parsed = JSON.parse(track.category_ids);
        if (Array.isArray(parsed)) {
          parsed.forEach((id) => {
            if (id !== undefined && id !== null) ids.add(normalizeCategoryId(id));
          });
        }
      } catch {
        track.category_ids
          .split(',')
          .map((part) => part.trim())
          .filter((id) => id)
          .forEach((id) => ids.add(normalizeCategoryId(id)));
      }
    }
    return Array.from(ids);
  };

  const trackMatchesSelectedCategories = (track) => {
    if (!track) return false;
    if (selectedMusicCategorySet.size === 0) return true;
    const ids = getTrackCategoryIdsForFilter(track);
    return ids.some((id) => selectedMusicCategorySet.has(id));
  };

  const filterTracksBySelectedCategories = (tracks = []) => {
    if (!Array.isArray(tracks)) return [];
    if (selectedMusicCategorySet.size === 0) return tracks;
    return tracks.filter(trackMatchesSelectedCategories);
  };

  const toggleMusicCategory = (id) => {
    const normalized = normalizeCategoryId(id);
    setSelectedMusicCategoryIds((prev) =>
      prev.includes(normalized)
        ? prev.filter((value) => value !== normalized)
        : [...prev, normalized]
    );
  };

  const clearMusicCategories = () => setSelectedMusicCategoryIds([]);

  // Filter labels for translation
  const filterLabels = {
    all: t('filters.all'),
    music: t('filters.music'),
    playlists: t('filters.playlists'),
    moods: 'Moods'
  };

  // Charger les catégories de moods
  const loadMoodCategories = async () => {
    try {
      const data = await fetchMoodCategories().catch(() => []);
      setMoodCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement mood categories:', error);
    }
  };

  const loadMusicCategories = async () => {
    try {
      const data = await fetchCategories().catch(() => []);
      setMusicCategoriesList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement catégories musique:', error);
    }
  };

  // Charger les moods d'une catégorie spécifique
  const loadMoodCategoryTracks = async (categoryId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mood-categories/${categoryId}/moods`);
      if (response.ok) {
        const data = await response.json();
        const enriched = (Array.isArray(data) ? data : []).map((t) => ({
          ...t,
          mood_category_id: t.mood_category_id || categoryId,
          mood_category_ids: Array.isArray(t.mood_category_ids) && t.mood_category_ids.length
            ? t.mood_category_ids
            : [categoryId],
          mood_category_name: t.mood_category_name || selectedMoodCategory?.name,
          category_name: t.category_name || selectedMoodCategory?.name
        }));
        setMoodCategoryTracks(enriched);
      }
    } catch (error) {
      console.error('Erreur chargement moods de la catégorie:', error);
    }
  };

  // Charger les moods quand une catégorie est sélectionnée
  React.useEffect(() => {
    if (selectedMoodCategory) {
      loadMoodCategoryTracks(selectedMoodCategory.id);
    }
  }, [selectedMoodCategory]);

  // Charger les sections dynamiques pour la page d'accueil
  React.useEffect(() => {
    if (activeFilter === 'all') {
      loadDynamicSections();
      loadMoodCategories(); // Charger aussi les mood categories pour la section Moods
    } else if (activeFilter === 'moods') {
      loadMoodCategories();
    }
  }, [activeFilter]);

  React.useEffect(() => {
    loadMusicCategories();
  }, []);

  // Recharger les sections quand l'utilisateur se connecte/déconnecte
  React.useEffect(() => {
    if (activeFilter === 'all') {
      loadDynamicSections();
    }
  }, [user]);

  // Écouter l'événement de sélection de mood depuis la recherche
  React.useEffect(() => {
    const handleOpenMoodCategory = async (event) => {
      const mood = event.detail;
      if (mood) {
        setActiveFilter('moods');
        if (mood.cover_image) {
          setSelectedMoodCategory(mood);
          return;
        }
        const existing = (moodCategories || []).find((c) => String(c.id) === String(mood.id));
        if (existing) {
          setSelectedMoodCategory(existing);
          return;
        }
        try {
          const response = await fetch(`${BACKEND_URL}/api/mood-categories`);
          if (response.ok) {
            const data = await response.json();
            const found = (data || []).find((c) => String(c.id) === String(mood.id));
            setSelectedMoodCategory(found || mood);
            return;
          }
        } catch (error) {
          console.error('Erreur chargement mood categories (openMoodCategory):', error);
        }
        setSelectedMoodCategory(mood);
      }
    };

    window.addEventListener('openMoodCategory', handleOpenMoodCategory);
    return () => {
      window.removeEventListener('openMoodCategory', handleOpenMoodCategory);
    };
  }, [moodCategories]);

  // Charger les pistes populaires quand on clique sur "Musique"
  React.useEffect(() => {
    if (activeFilter === 'music') {
      loadPopularTracks();
      loadTracksByCategories();
    }
  }, [activeFilter]);

  const loadDynamicSections = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sections/active`, { headers });
      const data = await response.json();
      setDynamicSections(data);
    } catch (error) {
      console.error('Erreur chargement sections dynamiques:', error);
    }
  };

  const loadPopularTracks = async () => {
    try {
      const data = await fetchTopTracksByStreams(50);
      // Filtrer les moods
      setPopularTracks(data.filter(track => !track.is_mood));
    } catch (error) {
      console.error('Erreur chargement pistes populaires:', error);
    }
  };


  const loadTracksByCategories = async () => {
    try {
      // Récupérer toutes les catégories
      const categories = await fetchCategories().catch(() => []);
      setMusicCategoriesList(Array.isArray(categories) ? categories : []);

      // Récupérer toutes les tracks
      const allTracksData = await fetchTracks();

      // Grouper les tracks par catégorie (exclure les moods)
      const categoriesData = categories.map(category => {
        const categoryTracks = allTracksData.filter(track => {
          if (track.is_mood) return false;
          const ids = getTrackCategoryIdsForFilter(track);
          return ids.includes(normalizeCategoryId(category.id));
        });
        return {
          ...category,
          tracks: categoryTracks
        };
      }).filter(cat => cat.tracks.length > 0); // Garder seulement les catégories avec des tracks

      setCategoriesWithTracks(categoriesData);
    } catch (error) {
      console.error('Erreur chargement tracks par catégories:', error);
    }
  };

  const markSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const unmarkSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const loadAllRecentlyPlayed = async () => {
    if (recentlyExpanded || !user) return;
    setRecentlyExpanded(true);
    try {
      const data = await fetchRecentlyPlayed(200);
      setRecentlyPlayed(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement complet récemment écouté:', error);
    }
  };

  const loadAllNewReleases = async () => {
    if (newReleasesExpanded) return;
    setNewReleasesExpanded(true);
    try {
      const tracks = await fetchTracks({ include_moods: true });
      setAllTracks(Array.isArray(tracks) ? tracks : []);
    } catch (error) {
      console.error('Erreur chargement complet nouveautés:', error);
    }
  };

  const loadAllPopularTracks = async () => {
    if (popularExpanded) return;
    setPopularExpanded(true);
    try {
      const data = await fetchTopTracksByStreams(500);
      const filtered = Array.isArray(data) ? data.filter((track) => !track.is_mood) : [];
      setPopularTracks(filtered);
    } catch (error) {
      console.error('Erreur chargement complet musiques populaires:', error);
    }
  };

  const loadAllHomeMoods = async () => {
    if (homeMoodsExpanded) return;
    setHomeMoodsExpanded(true);
    try {
      await loadMoodCategories();
    } catch (error) {
      console.error('Erreur chargement complet moods:', error);
    }
  };

  const loadAllSectionTracks = async (sectionId) => {
    if (sectionId === undefined || sectionId === null) return;
    const key = String(sectionId);
    if (expandedDynamicSectionIds.has(key) || loadingDynamicSectionIds.has(key)) return;
    markSet(setLoadingDynamicSectionIds, key);
    try {
      const data = await fetchSectionTracks(sectionId);
      const tracks = Array.isArray(data) ? data : [];
      setDynamicSections((prev) =>
        (prev || []).map((section) =>
          String(section.id) === String(sectionId) ? { ...section, tracks } : section
        )
      );
      markSet(setExpandedDynamicSectionIds, key);
    } catch (error) {
      console.error('Erreur chargement complet section:', error);
    } finally {
      unmarkSet(setLoadingDynamicSectionIds, key);
    }
  };

  const loadAllCategoryTracks = async (categoryId) => {
    if (categoryId === undefined || categoryId === null) return;
    const key = String(categoryId);
    if (expandedCategoryIds.has(key) || loadingCategoryIds.has(key)) return;
    markSet(setExpandedCategoryIds, key);
    markSet(setLoadingCategoryIds, key);
    try {
      const data = await fetchTracks({ category_id: categoryId });
      const tracks = Array.isArray(data) ? data.filter((t) => !t.is_mood) : [];
      setCategoriesWithTracks((prev) =>
        (prev || []).map((cat) =>
          String(cat.id) === String(categoryId) ? { ...cat, tracks } : cat
        )
      );
    } catch (error) {
      console.error('Erreur chargement complet catégorie:', error);
    } finally {
      unmarkSet(setLoadingCategoryIds, key);
    }
  };

  const handleLoadAllStories = React.useCallback(async () => {
    if (storiesExpanded) return;
    setStoriesExpanded(true);
    if (onRefreshStories) {
      await onRefreshStories();
    }
  }, [storiesExpanded, onRefreshStories]);
  
  // Filtrer les moods des tracks affichées sur l'accueil
  const musicOnlyTracks = allTracks.filter(track => !track.is_mood);
  const filteredMusicTracks = filterTracksBySelectedCategories(musicOnlyTracks);
  const latestTracks = filteredMusicTracks.slice(0, 8);
  const filteredRecentlyPlayed = filterTracksBySelectedCategories(
    (recentlyPlayed || []).filter(track => !track.is_mood)
  );
  const historyItemsUnfiltered = useMemo(() => {
    return (homeHistory && homeHistory.length > 0)
      ? homeHistory
      : recentlyPlayed;
  }, [homeHistory, recentlyPlayed]);

  const filteredDynamicSections = useMemo(() => {
    return (dynamicSections || []).map((section) => {
      const baseTracks = section.tracks ? section.tracks.filter(track => !track.is_mood) : [];
      const filtered = filterTracksBySelectedCategories(baseTracks);
      return { ...section, tracks: filtered };
    }).filter(section => section.tracks && section.tracks.length > 0);
  }, [dynamicSections, filterTracksBySelectedCategories]);

  const filteredCategoriesWithTracks = useMemo(() => {
    const categories = (categoriesWithTracks || []).map((category) => {
      const filteredTracks = filterTracksBySelectedCategories(
        (category.tracks || []).filter(track => !track.is_mood)
      );
      return { ...category, tracks: filteredTracks };
    });
    const filtered = selectedMusicCategorySet.size > 0
      ? categories.filter((cat) => selectedMusicCategorySet.has(normalizeCategoryId(cat.id)))
      : categories;
    return filtered
      .filter((cat) => cat.tracks && cat.tracks.length > 0)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [categoriesWithTracks, filterTracksBySelectedCategories, selectedMusicCategorySet]);

  const sortedMusicCategories = useMemo(() => {
    return [...(musicCategoriesList || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [musicCategoriesList]);
  const moodCategoriesForView =
    (shuffledMoodCategories && shuffledMoodCategories.length > 0)
      ? shuffledMoodCategories
      : shuffleArray(moodCategories || []);
  const moodCategoriesKey = useMemo(
    () => (moodCategoriesForView || []).map((c) => c.id).join('|'),
    [moodCategoriesForView]
  );
  useEffect(() => {
    if (activeFilter === 'moods') {
      setMoodCategoryAdSeed(Math.random());
    }
  }, [activeFilter, moodCategoriesKey]);
  const moodCategoryAdOffset = useMemo(
    () => Math.floor(moodCategoryAdSeed * 4),
    [moodCategoryAdSeed]
  );
  const homeMoodCategories = homeMoodsExpanded
    ? moodCategoriesForView
    : moodCategoriesForView.slice(0, 12);
  const homeMoodAdOffset = useMemo(
    () => Math.floor(Math.random() * 4),
    [homeMoodCategories.map((c) => c.id).join('|')]
  );
  const shuffledPopularTracks = useMemo(() => shuffleArray(popularTracks || []), [popularTracks]);
  const filteredPopularTracks = filterTracksBySelectedCategories(shuffledPopularTracks || []);
  const filteredPopularTrackKey = useMemo(
    () =>
      (filteredPopularTracks || [])
        .map((t) => t.id)
        .join('|') +
      `|cat=${selectedMusicCategoryIds.join(',')}|filter=${activeFilter}`,
    [filteredPopularTracks, selectedMusicCategoryIds, activeFilter]
  );
  const shuffledPopularTracksForView = useMemo(
    () => shuffleArray(filteredPopularTracks || []),
    [filteredPopularTrackKey]
  );
  const popularTracksForView = popularExpanded
    ? shuffledPopularTracksForView
    : shuffledPopularTracksForView.slice(0, 24);
  const popularAdOffset = useMemo(
    () => Math.floor(Math.random() * 4),
    [filteredPopularTrackKey]
  );
  const popularTrackIdSet = useMemo(
    () => new Set((filteredPopularTracks || []).map((t) => t.id)),
    [filteredPopularTrackKey]
  );
  const recentlyTracksForView = recentlyExpanded
    ? filteredRecentlyPlayed
    : filteredRecentlyPlayed.slice(0, 12);
  const newReleaseTracksForView = newReleasesExpanded
    ? filteredMusicTracks
    : filteredMusicTracks.slice(0, 12);
  const categoryShuffleKey = useMemo(
    () =>
      (filteredCategoriesWithTracks || [])
        .map((cat) => `${cat.id}:${(cat.tracks || []).map((t) => t.id).join(',')}`)
        .join('|') +
      `|popular:${filteredPopularTrackKey}`,
    [filteredCategoriesWithTracks, filteredPopularTrackKey]
  );
  const shuffledCategoryTracksMap = useMemo(() => {
    const map = new Map();
    (filteredCategoriesWithTracks || []).forEach((cat) => {
      const baseTracks = (cat.tracks || []).filter((t) => !popularTrackIdSet.has(t.id));
      map.set(cat.id, {
        tracks: shuffleArray(baseTracks),
        adOffset: Math.floor(Math.random() * 4)
      });
    });
    return map;
  }, [categoryShuffleKey]);
  const shuffledCategoriesWithTracks = useMemo(
    () => shuffleArray(categoriesWithTracks || []).map((category) => ({
      ...category,
      tracks: shuffleArray(category.tracks || [])
    })),
    [categoriesWithTracks]
  );
  const shuffledAdminPlaylistsAll = useMemo(
    () => shuffleArray(adminPlaylists || []),
    [adminPlaylists]
  );
  const shuffledAdminPlaylistsWithTracks = useMemo(
    () => shuffleArray((adminPlaylists || []).filter(p => p.track_count > 0)),
    [adminPlaylists]
  );

  // Formater la durée en mm:ss
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const sanitizeMoodTitle = (title) => {
    if (typeof title !== 'string') return title;
    return title.replace(/\s*0$/, '').trim();
  };

  // Un morceau est "en lecture" uniquement si c'est la piste courante ET que l'audio n'est pas en pause.
  const isTrackActuallyPlaying = (trackId) => {
    return currentTrack?.id === trackId && !!isPlaying;
  };

  const renderFilterTabs = (className = '') => {
    const tabs = ["all", "music", "playlists", "moods"];
    return (
    <div className={`flex flex-wrap gap-1 text-sm ${className}`}>
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => {
            setActiveFilter(tab);
            if (tab !== 'moods') {
              setSelectedMoodCategory(null);
            }
          }}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
            activeFilter === tab
              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
              : "bg-white/10 text-white/80 hover:text-white"
          }`}
        >
          {filterLabels[tab]}
        </button>
      ))}
    </div>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      {/* Filtres de navigation */}
      {!(isMobileScreen && activeFilter === 'moods' && selectedMoodCategory) && (
        <div className="mb-3">
          {renderFilterTabs()}
        </div>
      )}


      {/* Historique d'écoute (8 cartes) - visible uniquement pour l'utilisateur connecté */}
      {/* Masqué quand l'utilisateur consulte Playlists ou Moods */}
      {user && (activeFilter === 'all' || activeFilter === 'music') && (
        <HistoryStrip
          historyItems={historyItemsUnfiltered}
          onPlayTrack={(track) => handleHomePlayTrack(track, currentCollection)}
          onOpenPlaylist={onOpenPlaylist}
          currentTrack={currentTrack}
          isPlaying={isPlaying}
          userIsPremium={userIsPremium}
        />
      )}

      {activeFilter === 'music' && !userIsPremium && (
        <div className="min-h-[90px]">
          <AdBanner position="banner_top" userIsPremium={userIsPremium} />
        </div>
      )}

      {activeFilter === 'music' && sortedMusicCategories.length > 0 && (
        <div className="mt-4 mb-3">
          <div
            ref={musicCategoryScrollRef}
            className="flex items-center gap-2 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <button
              type="button"
              onClick={clearMusicCategories}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors flex-shrink-0 inline-flex items-center gap-2 ${
                selectedMusicCategorySet.size === 0
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'bg-white/10 text-white/80 hover:text-white'
              }`}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 3v10.55a4 4 0 1 0 2 3.45V7h6V3h-8z" />
              </svg>
              Tout
            </button>
            {sortedMusicCategories.map((category) => {
              const isSelected = selectedMusicCategorySet.has(normalizeCategoryId(category.id));
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => toggleMusicCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-colors flex-shrink-0 inline-flex items-center gap-2 ${
                    isSelected
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-white/10 text-white/80 hover:text-white'
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 3v10.55a4 4 0 1 0 2 3.45V7h6V3h-8z" />
                  </svg>
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
      

      {/* Vue détail catégorie Mood */}
      {activeFilter === 'moods' && selectedMoodCategory ? (
        <div className="mb-2">
          {/* Hero Banner de la catégorie (style artiste) */}
          <div className={`${isMobileScreen ? '-mx-4 -mt-4' : ''}`}>
            <div className={`relative h-64 md:h-80 overflow-hidden ${isMobileScreen ? 'rounded-none' : 'rounded-2xl'} mb-3`}>
              {selectedMoodCategory.cover_image ? (
                <img
                  src={selectedMoodCategory.cover_image}
                  alt={selectedMoodCategory.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-800"></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black"></div>

              <div className="relative h-full flex flex-col justify-between p-4 md:p-6">
                <div className="flex flex-col gap-2">
                  {user && isMobileScreen && (
                    <div className="bg-black/40 backdrop-blur-md rounded-2xl p-2">
                      {renderFilterTabs()}
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setSelectedMoodCategory(null);
                      setMoodCategoryTracks([]);
                    }}
                    className="flex items-center gap-1 text-white/70 hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour aux catégories
                  </button>
                </div>

                <div className="flex flex-col">
                  <span className="text-xs md:text-sm font-bold uppercase tracking-wide mb-1 text-white/70">Catégorie Mood</span>
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight mb-2 leading-none">
                    {selectedMoodCategory.name}
                  </h1>
                  <p className="text-sm md:text-base text-white/85 font-medium max-w-2xl">
                    {selectedMoodCategory.description}
                  </p>
                  <p className="text-sm text-white/60 mt-2">{moodCategoryTracks.length} pistes</p>
                </div>
              </div>
            </div>
          </div>

          {/* Toggle vue liste/carte */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold">Toutes les pistes</h2>
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setMoodViewMode('list')}
                className={`p-1 rounded ${moodViewMode === 'list' ? 'bg-white/20' : 'hover:bg-white/10'} transition-colors`}
                title="Vue liste"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => setMoodViewMode('grid')}
                className={`p-1 rounded ${moodViewMode === 'grid' ? 'bg-white/20' : 'hover:bg-white/10'} transition-colors`}
                title="Vue carte"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Liste des moods */}
          {moodViewMode === 'list' ? (
            <div className="space-y-2">
              {moodCategoryTracks.map((mood, index) => (
                <div
                  key={mood.id}
                  className={`group w-full grid grid-cols-[auto,1fr,auto,auto] gap-2 items-center px-3 py-2 rounded-lg hover:bg-white/10 transition-colors ${
                    currentTrack?.id === mood.id ? 'bg-white/10' : ''
                  }`}
                >
                  {/* Numéro / Play button */}
                  <div className="flex items-center justify-center w-8">
                    {isTrackActuallyPlaying(mood.id) ? (
                      <PlayingEqualizer isPlaying={true} />
                    ) : (
                      <>
                        <span className="text-white/50 group-hover:hidden">{index + 1}</span>
                        <button
                          onClick={() => handleMoodPlayTrack(mood)}
                          className="w-5 h-5 hidden group-hover:flex items-center justify-center text-white"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => handleMoodPlayTrack(mood)}
                    className="flex items-center gap-2 min-w-0 text-left"
                  >
                    {/* Cover image */}
                    <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                      {mood.cover_image ? (
                        <img
                          src={mood.cover_image}
                          alt={mood.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center text-base">
                          {mood.cover_emoji || '🎵'}
                        </div>
                      )}
                    </div>
                    {/* Titre et artiste */}
                    <div className="flex flex-col items-start min-w-0">
                      <span className={`text-sm font-normal truncate flex items-center gap-1 ${currentTrack?.id === mood.id ? 'text-pink-400' : 'text-white'}`}>
                        {sanitizeMoodTitle(mood.title)}
                        {mood.is_premium && !userIsPremium && (
                          <PremiumCrown className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                        )}
                      </span>
                      <span className="text-xs text-white/50 truncate">
                        {mood.artist_name || mood.artist || 'Mood'}
                      </span>
                    </div>
                  </button>

                  

                  {/* Durée */}
                  <span className="text-sm text-white/50 w-12 text-right">
                    {Number.isFinite(Number(mood.duration)) && Number(mood.duration) > 0
                      ? formatDuration(Number(mood.duration))
                      : ''}
                  </span>

                  {/* Menu 3 points */}
                  <button
                    onClick={(e) => handleMoodContextMenu(e, mood)}
                    className="flex w-8 h-8 items-center justify-center text-white/70 hover:text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="2"/>
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="12" cy="19" r="2"/>
                    </svg>
                  </button>
                </div>
              ))}

              {moodCategoryTracks.length === 0 && (
                <div className="text-center py-12 text-white/60">
                  Aucun mood dans cette catégorie
                </div>
              )}
            </div>
          ) : (
            /* Vue carte/grille */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-2">
              {moodCategoryTracks.map((mood) => (
                <div
                  key={mood.id}
                  className="group bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-all text-left relative"
                >
                  <button
                    onClick={() => handleMoodPlayTrack(mood)}
                    className="w-full text-left"
                  >
                    <div className="aspect-square rounded-lg overflow-hidden mb-3 relative">
                      {mood.cover_image ? (
                        <img
                          src={mood.cover_image}
                          alt={mood.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center text-4xl">
                          {mood.cover_emoji || '🎵'}
                        </div>
                      )}
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
                          <svg className="w-5 h-5 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      {mood.is_premium && !userIsPremium && (
                        <div className="absolute top-1 left-2">
                          <PremiumCrown className="w-4 h-4 text-yellow-500" />
                        </div>
                      )}
                    </div>
                    <h3 className={`font-medium truncate flex items-center gap-1 ${currentTrack?.id === mood.id ? 'text-pink-400' : ''}`}>
                      {sanitizeMoodTitle(mood.title)}
                    </h3>
                    <p className="text-xs text-white/50 truncate">{mood.artist_name || 'Mood'}</p>
                    {Number.isFinite(Number(mood.duration)) && Number(mood.duration) > 0 && (
                      <p className="text-xs text-white/40 mt-1">
                        {formatDuration(Number(mood.duration))}
                      </p>
                    )}
                  </button>
                  {/* Menu 3 points en haut à droite */}
                  <button
                    onClick={(e) => handleMoodContextMenu(e, mood)}
                    className="absolute top-1 right-2 w-8 h-8 flex items-center justify-center bg-black/60 rounded-full text-white/70 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="2"/>
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="12" cy="19" r="2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Context Menu pour les moods */}
          <TrackContextMenu
            track={moodContextMenu.track}
            isOpen={moodContextMenu.isOpen}
            onClose={() => setMoodContextMenu({ ...moodContextMenu, isOpen: false })}
            position={moodContextMenu.position}
            playlists={userPlaylists}
            folders={userFolders}
            onAddToPlaylist={onAddToPlaylist}
            onToggleLike={onToggleLike}
            isLiked={moodContextMenu.track && likedTrackIds?.includes(moodContextMenu.track.id)}
            onShowToast={onShowToast}
            userIsPremium={userIsPremium}
            user={user}
            onOpenSubscription={onOpenSubscription}
          />
        </div>
      ) : activeFilter === 'moods' ? (
        /* Vue liste des catégories de moods */
        <div className="mb-2">
          {/* Hero Banner */}
          <div className="relative rounded-2xl overflow-hidden mb-2 bg-gradient-to-br from-purple-900 via-indigo-900 to-black">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cmFkaWFsR3JhZGllbnQgaWQ9InN0YXIiIGN4PSI1MCUiIGN5PSI1MCUiIHI9IjUwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0id2hpdGUiIHN0b3Atb3BhY2l0eT0iMSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0id2hpdGUiIHN0b3Atb3BhY2l0eT0iMCIvPjwvcmFkaWFsR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjEwMCIgY3k9IjEwMCIgcj0iMSIgZmlsbD0idXJsKCNzdGFyKSIvPjwvc3ZnPg==')] opacity-30"></div>
            <div className="relative px-8 py-12">
              <h1 className="text-4xl font-bold mb-3">Moods pour créateurs</h1>
              <p className="text-white/70 text-lg max-w-2xl">
                Des ambiances sonores prêtes à l'emploi pour vos projets créatifs. 
                Parfait pour la méditation, la concentration ou la relaxation.
              </p>
            </div>
          </div>

          {/* Grid des catégories de moods */}
          {/*
            IMPORTANT (responsive): Sur certains écrans (ex: 13") avec la sidebar droite ouverte,
            4 colonnes dès le breakpoint "lg" peut devenir trop étroit.
            On passe donc à 3 colonnes en lg, et 4 seulement en xl pour éviter toute superposition.
          */}
          {/**
            Quand la sidebar droite est visible (desktop "lg" + une piste existe),
            la zone centrale peut devenir trop étroite (ex: 13").
            Plutôt que de compresser la grille (et risquer des superpositions),
            on passe la liste en scroll horizontal "Spotify-like" avec flèches.
          */}
          {isDesktop && !!currentTrack ? (
            <div className="relative">
              {/* Flèche gauche (PC uniquement) */}
              <button
                type="button"
                onClick={() => scrollMoodTab('left')}
                className="hidden lg:flex absolute -left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-black/75 border border-white/10 items-center justify-center backdrop-blur-sm"
                aria-label="Scroll left"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>

              <div
                ref={moodTabScrollRef}
                className="overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                <div className="flex gap-6 pr-6">
                  {moodCategoriesForView.flatMap((category, index) => {
                    const itemWidthStyle = { width: 'calc((100% - 72px) / 4)' }; // 4 items visibles, gap-6 => 24px * 3 = 72px
                    const shouldInsertAd =
                      !userIsPremium &&
                      index + 1 >= moodCategoryAdOffset &&
                      (index + 1 - moodCategoryAdOffset) % 6 === 0;

                    const items = [
                      <button
                        key={`mood-cat-${category.id}`}
                        onClick={() => {
                          if (!user) {
                            onOpenAuthModal?.();
                            return;
                          }
                          setSelectedMoodCategory(category);
                        }}
                        style={itemWidthStyle}
                        className="flex-none min-w-[220px] group relative aspect-[16/10] min-h-[180px] rounded-2xl overflow-hidden bg-gradient-to-br from-purple-600/50 to-indigo-800/50 hover:scale-[1.02] hover:z-10 focus-visible:z-10 transition-transform duration-300 will-change-transform"
                      >
                        {category.cover_image ? (
                          <img
                            src={category.cover_image}
                            alt={category.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-800"></div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"></div>

                        <div className="absolute inset-0 p-5 flex flex-col justify-end">
                          <h3
                            className="text-xl font-bold text-white mb-1 line-clamp-2"
                            style={{
                              fontSize: '1.2rem',
                              fontWeight: 900,
                              letterSpacing: '-0.06em',
                              textTransform: 'uppercase',
                              color: '#ffffff68',
                              lineHeight: '1',
                            }}
                          >
                            {category.name}
                          </h3>
                          <p className="text-sm text-white/65 line-clamp-2">{category.description}</p>
                          {category.mood_count > 0 && (
                            <p className="text-xs text-white/45 mt-3">{category.mood_count} pistes</p>
                          )}
                        </div>
                      </button>
                    ];

                    if (!userIsPremium && moodCategoryAdOffset === 0 && index === 0) {
                      items.unshift(
                        <AdCardInlineSlot
                          key={`mood-cat-ad-before-${index}`}
                          userIsPremium={userIsPremium}
                          variant="landscape"
                          className="w-full h-full"
                          wrapperStyle={itemWidthStyle}
                          wrapperClassName="flex-none min-w-[220px]"
                          innerClassName="aspect-[16/10] min-h-[180px] rounded-2xl overflow-hidden"
                        />
                      );
                    }

                    if (shouldInsertAd) {
                      items.push(
                        <AdCardInlineSlot
                          key={`mood-cat-ad-${index}`}
                          userIsPremium={userIsPremium}
                          variant="landscape"
                          className="w-full h-full"
                          wrapperStyle={itemWidthStyle}
                          wrapperClassName="flex-none min-w-[220px]"
                          innerClassName="aspect-[16/10] min-h-[180px] rounded-2xl overflow-hidden"
                        />
                      );
                    }

                    return items;
                  })}

                  {moodCategoriesForView.length === 0 && (
                    <div className="text-center py-12 text-white/60 w-full">
                      Aucune catégorie de mood disponible
                    </div>
                  )}
                </div>
              </div>

              {/* Flèche droite (PC uniquement) */}
              <button
                type="button"
                onClick={() => scrollMoodTab('right')}
                className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-black/75 border border-white/10 items-center justify-center backdrop-blur-sm"
                aria-label="Scroll right"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="grid w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 sm:gap-x-6 md:gap-x-8 gap-y-6 px-1 sm:px-0">
              {moodCategoriesForView.flatMap((category, index) => {
                const shouldInsertAd =
                  !userIsPremium &&
                  index + 1 >= moodCategoryAdOffset &&
                  (index + 1 - moodCategoryAdOffset) % 6 === 0;

                const items = [
                  <button
                    key={`mood-cat-grid-${category.id}`}
                    onClick={() => {
                      if (!user) {
                        onOpenAuthModal?.();
                        return;
                      }
                      setSelectedMoodCategory(category);
                    }}
                    className="group relative aspect-[16/10] min-h-[180px] rounded-2xl overflow-hidden bg-gradient-to-br from-purple-600/50 to-indigo-800/50 hover:scale-[1.02] hover:z-10 focus-visible:z-10 transition-transform duration-300 will-change-transform"
                  >
                    {category.cover_image ? (
                      <img
                        src={category.cover_image}
                        alt={category.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-800"></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"></div>

                    <div className="absolute inset-0 p-5 flex flex-col justify-end">
                      <h3
                        className="text-xl font-bold text-white mb-1 line-clamp-2"
                        style={{
                          fontSize: '1.1rem',
                          fontWeight: 900,
                          letterSpacing: '-0.06em',
                          textTransform: 'uppercase',
                          color: '#ffffffd7',
                          lineHeight: '1',
                        }}
                      >
                        {category.name}
                      </h3>
                      <p className="text-sm text-white/65 line-clamp-2">{category.description}</p>
                      {category.mood_count > 0 && (
                        <p className="text-xs text-white/45 mt-3">{category.mood_count} pistes</p>
                      )}
                    </div>
                  </button>
                ];

                if (!userIsPremium && moodCategoryAdOffset === 0 && index === 0) {
                  items.unshift(
                    <AdCardInlineSlot
                      key={`mood-cat-grid-ad-before-${index}`}
                      userIsPremium={userIsPremium}
                      variant="landscape"
                      className="w-full h-full"
                      wrapperClassName="aspect-[16/10] min-h-[180px] rounded-2xl overflow-hidden"
                    />
                  );
                }

                if (shouldInsertAd) {
                  items.push(
                    <AdCardInlineSlot
                      key={`mood-cat-grid-ad-${index}`}
                      userIsPremium={userIsPremium}
                      variant="landscape"
                      className="w-full h-full"
                      wrapperClassName="aspect-[16/10] min-h-[180px] rounded-2xl overflow-hidden"
                    />
                  );
                }

                return items;
              })}

              {moodCategoriesForView.length === 0 && (
                <div className="col-span-full text-center py-12 text-white/60">
                  Aucune catégorie de mood disponible
                </div>
              )}
            </div>
          )}
        </div>
      ) : activeFilter === 'playlists' ? (
        // Vue Playliste : Afficher toutes les playlists admin
        <div className="mb-2">
          <h2 className="text-2xl font-bold mb-2">{t('admin.playlists')}</h2>
          <p className="text-sm text-white/60 mb-2">{t('library.playlists')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-2">
            {shuffledAdminPlaylistsAll.flatMap((playlist, index) => {
              const items = [
                <button
                  key={`playlist-${playlist.id}`}
                  onClick={() => onOpenPlaylist?.(playlist.id, true)}
                  className="group bg-transparent hover:bg-white/10 rounded-lg transition-all text-left flex flex-col"
                >
                  <div className="w-full aspect-square rounded-t-lg overflow-hidden relative">
                    {playlist.cover_image ? (
                      <img loading="lazy" decoding="async" 
                        src={getImageUrl(playlist.cover_image, 'playlist')}
                        alt={playlist.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-8xl">
                        {playlist.cover_emoji || '🎵'}
                      </div>
                    )}
                    {/* Bouton play vert en bas à droite */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        playCollectionFirstTrack(playlist);
                      }}
                      className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hidden md:flex flex items-center justify-center text-black shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
                    >
                      <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  </div>
                  <div className="p-1">
                    <h3 className="font-bold text-sm truncate mb-1">{playlist.name}</h3>
                    <p className="text-xs text-white/60 truncate">
                      {playlist.track_count || 0} piste{playlist.track_count > 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
              ];

              const shouldInsertAd = !userIsPremium && (index + 1) % 3 === 0;
              if (shouldInsertAd) {
                items.push(
                  <AdCardInline key={`playlist-ad-${index}`} userIsPremium={userIsPremium} />
                );
              }

              return items;
            })}
          </div>
        </div>
      ) : activeFilter === 'music' ? (
        // Vue Musique : Afficher les pistes les plus écoutées + blocs par catégorie
        <>
          {/* Bloc Musiques populaires */}
          <div className="mb-2">
            <h2 className="text-2xl font-bold mb-2">{t('home.popularTracks')}</h2>
            <div
              onScroll={handlePopularScroll}
              className="flex gap-3 overflow-x-auto scroll-smooth pb-2 pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {popularTracksForView.flatMap((track, index) => {
                const items = [
                  <button
                    key={`popular-${track.id}`}
                    onClick={() => onOpenArtist?.(track) || {}}
                    className="group bg-transparent hover:bg-white/10 rounded-lg transition-all text-left flex flex-col flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                  >
                    <div className="w-full aspect-square rounded-t-lg overflow-hidden relative" onClick={(e) => { e.stopPropagation(); handleHomePlayTrack(track, currentCollection); }}>
                      {track.cover_image ? (
                        <img loading="lazy" decoding="async"
                          src={getImageUrl(track.cover_image)}
                          alt={track.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">
                          {track.cover_emoji || '🎵'}
                        </div>
                      )}
                      {/* Icône Premium */}
                      {!!track.is_premium && !userIsPremium && (
                        <div className="absolute top-1 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1">
                          <PremiumCrown className="w-4 h-4" />
                        </div>
                      )}
                      {/* Like button - toujours visible (mobile inclus), en haut à gauche */}
                      {onToggleLike && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleLike(track.id);
                          }}
                          className={`absolute top-2 left-2 z-10 text-xl transition-transform hover:scale-110 ${
                            likedTrackIds.includes(track.id) ? 'text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text' : 'text-white/70 hover:text-white'
                          }`}
                          title={likedTrackIds.includes(track.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        >
                          {likedTrackIds.includes(track.id) ? '💚' : '🤍'}
                        </button>
                      )}
                      {/* Bouton play vert en bas à droite */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHomePlayTrack(track, currentCollection);
                        }}
                        className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hidden md:flex flex items-center justify-center text-black shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
                      >
                        <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          {currentTrack?.id === track.id && isPlaying ? (
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                          ) : (
                            <path d="M8 5v14l11-7z" />
                          )}
                        </svg>
                      </button>
                    </div>
                    <div className="p-1">
                      <h3 className="font-bold text-sm truncate mb-1 flex items-center gap-1">
                        {track.title}
                        {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-3 h-3 inline-flex flex-shrink-0" />}
                      </h3>
                    </div>
                  </button>
                ];

                if (!userIsPremium && popularAdOffset === 0 && index === 0) {
                  items.unshift(
                    <AdCardInline
                      key={`popular-ad-before-${index}`}
                      userIsPremium={userIsPremium}
                      className="flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                    />
                  );
                }

                const shouldInsertAd =
                  !userIsPremium &&
                  index + 1 >= popularAdOffset &&
                  (index + 1 - popularAdOffset) % 3 === 0;
                if (shouldInsertAd) {
                  items.push(
                    <AdCardInline
                      key={`popular-ad-${index}`}
                      userIsPremium={userIsPremium}
                      className="flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                    />
                  );
                }

                return items;
              })}
            </div>
          </div>

          {/* Blocs par catégorie (sans doublons) */}
          {filteredCategoriesWithTracks.map((category) => {
            const categoryData = shuffledCategoryTracksMap.get(category.id) || { tracks: [], adOffset: 1 };
            const filteredTracks = categoryData.tracks || [];
            const isExpanded = expandedCategoryIds.has(String(category.id));
            
            // Afficher seulement si il y a des tracks après filtrage
            if (filteredTracks.length === 0) return null;

            return (
              <div key={category.id} className="mb-2">
                <h2 className="text-2xl font-bold mb-2">{category.name}</h2>
                <div
                  onScroll={(event) => handleCategoryScroll(category.id, event)}
                  className="flex gap-3 overflow-x-auto scroll-smooth pb-2 pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {(() => {
                    const shuffledTracks = isExpanded ? filteredTracks : (filteredTracks || []).slice(0, 12);
                    const adOffset = Number.isFinite(categoryData.adOffset) ? categoryData.adOffset : 0; // 0..3
                    return shuffledTracks.flatMap((track, index) => {
                    const items = [
                      <button
                        key={`category-${category.id}-${track.id}`}
                        onClick={() => onOpenArtist?.(track) || {}}
                        className="group bg-transparent hover:bg-white/10 rounded-lg transition-all text-left flex flex-col flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                      >
                        <div className="w-full aspect-square rounded-t-lg overflow-hidden relative" onClick={(e) => { e.stopPropagation(); handleHomePlayTrack(track, currentCollection); }}>
                          {track.cover_image ? (
                            <img loading="lazy" decoding="async"
                              src={getImageUrl(track.cover_image)}
                              alt={track.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">
                              {track.cover_emoji || '🎵'}
                            </div>
                          )}
                          {/* Icône Premium */}
                          {!!track.is_premium && !userIsPremium && (
                            <div className="absolute top-1 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1">
                              <PremiumCrown className="w-4 h-4" />
                            </div>
                          )}
                          {/* Like button - toujours visible (mobile inclus), en haut à gauche */}
                          {onToggleLike && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleLike(track.id);
                              }}
                              className={`absolute top-2 left-2 z-10 text-sm transition-transform hover:scale-110 ${
                                likedTrackIds.includes(track.id) ? 'text-pink-400' : 'text-white/70 hover:text-white'
                              }`}
                              title={likedTrackIds.includes(track.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            >
                              {likedTrackIds.includes(track.id) ? '💚' : '🤍'}
                            </button>
                          )}
                          {/* Bouton play vert en bas à droite */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHomePlayTrack(track, currentCollection);
                            }}
                            className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hidden md:flex flex items-center justify-center text-black shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
                          >
                            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              {currentTrack?.id === track.id && isPlaying ? (
                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                              ) : (
                                <path d="M8 5v14l11-7z" />
                              )}
                            </svg>
                          </button>
                        </div>
                        <div className="p-1">
                          <h3 className="font-bold text-sm truncate mb-1 flex items-center gap-1">
                            {track.title}
                            {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-3 h-3 inline-flex flex-shrink-0" />}
                          </h3>
                        </div>
                      </button>
                    ];

                    if (!userIsPremium && adOffset === 0 && index === 0) {
                      items.unshift(
                        <AdCardInline
                          key={`category-ad-before-${category.id}-${index}`}
                          userIsPremium={userIsPremium}
                          className="flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                        />
                      );
                    }

                    const shouldInsertAd =
                      !userIsPremium &&
                      index + 1 >= adOffset &&
                      (index + 1 - adOffset) % 3 === 0;
                    if (shouldInsertAd) {
                      items.push(
                        <AdCardInline
                          key={`category-ad-${category.id}-${index}`}
                          userIsPremium={userIsPremium}
                          className="flex-none w-40 sm:w-44 md:w-48 lg:w-52 xl:w-56"
                        />
                      );
                    }

                    return items;
                  });
                  })()}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        // Vue Tout : Afficher contenu dynamique (sections créées par l'admin)
        <>
          {/* Sections Dynamiques - Toujours affichées */}
          {/* Bannière publicitaire en haut */}
          <AdBanner position="banner_top" userIsPremium={userIsPremium} />

          {filteredDynamicSections.map((section, sectionIndex) => {
            // Filtrer les moods des tracks de chaque section
            const sectionTracks = section.tracks ? section.tracks.filter(track => !track.is_mood) : [];
            if (sectionTracks.length === 0) return null;
            
            return (
            <div key={section.id} className="mb-2">
              <h2 className="text-2xl font-bold mb-2">{section.title}</h2>
              {section.description && (
                <p className="text-sm text-white/60 mb-2">{section.description}</p>
              )}
              <DynamicSectionRow
                tracks={sectionTracks}
                onPlayTrack={(track) => handleHomePlayTrack(track, currentCollection)}
                onOpenArtist={onOpenArtist}
                onToggleLike={onToggleLike}
                likedTrackIds={likedTrackIds}
                currentTrack={currentTrack}
                isPlaying={isPlaying}
                userIsPremium={userIsPremium}
                onReachEnd={() => loadAllSectionTracks(section.id)}
              />
            </div>
          );
          })}

          {/* Section Story */}
          {user && storiesEnabled === true && activeFilter === 'all' && (
            <StorySection
              stories={stories}
              onCreateStory={onCreateStory}
              onReportStory={onReportStory}
              onDeleteStory={onDeleteStory}
              isLoggedIn={!!user}
              onOpenStory={(index) => setOpenStoryIndex(index)}
              currentUser={user}
              onReachEnd={handleLoadAllStories}
            />
          )}

          {/* Playlists Admin - uniquement celles avec des pistes */}
          {shuffledAdminPlaylistsWithTracks.length > 0 && (
            <div className="mb-2">
              <h2 className="text-2xl font-bold mb-2">Playlists sélectionnées</h2>
              <p className="text-sm text-white/60 mb-2">Découvrez nos sélections</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-2">
                {shuffledAdminPlaylistsWithTracks.map((playlist, index) => {
                  const showAdAfterThis = !userIsPremium && (index + 1) % 3 === 0;
                  return (
                    <React.Fragment key={playlist.id}>
                      <button
                        onClick={() => onOpenPlaylist?.(playlist.id, true)}
                        className="group bg-transparent hover:bg-white/10 rounded-lg transition-all text-left flex flex-col"
                      >
                        <div className="w-full aspect-square rounded-t-lg overflow-hidden relative">
                          {playlist.cover_image ? (
                            <img loading="lazy" decoding="async" 
                              src={getImageUrl(playlist.cover_image, 'playlist')}
                              alt={playlist.name}
                              className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('❌ Erreur chargement image:', playlist.cover_image);
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<div class="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">${playlist.cover_emoji || '🎵'}</div>`;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-8xl">
                            {playlist.cover_emoji || '🎵'}
                          </div>
                        )}
                        {/* Bouton play vert en bas à droite */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            playCollectionFirstTrack({
                              id: `admin-playlist-${playlist.id}`,
                              type: 'admin-playlist',
                              playlistId: playlist.id,
                              title: playlist.name,
                              tracks: []
                            });
                          }}
                          className="absolute bottom-2 right-2 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hidden md:flex flex items-center justify-center text-black shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-all hover:scale-110"
                        >
                          <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </button>
                      </div>
                      <div className="p-1">
                        <h3 className="font-bold text-sm truncate mb-1">{playlist.name}</h3>
                        <p className="text-xs text-white/60 truncate">
                          {playlist.track_count || 0} piste{playlist.track_count > 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>
                    {showAdAfterThis && <AdCardInline userIsPremium={userIsPremium} />}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
      
          {/* Récemment écouté - seulement si l'utilisateur a un historique */}
          {user && filteredRecentlyPlayed && filteredRecentlyPlayed.length > 0 && (
            <HomeRow
              title={t('library.recentlyPlayed')}
            showScrollArrows
              tracks={recentlyTracksForView}
              onPlayTrack={(track) => handleHomePlayTrack(track, currentCollection)}
              onOpenArtist={onOpenArtist}
              onToggleLike={onToggleLike}
              currentTrack={currentTrack}
              likedTrackIds={likedTrackIds}
              isPlaying={isPlaying}
              userIsPremium={userIsPremium}
              onReachEnd={loadAllRecentlyPlayed}
            />
          )}

          {/* Section Moods - Affiche les catégories de moods */}
          {activeFilter === 'all' && shuffledMoodCategories && shuffledMoodCategories.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold"> Moods</h2>
                  <p className="text-sm text-white/60">Ambiances sonores pour chaque moment</p>
                </div>
                <button
                  onClick={() => setActiveFilter('moods')}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  Voir tout
                </button>
              </div>
              <div className="relative">
                {hasMoodsScrolledRight && (
                <button
                  type="button"
                  onClick={() => scrollMoods('left')}
                  aria-label="Défiler vers la gauche"
                  className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
                >
                  ‹
                </button>
              )}

                <div
                  ref={moodsScrollRef}
                  onScroll={handleHomeMoodsScroll}
                  className="flex gap-4 overflow-x-auto scroll-smooth pb-2 pr-2"
                  style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
                >
                  {homeMoodCategories.flatMap((category, index) => {
                    const items = [
                      <button
                        key={`mood-${category.id}`}
                        onClick={() => {
                          if (!user) {
                            onOpenAuthModal?.();
                            return;
                          }
                          // Section Moods (accueil) : ouvrir la page de la catégorie
                          setActiveFilter('moods');
                          setSelectedMoodCategory(category);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="group relative aspect-[16/10] w-64 sm:w-72 md:w-80 flex-shrink-0 rounded-2xl overflow-hidden bg-gradient-to-br from-purple-600/50 to-indigo-800/50 hover:scale-[1.02] hover:z-10 focus-visible:z-10 transition-transform duration-300 will-change-transform"
                      >
                        {category.cover_image ? (
                          <img
                            src={category.cover_image}
                            alt={category.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-800"></div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 p-3 flex flex-col justify-end">
                          <h3
                            className="text-sm font-bold text-white truncate"
                            style={{
                              fontSize: "1.1rem",
                              fontWeight: 900,
                              letterSpacing: "-0.06em",
                              textTransform: "uppercase",
                              color: "#ffffffd7",
                              lineHeight: "1",
                            }}
                          >
                            {category.name}
                          </h3>
                          {category.mood_count > 0 && (
                            <p className="text-xs text-white/60">{category.mood_count} pistes</p>
                          )}
                        </div>
                      </button>
                    ];

                    if (!userIsPremium && homeMoodAdOffset === 0 && index === 0) {
                      items.unshift(
                        <AdCardInline
                          key={`mood-ad-before-${index}`}
                          userIsPremium={userIsPremium}
                          variant="landscape"
                          className="w-64 sm:w-72 md:w-80 flex-shrink-0"
                        />
                      );
                    }

                    const shouldInsertAd =
                      !userIsPremium &&
                      index + 1 >= homeMoodAdOffset &&
                      (index + 1 - homeMoodAdOffset) % 3 === 0;

                    if (shouldInsertAd) {
                      items.push(
                        <AdCardInline
                          key={`mood-ad-${index}`}
                          userIsPremium={userIsPremium}
                          variant="landscape"
                          className="w-64 sm:w-72 md:w-80 flex-shrink-0"
                        />
                      );
                    }

                    return items;
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => scrollMoods('right')}
                  aria-label="Défiler vers la droite"
                  className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
                >
                  ›
                </button>
              </div>
            </div>
          )}
      
          {/* Nouveautés - toujours affiché */}
          <HomeRow
            title={t('home.newReleases')}
            showScrollArrows
            tracks={newReleaseTracksForView}
            onPlayTrack={(track) => handleHomePlayTrack(track, currentCollection)}
            onOpenArtist={onOpenArtist}
            onToggleLike={onToggleLike}
            currentTrack={currentTrack}
            likedTrackIds={likedTrackIds}
            isPlaying={isPlaying}
            userIsPremium={userIsPremium}
            onReachEnd={loadAllNewReleases}
          />

          {/* Bannière publicitaire en bas */}
          <AdBanner position="banner_bottom" userIsPremium={userIsPremium} />
        </>
      )}
    </div>
  );
}

// ---- Liked Tracks page ----
function LikedTracksView({ tracks, onPlayTrack, onToggleLike, currentTrack, isPlaying, likedTrackIds, userPlaylists, userFolders, onAddToPlaylist, onShowToast, userIsPremium = false, user, onOpenSubscription }) {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [contextMenu, setContextMenu] = useState({ isOpen: false, track: null, position: { x: 0, y: 0 } });
  const currentTrackInList = !!(currentTrack && tracks?.some((t) => t.id === currentTrack.id));
  const isListPlaying = currentTrackInList && !!isPlaying;
  
  const handleContextMenu = (e, track) => {
    e.preventDefault();
    e.stopPropagation();
    const buttonRect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      track,
      position: { 
        x: buttonRect.left - 210,
        y: buttonRect.top 
      }
    });
  };
  
  if (!tracks || tracks.length === 0) return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="text-6xl mb-2">💚</div>
      <h2 className="text-2xl font-bold mb-2">{t('library.likedTracks')}</h2>
      <p className="text-white/60">{t('library.noItems')}</p>
    </div>
  );

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative h-44 sm:h-56 md:h-80 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/30 via-purple-700/20 to-black/80"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50"></div>
        
        <div className="relative h-full flex items-end p-4 sm:p-6 md:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 sm:gap-6">
            {/* Icon */}
            <div className="w-52 h-52 rounded shadow-2xl flex items-center justify-center text-8xl flex-shrink-0 bg-gradient-to-br from-purple-500 to-blue-500">
              💚
            </div>
            
            {/* Info */}
            <div className="flex flex-col justify-end sm:pb-2">
              <span className="text-sm font-bold uppercase tracking-wide mb-2">{t('playlist.title')}</span>
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-black tracking-tight mb-2 leading-none break-words max-w-full">
                {t('library.likedTracks')}
              </h1>
              <p className="text-base text-white/90 font-medium">
                {tracks.length.toLocaleString()} {t('artist.tracks')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - pleine largeur sur mobile */}
      <div className="bg-gradient-to-b from-black/40 to-black px-2 md:px-8 pt-6">
        {/* Publicité page */}
        <AdPageBanner userIsPremium={userIsPremium} />

        {/* Action Buttons */}
        <div className="flex items-center gap-1 mb-5 px-2 md:px-0">
          <button
            onClick={() => {
              if (currentTrackInList && currentTrack) {
                onPlayTrack(currentTrack);
                return;
              }
              onPlayTrack(tracks[0]);
            }}
            className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:bg-pink-400 hover:scale-105 transition-all flex items-center justify-center shadow-lg"
            title={currentTrackInList ? 'Lire / Pause' : 'Lire'}
          >
            {isListPlaying ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>

        {/* Tracks List */}
        <div>
          <div className="space-y-1">
            {tracks.map((track, index) => (
              <div
                key={track.id}
                className={`group w-full grid grid-cols-[auto,1fr,auto,auto,auto] md:grid-cols-[auto,1fr,auto,auto,auto] gap-1 md:gap-1 items-center px-2 md:px-4 py-2 md:py-3 rounded hover:bg-white/10 transition-colors ${
                  currentTrack?.id === track.id ? 'bg-white/10' : ''
                }`}
              >
                {/* Number + Image - Clickable pour jouer */}
                <div 
                  className="flex items-center gap-1 md:gap-1 w-14 md:w-20 cursor-pointer"
                  onClick={() => onPlayTrack(track)}
                >
                  <span className={`w-4 text-right text-xs md:text-sm ${currentTrack?.id === track.id ? 'text-pink-400' : 'text-white/50'}`}>
                    {index + 1}
                  </span>
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded overflow-hidden flex-shrink-0">
                    {track.cover_image ? (
                      <img loading="lazy" decoding="async"
                        src={getImageUrl(track.cover_image)}
                        alt={track.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-sm md:text-lg">
                        {track.cover_emoji || '🎵'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Title + Artist - Clickable pour jouer */}
                <div 
                  className="flex flex-col items-start min-w-0 cursor-pointer"
                  onClick={() => onPlayTrack(track)}
                >
                  <span className={`text-sm md:text-base font-normal truncate flex items-center gap-1 ${currentTrack?.id === track.id ? 'text-pink-400' : 'text-white'}`}>
                    {track.title}
                    {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-4 h-4 inline-flex flex-shrink-0" />}
                  </span>
                  <span className="text-xs md:text-sm text-white/60 truncate">{track.artist || 'Artiste'}</span>
                </div>

                {/* Like button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleLike(track.id);
                  }}
                  className="hidden md:flex opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center"
                >
                  <svg className={`w-5 h-5 ${likedTrackIds?.includes(track.id) ? 'fill-green-500' : 'fill-none stroke-white/60 hover:stroke-white'}`} viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>

                {/* Duration */}
                <span className="text-xs md:text-sm text-white/60 tabular-nums text-right">
                  {Math.floor(track.duration / 60)}:{String(track.duration % 60).padStart(2, '0')}
                </span>

                {/* Menu 3 points - toujours visible sur mobile */}
                <button
                  onClick={(e) => handleContextMenu(e, track)}
                  className="flex w-8 h-8 items-center justify-center text-white/70 hover:text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Context Menu */}
      <TrackContextMenu
        track={contextMenu.track}
        isOpen={contextMenu.isOpen}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        position={contextMenu.position}
        playlists={userPlaylists}
        folders={userFolders}
        onAddToPlaylist={onAddToPlaylist}
        onToggleLike={onToggleLike}
        isLiked={contextMenu.track && likedTrackIds?.includes(contextMenu.track.id)}
        onShowToast={onShowToast}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={onOpenSubscription}
      />
    </div>
  );
}

// ---- Account Settings Panel ----
function AccountSettingsPanel({ isOpen, onClose, onSelectOption, isEmbedded = false }) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const options = [
    {
      category: t('profile.personalInfo'),
      items: [
        { id: 'personal-info', label: t('profile.editProfile'), icon: '✏️' },
        { id: 'address', label: t('profile.address'), icon: '🏠' }
      ]
    },
    {
      category: t('profile.subscription'),
      items: [
        { id: 'subscription', label: t('profile.subscription'), icon: '🎵' },
        { id: 'cancel-account', label: t('profile.cancelAccount'), icon: '✖️' }
      ]
    }
  ];

  const contentInner = (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-2xl md:text-2xl font-bold">{t('nav.settings')}</h2>
      <button 
        onClick={onClose}
        className="w-10 h-10 md:w-8 md:h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
      >
        <svg className="w-7 h-7 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  );

  const optionsList = (
    <>
      {options.map((section, idx) => (
        <div key={idx} className={isEmbedded ? "mb-1" : "mb-2"}>
          <h3 className={`text-lg md:text-lg font-bold ${isEmbedded ? "mb-1" : "mb-2"}`}>{section.category}</h3>
              <div className={isEmbedded ? "space-y-1" : "space-y-2"}>
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      onSelectOption(item.id);
                      onClose();
                    }}
                    className={`w-full flex items-center justify-between ${isEmbedded ? "px-3 py-2" : "p-4"} rounded-lg hover:bg-white/5 transition-colors text-left group text-base`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-3xl md:text-2xl">{item.icon}</span>
                      <span className="text-base">{item.label}</span>
                    </div>
                    <svg className="w-5 h-5 text-white/60 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                    </svg>
                  </button>
                ))}
              </div>
        </div>
      ))}
    </>
  );

  if (isEmbedded) {
    return (
      <div className="w-full h-full text-white flex flex-col">
        <div className="pt-2 pb-4">
          {contentInner}
        </div>
        <div className="flex-1 overflow-y-auto">
          {optionsList}
        </div>
      </div>
    );
  }

  const content = (
    <div className="h-full w-96 bg-[#121212] text-white flex flex-col">
      <div className="p-6 flex-1 overflow-y-auto">
        {contentInner}
        {optionsList}
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      ></div>
      
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-96 z-50 shadow-2xl overflow-hidden">
        {content}
      </div>
    </>
  );
}

// ---- Profile page ----
function ProfileView({ user, userPlaylists, likedTracks, stories = [], storiesEnabled, onPlayTrack, onToggleLike, currentTrack, likedTrackIds, playCollectionFirstTrack, onUpdateProfileImage, onUpdateCoverImage, onOpenStory, onDeleteStory }) {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [showCoverSuccess, setShowCoverSuccess] = useState(false);
  const [storyMenuId, setStoryMenuId] = useState(null);
  const userStories = useMemo(() => {
    if (!user?.id) return [];
    return (stories || []).filter((story) => story.user_id === user.id);
  }, [stories, user?.id]);

  const resolveStoryImage = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const filename = url.split('/').pop();
    return `${BACKEND_URL}/api/images/${filename}`;
  };

  const getStoryFitClass = (story) => {
    const w = Number(story?.format_width);
    const h = Number(story?.format_height);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return h >= w ? 'object-cover' : 'object-contain';
    }
    if (story?.format_id === 'mobile-9-16') return 'object-cover';
    if (story?.format_id === 'landscape-16-9') return 'object-contain';
    if (story?.format_id === 'square-1-1') return 'object-contain';
    return 'object-contain';
  };

  const inferVideoType = (url = '') => {
    const clean = url.split('?')[0].toLowerCase();
    if (clean.endsWith('.mp4')) return 'video/mp4';
    if (clean.endsWith('.mov')) return 'video/quicktime';
    if (clean.endsWith('.webm') || clean.endsWith('.bin')) return 'video/webm';
    return undefined;
  };

  useEffect(() => {
    const handleOutside = (event) => {
      if (storyMenuId && !event.target.closest?.('[data-story-menu]')) {
        setStoryMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [storyMenuId]);

  const handleProfileImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleCoverClick = () => {
    coverInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert(t('common.error'));
      return;
    }

    // Vérifier la taille du fichier (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB en bytes
    if (file.size > maxSize) {
      alert('Le fichier est trop volumineux. Taille maximum : 10MB');
      return;
    }

    setIsUploading(true);
    
    const formData = new FormData();
    formData.append('profile_image', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/profile/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        onUpdateProfileImage(data.profile_image);
        alert('Photo de profil mise à jour avec succès !');
      } else {
        alert(data.error || 'Erreur lors du téléchargement de l\'image');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du téléchargement : ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(t('common.error'));
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Le fichier est trop volumineux. Taille maximum : 10MB');
      return;
    }

    setIsUploadingCover(true);

    const formData = new FormData();
    formData.append('cover_image', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/profile/cover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        if (typeof onUpdateCoverImage === 'function') {
          onUpdateCoverImage(data.cover_image);
        }
        setShowCoverSuccess(true);
      } else {
        alert(data.error || 'Erreur lors du téléchargement de l\'image');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du téléchargement : ' + error.message);
    } finally {
      setIsUploadingCover(false);
    }
  };

  if (!user) return null;

  return (
    <div className="flex flex-col w-full overflow-x-hidden">
      <InfoModal
        isOpen={showCoverSuccess}
        onClose={() => setShowCoverSuccess(false)}
        title="Succès"
        message="Image de couverture mise à jour avec succès !"
      />
      {/* Hero Section */}
      <div className="relative w-full overflow-x-hidden h-[clamp(11rem,40vw,14rem)] sm:h-56 md:h-80 overflow-hidden">
        <img
          key={user.cover_image || 'default-cover'}
          src={getUserCoverUrl(user.cover_image)}
          alt="Cover"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/50 via-purple-700/30 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>

        <button
          onClick={handleCoverClick}
          disabled={isUploadingCover}
          className="absolute top-3 right-3 z-10 px-3 py-1.5 rounded-full text-xs font-semibold bg-black/60 hover:bg-black/80 text-white transition"
        >
          {isUploadingCover ? 'Téléchargement…' : 'Changer la couverture'}
        </button>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          onChange={handleCoverChange}
          className="hidden"
        />
        
        <div className="relative h-full flex items-end p-4 sm:p-6 md:p-8">
          <div className="flex flex-row items-end gap-4 sm:gap-6 w-full max-w-full">
            {/* Profile Picture */}
            <div className="relative group">
              <button
                onClick={handleProfileImageClick}
                disabled={isUploading}
                className="w-[clamp(5rem,22vw,6.5rem)] h-[clamp(5rem,22vw,6.5rem)] sm:w-36 sm:h-36 md:w-52 md:h-52 rounded-full shadow-2xl flex items-center justify-center flex-shrink-0 overflow-hidden relative"
              >
                <img
                  loading="lazy"
                  decoding="async"
                  src={getUserImageUrl(user.profile_image)}
                  alt={user.email}
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                  <svg className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white mb-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 4V1h2v3h3v2H5v3H3V6H0V4h3zm3 6V7h3V4h7l1.83 2H21c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V10h3zm7 9c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-3.2-5c0 1.77 1.43 3.2 3.2 3.2s3.2-1.43 3.2-3.2-1.43-3.2-3.2-3.2-3.2 1.43-3.2 3.2z"/>
                  </svg>
                  <span className="text-white text-sm font-semibold">
                    {isUploading ? 'Téléchargement...' : 'Choisir une photo'}
                  </span>
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            
            {/* Info */}
            <div className="flex flex-col justify-end sm:pb-2 min-w-0 flex-1">
              <span className="text-sm font-bold uppercase tracking-wide mb-2">{t('profile.title')}</span>
              <h1 className="text-2xl sm:text-5xl md:text-6xl lg:text-8xl font-black tracking-tight mb-2 leading-none break-words max-w-full">
                {user.email?.split('@')[0] || t('profile.title')}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-gradient-to-b from-black/40 to-black px-4 sm:px-6 md:px-8 pt-6">
        {/* Playlists publiques */}
        {userPlaylists && userPlaylists.length > 0 && (
          <div className="mb-2">
            <h2 className="text-2xl font-bold mb-3">{t('library.playlists')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-2">
              {userPlaylists.map((playlist) => (
                <button
                  key={playlist.id}
                  onClick={() => playCollectionFirstTrack({
                    id: `user-playlist-${playlist.id}`,
                    type: 'user-playlist',
                    playlistId: playlist.id,
                    title: playlist.name,
                    tracks: []
                  })}
                  className="group bg-transparent hover:bg-white/10 rounded-lg transition-all text-left flex flex-col"
                >
                  <div className="w-full aspect-square rounded-lg mb-2 overflow-hidden bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                    <span className="text-6xl">{playlist.cover_emoji || '🎵'}</span>
                  </div>
                  <h3 className="font-bold text-base truncate mb-1">{playlist.name}</h3>
                  <p className="text-sm text-white/60 truncate">
                    Playlist • {user.email?.split('@')[0] || 'Vous'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {storiesEnabled === true && (
          <div className="mb-2">
            <h2 className="text-2xl font-bold mb-3">Mes stories</h2>
            {userStories.length === 0 ? (
              <div className="text-sm text-white/60">Aucune story publiée pour le moment.</div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2 pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {userStories.map((story) => {
                  const thumb = resolveStoryImage(story.thumbnail_url);
                  const fitClass = getStoryFitClass(story);
                  const videoType = inferVideoType(story.video_url || '');
                  return (
                    <div
                      key={story.id}
                      onClick={() => onOpenStory?.(story.id)}
                      className="relative w-40 sm:w-44 md:w-48 aspect-[9/16] flex-shrink-0 rounded-2xl overflow-hidden bg-white/5 border border-white/10 text-left cursor-pointer"
                    >
                      <button
                        type="button"
                        data-story-menu
                        onClick={(e) => {
                          e.stopPropagation();
                          setStoryMenuId(storyMenuId === story.id ? null : story.id);
                        }}
                        className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white/80 flex items-center justify-center"
                        title="Options"
                      >
                        ⋯
                      </button>
                      {storyMenuId === story.id && (
                        <div
                          data-story-menu
                          className="absolute top-11 right-2 z-20 min-w-[140px] bg-[#14141f] border border-white/10 rounded-lg shadow-xl overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStoryMenuId(null);
                              onDeleteStory?.(story.id);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 text-red-300"
                          >
                            Supprimer
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStoryMenuId(null);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 text-white/80"
                          >
                            Fermer
                          </button>
                        </div>
                      )}
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={`Story ${story.id}`}
                          className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none`}
                          loading="lazy"
                        />
                      ) : (
                        <video
                          className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none`}
                          muted
                          playsInline
                          preload="metadata"
                          onLoadedMetadata={(e) => {
                            try {
                              const v = e.currentTarget;
                              const stillTime = Math.min(0.01, Math.max((v.duration || 0.01) - 0.01, 0));
                              v.currentTime = stillTime;
                              v.pause();
                            } catch (_) {}
                          }}
                        >
                          <source src={story.video_url} type={videoType} />
                        </video>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none"></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Titres likés */}
        {likedTracks && likedTracks.length > 0 && (
          <div className="mb-2 md:mb-2">
            <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">{t('library.likedTracks')}</h2>
            <div className="space-y-1">
              {likedTracks.slice(0, 10).map((track, index) => (
                <button
                  key={track.id}
                  onClick={() => onPlayTrack(track)}
                  className={`group w-full grid grid-cols-[auto,1fr,auto] gap-1 md:gap-1 items-center px-2 md:px-4 py-2 md:py-3 rounded hover:bg-white/10 transition-colors ${
                    currentTrack?.id === track.id ? 'bg-white/10' : ''
                  }`}
                >
                  {/* Number + Image */}
                  <div className="flex items-center gap-1 md:gap-1">
                    <span className="w-3 md:w-4 text-xs md:text-sm text-white/60">{index + 1}</span>
                    <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                      {track.cover_image ? (
                        <img loading="lazy" decoding="async"
                          src={getImageUrl(track.cover_image)}
                          alt={track.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-base md:text-lg">
                          {track.cover_emoji || '🎵'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Title + Artist */}
                  <div className="flex flex-col items-start min-w-0">
                    <span className={`text-sm md:text-base font-normal truncate w-full ${currentTrack?.id === track.id ? 'text-pink-400' : 'text-white'}`}>
                      {track.title}
                    </span>
                    <span className="text-xs md:text-sm text-white/60 truncate w-full">{track.artist || 'Artiste'}</span>
                  </div>

                  {/* Like button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleLike(track.id);
                    }}
                    className="opacity-100"
                  >
                    <svg className="w-5 h-5 fill-green-500" viewBox="0 0 24 24">
                      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---- Personal Information Page ----
function PersonalInfoView({ user, onUpdateInfo, onBack }) {
  const { t } = useTranslation();
  // Formater la date au format YYYY-MM-DD pour l'input HTML
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    email: user?.email || '',
    gender: user?.gender || '',
    birthdate: formatDateForInput(user?.birthdate) || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Mettre à jour le formulaire quand les données utilisateur changent
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        gender: user.gender || '',
        birthdate: formatDateForInput(user.birthdate) || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onUpdateInfo(formData);
      setShowSuccessModal(true);
    } catch (error) {
      alert(t('messages.errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <InfoModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Succès"
        message={t('messages.profileUpdated')}
      />
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-white/60 hover:text-white mb-3"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
        {t('common.cancel')}
      </button>

      <h1 className="text-4xl font-bold mb-2">{t('profile.personalInfo')}</h1>
      <p className="text-white/60 mb-2">{t('profile.editProfile')}</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-semibold mb-2">{t('profile.email')}</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">{t('profile.gender')}</label>
          <select
            value={formData.gender}
            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
          >
            <option value="">{t('common.none')}</option>
            <option value="male">{t('profile.male')}</option>
            <option value="female">{t('profile.female')}</option>
            <option value="other">{t('profile.other')}</option>
            <option value="prefer-not-to-say">{t('profile.preferNotToSay')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">{t('profile.birthdate')}</label>
          <input
            type="date"
            value={formData.birthdate}
            onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
          />
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-semibold transition-colors disabled:opacity-50"
        >
          {isSaving ? t('common.loading') : t('profile.saveProfile')}
        </button>
      </form>
    </div>
  );
}

// ---- Address Page ----
function AddressView({ user, onUpdateAddress, onBack }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    street: user?.street || '',
    city: user?.city || '',
    postal_code: user?.postal_code || '',
    country: user?.country || 'France'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onUpdateAddress(formData);
      setShowSuccessModal(true);
    } catch (error) {
      alert(t('messages.errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <InfoModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Succès"
        message={t('messages.addressUpdated')}
      />
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-white/60 hover:text-white mb-3"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
        {t('common.cancel')}
      </button>

      <h1 className="text-4xl font-bold mb-2">{t('profile.postalAddress')}</h1>
      <p className="text-white/60 mb-2">{t('profile.address')}</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-semibold mb-2">{t('profile.street')}</label>
          <input
            type="text"
            value={formData.street}
            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
            placeholder="123 Rue de la Musique"
          />
        </div>

        <div className="grid grid-cols-2 gap-1">
          <div>
            <label className="block text-sm font-semibold mb-2">{t('profile.postalCode')}</label>
            <input
              type="text"
              value={formData.postal_code}
              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
              placeholder="75001"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">{t('profile.city')}</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
              placeholder="Paris"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">{t('profile.country')}</label>
          <select
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-pink-500/50"
          >
            <option value="France">France</option>
            <option value="Belgique">Belgique</option>
            <option value="Suisse">Suisse</option>
            <option value="Canada">Canada</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-semibold transition-colors disabled:opacity-50"
        >
          {isSaving ? t('common.loading') : t('profile.saveAddress')}
        </button>
      </form>
    </div>
  );
}

// ---- Subscription Page ----
function SubscriptionView({ user, onSubscribe, onBack }) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState('free'); // 'free' or 'premium'
  const [selectedDuration, setSelectedDuration] = React.useState('monthly'); // 'monthly', 'quarterly', 'annual'

  const handleSubscribe = async () => {
    if (selectedPlan === 'free') {
      // Pas d'action pour le plan gratuit
      onBack();
      return;
    }
    
    setIsProcessing(true);
    try {
      await onSubscribe(`premium-${selectedDuration}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Prix selon la durée
  const getPremiumPrice = () => {
    switch (selectedDuration) {
      case 'monthly':
        return { price: 9, suffix: t('subscription.perMonth') };
      case 'quarterly':
        return { price: 24, suffix: t('subscription.perQuarter'), savings: '11%' };
      case 'annual':
        return { price: 90, suffix: t('subscription.perYear'), savings: '17%' };
      default:
        return { price: 9, suffix: t('subscription.perMonth') };
    }
  };

  const premiumPricing = getPremiumPrice();

  // Vérifier si l'utilisateur est déjà premium
  const userIsPremium = user?.is_premium || user?.subscription_type === 'premium';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-white/60 hover:text-white mb-3"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
        {t('common.back')}
      </button>

      <h1 className="text-4xl font-bold mb-2">{t('profile.chooseSubscription')}</h1>
      <p className="text-white/60 mb-2">{t('profile.enjoyPremium')}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-3">
        {/* Plan Gratuit */}
        <div
          onClick={() => setSelectedPlan('free')}
          className={`relative cursor-pointer rounded-2xl p-6 transition-all ${
            selectedPlan === 'free'
              ? 'bg-white/10 border-2 border-white'
              : 'bg-white/5 border border-white/10 hover:bg-white/10'
          }`}
        >
          {/* Radio indicator */}
          <div className="absolute top-6 right-6">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              selectedPlan === 'free' ? 'border-white' : 'border-white/40'
            }`}>
              {selectedPlan === 'free' && (
                <div className="w-3 h-3 rounded-full bg-white" />
              )}
            </div>
          </div>

          {/* Current plan badge */}
          {!userIsPremium && (
            <div className="inline-block mb-2 px-3 py-1 bg-white/20 rounded-full text-xs font-semibold">
              {t('subscription.currentPlan')}
            </div>
          )}

          <div className="mb-3">
            <h3 className="text-2xl font-bold mb-2">{t('subscription.freePlan')}</h3>
            <p className="text-white/60 text-sm">{t('subscription.freePlanDescription')}</p>
          </div>

          <div className="flex items-baseline gap-1 mb-3">
            <span className="text-4xl font-bold">0€</span>
          </div>

          <ul className="space-y-3">
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-pink-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.freeFeature1')}</span>
            </li>
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-pink-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.freeFeature2')}</span>
            </li>
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-pink-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.freeFeature3')}</span>
            </li>
            <li className="flex items-center gap-1 text-sm text-white/40">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              <span>{t('subscription.premiumFeature1')}</span>
            </li>
          </ul>
        </div>

        {/* Plan Premium */}
        <div
          onClick={() => setSelectedPlan('premium')}
          className={`relative cursor-pointer rounded-2xl p-6 transition-all ${
            selectedPlan === 'premium'
              ? 'bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-2 border-yellow-500'
              : 'bg-white/5 border border-white/10 hover:bg-white/10'
          }`}
        >
          {/* Radio indicator */}
          <div className="absolute top-6 right-6">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              selectedPlan === 'premium' ? 'border-yellow-500' : 'border-white/40'
            }`}>
              {selectedPlan === 'premium' && (
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
              )}
            </div>
          </div>

          {/* Current plan badge for premium users */}
          {userIsPremium && (
            <div className="inline-block mb-2 px-3 py-1 bg-yellow-500/20 rounded-full text-xs font-semibold text-yellow-500">
              {t('subscription.currentPlan')}
            </div>
          )}

          {/* Crown icon */}
          <div className="flex items-center gap-1 mb-2">
            <svg 
              viewBox="0 0 24 24" 
              fill="currentColor" 
              className="w-6 h-6 text-yellow-500"
            >
              <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
            </svg>
            <span className="text-xs font-semibold text-yellow-500 uppercase">{t('subscription.popular')}</span>
          </div>

          <div className="mb-2">
            <h3 className="text-2xl font-bold mb-2">{t('subscription.premiumPlan')}</h3>
            <p className="text-white/60 text-sm">{t('subscription.premiumPlanDescription')}</p>
          </div>

          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-4xl font-bold">{premiumPricing.price}€</span>
            <span className="text-white/60">{premiumPricing.suffix}</span>
          </div>
          {premiumPricing.savings && (
            <div className="text-pink-400 text-sm font-semibold mb-2">
              {t('subscription.savePercent', { percent: premiumPricing.savings })}
            </div>
          )}

          {/* Sélection de durée */}
          {selectedPlan === 'premium' && (
            <div className="mb-3 p-4 bg-black/20 rounded-xl">
              <p className="text-sm font-semibold mb-3">{t('subscription.chooseDuration')}</p>
              <div className="space-y-2">
                <label className="flex items-center gap-1 cursor-pointer p-1 rounded-lg hover:bg-white/5">
                  <input
                    type="radio"
                    name="duration"
                    value="monthly"
                    checked={selectedDuration === 'monthly'}
                    onChange={(e) => setSelectedDuration(e.target.value)}
                    className="w-4 h-4 accent-yellow-500"
                  />
                  <span className="flex-1">{t('subscription.oneMonth')}</span>
                  <span className="font-semibold">9€</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer p-1 rounded-lg hover:bg-white/5">
                  <input
                    type="radio"
                    name="duration"
                    value="quarterly"
                    checked={selectedDuration === 'quarterly'}
                    onChange={(e) => setSelectedDuration(e.target.value)}
                    className="w-4 h-4 accent-yellow-500"
                  />
                  <span className="flex-1">{t('subscription.threeMonths')}</span>
                  <span className="font-semibold">24€</span>
                  <span className="text-xs text-pink-400">-11%</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer p-1 rounded-lg hover:bg-white/5">
                  <input
                    type="radio"
                    name="duration"
                    value="annual"
                    checked={selectedDuration === 'annual'}
                    onChange={(e) => setSelectedDuration(e.target.value)}
                    className="w-4 h-4 accent-yellow-500"
                  />
                  <span className="flex-1">{t('subscription.twelveMonths')}</span>
                  <span className="font-semibold">90€</span>
                  <span className="text-xs text-pink-400">-17%</span>
                </label>
              </div>
            </div>
          )}

          <ul className="space-y-3">
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.premiumFeature1')}</span>
            </li>
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.premiumFeature2')}</span>
            </li>
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.premiumFeature3')}</span>
            </li>
            <li className="flex items-center gap-1 text-sm">
              <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{t('subscription.premiumFeature4')}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bouton de validation */}
      <button
        onClick={handleSubscribe}
        disabled={isProcessing}
        className={`w-full py-4 rounded-full font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
          selectedPlan === 'premium'
            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black'
            : 'bg-white/10 hover:bg-white/20 text-white'
        }`}
      >
        {isProcessing ? t('common.loading') : t('subscription.selectPlan')}
      </button>
    </div>
  );
}

// ---- Cancel Account Page ----
function CancelAccountView({ onCancel, onBack }) {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 relative">
      <button
        onClick={onBack}
        className="absolute top-6 left-6 flex items-center gap-1 text-white/60 hover:text-white"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
        {t('common.cancel')}
      </button>

      <div className="max-w-2xl w-full text-center">
        <h1 className="text-5xl font-bold mb-2">{t('profile.cancelAccountTitle')}</h1>
        <p className="text-lg text-white/80 mb-16">{t('profile.cancelAccountWarning')}</p>

        <div className="flex gap-1 justify-center">
          <button
            onClick={onBack}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-bold text-lg hover:scale-105 transition-transform"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => setIsConfirming(true)}
            className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-full font-bold text-lg transition-colors"
          >
            {isMobile ? t('profile.confirmCancelShort') : t('profile.confirmCancel')}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirming && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6 modal-text">
          <div className="bg-[#282828] rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-2">{t('common.confirm')}</h2>
            <p className="text-white/80 mb-3">{t('profile.cancelAccountWarning')}</p>
            <div className="flex gap-1">
              <button
                onClick={() => setIsConfirming(false)}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-full font-semibold transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={onCancel}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 rounded-full font-semibold transition-colors"
              >
                {t('profile.cancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Artist page ----
function ArtistView({ artist, tracks, onPlayTrack, currentTrack, isPlaying, userPlaylists, userFolders, onAddToPlaylist, likedTrackIds, onToggleLike, user, onShowToast, userIsPremium = false, onOpenSubscription }) {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [contextMenu, setContextMenu] = useState({ isOpen: false, track: null, position: { x: 0, y: 0 } });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentTrackInList = !!(currentTrack && tracks?.some((t) => t.id === currentTrack.id));
  const isListPlaying = currentTrackInList && !!isPlaying;
  
  if (!artist) return null;

  
  // Indique si une piste est réellement en lecture dans cette vue.
  // (ArtistView ne dispose pas de audioRef, on se base donc sur la piste courante + isPlaying.)
  const isTrackActuallyPlaying = useCallback(
    (trackId) => currentTrack?.id === trackId && !!isPlaying,
    [currentTrack?.id, isPlaying]
  );
// Vérifier si l'utilisateur est abonné à cet artiste
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || !artist.name) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/artist-subscriptions/check/${encodeURIComponent(artist.name)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setIsSubscribed(data.isSubscribed);
      } catch (error) {
        console.error('Erreur vérification abonnement:', error);
      }
    };

    checkSubscription();
  }, [artist.name, user, BACKEND_URL]);

  const handleSubscribeToggle = async () => {
    if (!user || isLoading) return;
    
    setIsLoading(true);
    try {
      const url = isSubscribed 
        ? `${BACKEND_URL}/api/artist-subscriptions/unsubscribe`
        : `${BACKEND_URL}/api/artist-subscriptions/subscribe`;
      
      // Utilise toujours POST pour compatibilité OVH mutualisé
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ artistName: artist.name })
      });

      if (response.ok) {
        setIsSubscribed(!isSubscribed);
      }
    } catch (error) {
      console.error('Erreur abonnement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContextMenu = (e, track) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Obtenir la position du bouton (3 points)
    const buttonRect = e.currentTarget.getBoundingClientRect();
    
    // Positionner le menu à gauche du bouton
    setContextMenu({
      isOpen: true,
      track,
      position: { 
        x: buttonRect.left - 210, // 210px est la largeur du menu
        y: buttonRect.top 
      }
    });
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section with Cover Image Background */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        {/* Cover Image or Gradient Background */}
        {artist.cover_image ? (
          <>
            <img loading="lazy" decoding="async"
              src={getImageUrl(artist.cover_image)}
              alt={artist.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black"></div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-purple-900 via-purple-800 to-black"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
          </>
        )}
        
        <div className="relative h-full flex items-end p-4 md:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-1 md:gap-6 w-full">
            {/* Artist Profile Image - Large Circular */}
            <div className="w-32 h-32 md:w-52 md:h-52 rounded-full shadow-2xl flex items-center justify-center text-4xl md:text-7xl flex-shrink-0 overflow-hidden border-2 md:border-4 border-white/10">
              {artist.profile_image ? (
                <img loading="lazy" decoding="async"
                  src={getImageUrl(artist.profile_image)}
                  alt={artist.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
                  {artist.emoji || '🎤'}
                </div>
              )}
            </div>
            
            {/* Artist Info */}
            <div className="flex flex-col justify-end pb-1 md:pb-2">
              <span className="text-xs md:text-sm font-bold uppercase tracking-wide mb-1 md:mb-2">{t('artist.title')}</span>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-black tracking-tight mb-2 md:mb-2 leading-none">
                {artist.name || t('artist.title')}
              </h1>
              <p className="text-sm md:text-base text-white/90 font-medium">
                {tracks ? tracks.length.toLocaleString() : 0} {t('artist.tracks')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - pleine largeur sur mobile */}
      <div className="bg-gradient-to-b from-black/40 to-black px-2 md:px-6 lg:px-8 pt-4 md:pt-6">
        {/* Publicité page artiste */}
        <AdPageBanner userIsPremium={userIsPremium} />

        {/* Action Buttons */}
        <div className="flex items-center gap-1 md:gap-6 mb-3 md:mb-2 px-2 md:px-0">
          <button
            onClick={() => {
              if (currentTrackInList && currentTrack) {
                onPlayTrack(currentTrack);
                return;
              }
              if (tracks && tracks.length > 0) {
                onPlayTrack(tracks[0]);
              }
            }}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:bg-pink-400 hover:scale-105 transition-all flex items-center justify-center shadow-lg"
            title={currentTrackInList ? 'Lire / Pause' : 'Lire'}
          >
            {isListPlaying ? (
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 md:w-6 md:h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          
          <button 
            onClick={handleSubscribeToggle}
            disabled={isLoading}
            className={`px-4 md:px-8 py-2 md:py-3 rounded-full border transition-all text-xs md:text-sm font-bold tracking-wide ${
              isSubscribed 
                ? 'border-white bg-white text-black hover:bg-white/90' 
                : 'border-white/30 hover:border-white hover:scale-105'
            }`}
          >
            {isLoading ? '...' : isSubscribed ? t('artist.subscribed') : t('artist.subscribe')}
          </button>
          
          <button className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white">
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
          </button>
        </div>

        {/* Tracks List */}
        {tracks && tracks.length > 0 ? (
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Populaires</h2>
            
            <div className="space-y-1">
              {tracks.slice(0, 5).map((track, index) => (
                <div
                  key={track.id}
                  className={`group w-full grid grid-cols-[auto,1fr,auto,auto] md:grid-cols-[auto,1fr,auto,auto] gap-1 md:gap-1 items-center px-2 md:px-4 py-2 md:py-3 rounded hover:bg-white/10 transition-colors ${
                    currentTrack?.id === track.id ? 'bg-white/10' : ''
                  }`}
                >
                  <div className="flex items-center justify-center w-8">
                    {isTrackActuallyPlaying(track.id) ? (
                      <PlayingEqualizer isPlaying={true} />
                    ) : (
                      <>
                        <span className="text-base text-white/70 group-hover:hidden">
                          {index + 1}
                        </span>
                        <button
                          onClick={() => onPlayTrack(track)}
                          className="w-5 h-5 hidden group-hover:flex items-center justify-center text-white"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={() => onPlayTrack(track)}
                    className="flex items-center gap-1 md:gap-1 min-w-0 text-left"
                  >
                    <div className="w-10 h-10 md:w-10 md:h-10 rounded overflow-hidden flex-shrink-0">
                      {track.cover_image ? (
                        <img loading="lazy" decoding="async"
                          src={getImageUrl(track.cover_image)}
                          alt={track.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-base md:text-lg">
                          {track.cover_emoji || '🎵'}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-start min-w-0">
                      <span className={`text-sm md:text-base font-normal truncate flex items-center gap-1 ${currentTrack?.id === track.id ? 'text-pink-400' : 'text-white'}`}>
                        {track.title}
                        {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-4 h-4 inline-flex flex-shrink-0" />}
                      </span>
                    </div>
                  </button>
                  
                  <span className="text-xs md:text-sm text-white/70 tabular-nums">
                    {formatDuration(track.duration)}
                  </span>

                  {/* Menu 3 points - toujours visible sur mobile */}
                  <button
                    onClick={(e) => handleContextMenu(e, track)}
                    className="flex w-8 h-8 items-center justify-center text-white/70 hover:text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="2"/>
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="12" cy="19" r="2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {tracks.length > 5 && (
              <div className="mt-6 md:mt-8">
                <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Discographie</h2>
                <div className="hidden md:grid grid-cols-[auto,1fr,auto,auto,auto] gap-1 px-4 py-2 text-sm text-white/50 border-b border-white/10">
                  <span>#</span>
                  <span>Titre</span>
                  <span>Catégorie</span>
                  <span className="text-right">Durée</span>
                  <span className="w-8"></span>
                </div>
                <div>
                  {tracks.slice(5).map((track, index) => (
                    <div
                      key={track.id}
                      className={`group w-full grid grid-cols-[auto,1fr,auto,auto] md:grid-cols-[auto,1fr,auto,auto,auto] gap-1 md:gap-1 items-center px-2 md:px-4 py-2 text-xs md:text-sm hover:bg-white/10 ${
                        currentTrack?.id === track.id ? 'bg-white/10' : ''
                      }`}
                    >
                      <div className="w-6 flex items-center justify-center">
                        {isTrackActuallyPlaying(track.id) ? (
                      <PlayingEqualizer isPlaying={true} />
                    ) : (
                          <span className="text-white/50">{index + 6}</span>
                        )}
                      </div>
                      <button
                        onClick={() => onPlayTrack(track)}
                        className={`text-left truncate flex items-center gap-1 ${currentTrack?.id === track.id ? 'text-pink-400' : ''}`}
                      >
                        {track.title}
                        {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-4 h-4 inline-flex flex-shrink-0" />}
                      </button>
                      <span className="hidden md:inline text-white/50 text-sm">{track.category_name || '—'}</span>
                      <span className="text-white/50 text-right tabular-nums text-xs md:text-sm">
                        {formatDuration(track.duration)}
                      </span>
                      {/* Menu 3 points - toujours visible sur mobile */}
                      <button
                        onClick={(e) => handleContextMenu(e, track)}
                        className="flex w-8 h-8 items-center justify-center text-white/70 hover:text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="5" r="2"/>
                          <circle cx="12" cy="12" r="2"/>
                          <circle cx="12" cy="19" r="2"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-white/60 text-center py-16">Aucune piste disponible pour cet artiste</p>
        )}
      </div>

      {/* Context Menu */}
      <TrackContextMenu
        track={contextMenu.track}
        isOpen={contextMenu.isOpen}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        position={contextMenu.position}
        playlists={userPlaylists}
        folders={userFolders}
        onAddToPlaylist={onAddToPlaylist}
        onToggleLike={onToggleLike}
        isLiked={contextMenu.track && likedTrackIds?.includes(contextMenu.track.id)}
        onShowToast={onShowToast}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={onOpenSubscription}
      />
    </div>
  );
}

// ---- Playlist View (similar to Artist View) ----
function PlaylistView({ playlist, tracks, onPlayTrack, currentTrack, isPlaying, userPlaylists, userFolders, onAddToPlaylist, likedTrackIds, onToggleLike, onRemoveFromPlaylist, onPlaylistUpdated, onShowToast, userIsPremium = false, user, onOpenSubscription }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [contextMenu, setContextMenu] = useState({ isOpen: false, track: null, position: { x: 0, y: 0 } });
  const currentTrackInList = !!(currentTrack && tracks?.some((t) => t.id === currentTrack.id));
  const isListPlaying = currentTrackInList && !!isPlaying;

  const isTrackActuallyPlaying = useCallback(
    (trackId) => currentTrack?.id === trackId && !!isPlaying,
    [currentTrack?.id, isPlaying]
  );
  
  if (!playlist) return null;

  const handleContextMenu = (e, track) => {
    e.preventDefault();
    e.stopPropagation();
    
    const buttonRect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      track,
      position: { 
        x: buttonRect.left - 210,
        y: buttonRect.top 
      }
    });
  };

  const totalDuration = tracks ? tracks.reduce((acc, t) => acc + (t.duration || 0), 0) : 0;
  const minutes = Math.round(totalDuration / 60);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900 via-purple-800 to-black"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
        
        <div className="relative h-full flex items-end p-4 md:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-1 md:gap-6 w-full">
            {/* Playlist Cover */}
            <div className="w-32 h-32 md:w-52 md:h-52 rounded shadow-2xl flex items-center justify-center text-4xl md:text-7xl flex-shrink-0 bg-gradient-to-br from-purple-600 to-blue-600">
              {playlist.cover_emoji || '🎵'}
            </div>
            
            {/* Playlist Info */}
            <div className="flex flex-col justify-end pb-1 md:pb-2">
              <span className="text-xs md:text-sm font-bold uppercase tracking-wide mb-1 md:mb-2">Playlist</span>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-black tracking-tight mb-2 md:mb-2 leading-none">
                {playlist.name || 'Playlist'}
              </h1>
              <p className="text-sm md:text-base text-white/90 font-medium">
                {playlist.description || ''}
              </p>
              <p className="text-xs md:text-sm text-white/70 mt-1 md:mt-2">
                {tracks ? tracks.length.toLocaleString() : 0} titres • {minutes} min
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - pleine largeur sur mobile */}
      <div className="bg-gradient-to-b from-black/40 to-black px-2 md:px-6 lg:px-8 pt-4 md:pt-6">
        {/* Publicité page artiste */}
        <AdPageBanner userIsPremium={userIsPremium} />

        {/* Action Buttons */}
        <div className="flex items-center gap-1 md:gap-6 mb-3 md:mb-2 px-2 md:px-0">
          <button
            onClick={() => {
              if (currentTrackInList && currentTrack) {
                onPlayTrack(currentTrack);
                return;
              }
              if (tracks && tracks.length > 0) {
                onPlayTrack(tracks[0]);
              }
            }}
            className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:bg-pink-400 hover:scale-105 transition-all flex items-center justify-center shadow-lg"
            title={currentTrackInList ? 'Lire / Pause' : 'Lire'}
          >
            {isListPlaying ? (
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 md:w-6 md:h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>

        {/* Tracks List */}
        {tracks && tracks.length > 0 ? (
          <div>
            <div className="hidden md:grid grid-cols-[auto,1fr,auto,auto,auto] gap-1 px-4 py-2 text-sm text-white/50 border-b border-white/10 mb-2">
              <span>#</span>
              <span>Titre</span>
              <span>Artiste</span>
              <span className="text-right">Durée</span>
              <span className="w-8"></span>
            </div>
            <div>
              {tracks.map((track, index) => (
                <div
                  key={track.id}
                  className={`group w-full grid grid-cols-[auto,1fr,auto,auto] md:grid-cols-[auto,1fr,auto,auto,auto] gap-1 md:gap-1 items-center px-2 md:px-4 py-2 text-xs md:text-sm hover:bg-white/10 rounded ${
                    currentTrack?.id === track.id ? 'bg-white/10' : ''
                  }`}
                >
                  <div className="w-6 flex items-center justify-center">
                    {isTrackActuallyPlaying(track.id) ? (
                      <PlayingEqualizer isPlaying={true} />
                    ) : (
                      <span className="text-white/50">{index + 1}</span>
                    )}
                  </div>
                  <button
                    onClick={() => onPlayTrack(track)}
                    className={`text-left truncate flex items-center gap-1 ${currentTrack?.id === track.id ? 'text-pink-400' : ''}`}
                  >
                    {track.title}
                    {!!track.is_premium && !userIsPremium && <PremiumCrown className="w-4 h-4 inline-flex flex-shrink-0" />}
                  </button>
                  <span className="hidden md:inline text-white/50 text-sm truncate">{track.artist || '—'}</span>
                  <span className="text-white/50 text-right tabular-nums text-xs md:text-sm">
                    {formatDuration(track.duration)}
                  </span>
                  {/* Menu 3 points - toujours visible sur mobile */}
                  <button
                    onClick={(e) => handleContextMenu(e, track)}
                    className="flex w-8 h-8 items-center justify-center text-white/70 hover:text-white md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="2"/>
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="12" cy="19" r="2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-white/60 text-center py-16">Aucune piste dans cette playlist</p>
        )}
      </div>

      {/* Context Menu */}
      <TrackContextMenu
        track={contextMenu.track}
        isOpen={contextMenu.isOpen}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        position={contextMenu.position}
        playlists={userPlaylists}
        folders={userFolders}
        onAddToPlaylist={onAddToPlaylist}
        onToggleLike={onToggleLike}
        isLiked={contextMenu.track && likedTrackIds?.includes(contextMenu.track.id)}
        currentPlaylistId={playlist?.id}
        onRemoveFromPlaylist={onRemoveFromPlaylist}
        onShowToast={onShowToast}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={onOpenSubscription}
      />
    </div>
  );
}

// ---- Collection / playlist page ----
function CollectionView({ collection, onPlayTrack, onTogglePlayCollection, isPlayingCollection, user, currentTrack }) {
  if (!collection) return null;
  const totalDuration = collection.tracks ? collection.tracks.reduce((acc, t) => acc + t.duration, 0) : 0;
  const minutes = Math.round(totalDuration / 60);
  const userName = user?.name || "Utilisateur";

  return (
    <div className="flex flex-col gap-1 md:gap-5">
      <div className="flex flex-col md:flex-row items-start md:items-end gap-1 md:gap-1 pt-3 md:pt-4">
        <div
          className={`w-32 h-32 md:w-32 lg:w-40 md:h-32 lg:h-40 rounded bg-gradient-to-br ${collection.color} shadow-2xl flex items-center justify-center text-5xl flex-shrink-0`}
        >
          {collection.coverEmoji}
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="text-xs uppercase text-white/60">
            {collection.type === "artist" ? "Playlist publique" : "Playlist publique"}
          </span>
          <h1 className="text-base sm:text-3xl md:text-4xl lg:text-5xl font-bold md:font-black leading-tight md:tracking-tight">{collection.title}</h1>
          <p className="text-xs md:text-sm text-white/70">{collection.subtitle}</p>
          <p className="text-xs text-white/60 mt-1">
            {collection.tracks ? collection.tracks.length : 0} titres • {minutes} min
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 md:gap-1 mb-1">
        <button
          onClick={onTogglePlayCollection}
          className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-black text-xl md:text-2xl flex items-center justify-center hover:scale-105"
        >
          {isPlayingCollection ? "⏸" : "▶"}
        </button>
        <button className="rounded-full border border-white/30 text-xs px-3 py-1 hidden md:inline-block">Suivre</button>
        <span className="text-lg md:text-xl text-white/60">•••</span>
      </div>

      {collection.tracks && collection.tracks.length > 0 ? (
        <div className="rounded-lg bg-white/5 overflow-hidden border border-white/10">
          <div className="hidden md:grid grid-cols-[auto,1fr,auto] gap-1 px-4 py-1.5 text-[11px] text-white/50 border-b border-white/10 uppercase tracking-wide">
            <span>#</span>
            <span>Titre</span>
            <span className="text-right">Durée</span>
          </div>
          <div>
            {collection.tracks.map((track, index) => (
              <button
                key={track.id}
                onClick={() => onPlayTrack(track, collection)}
                className={`w-full grid grid-cols-[auto,1fr,auto] gap-1 md:gap-1 items-center px-2 md:px-4 py-2 md:py-1.5 text-xs md:text-sm hover:bg-white/10 text-left ${
                  currentTrack?.id === track.id ? 'bg-white/5' : ''
                }`}
              >
                <span className="text-[10px] md:text-[11px] text-white/60">{index + 1}</span>
                <div className="flex flex-col leading-tight min-w-0">
                  <span className={`text-xs md:text-sm truncate ${currentTrack?.id === track.id ? 'text-emerald-500' : ''}`}>{track.title}</span>
                  <span className="text-[10px] md:text-[11px] text-white/60 truncate">{track.artist}</span>
                </div>
                <span className="text-[10px] md:text-[11px] text-white/60 text-right tabular-nums">
                  {formatDuration(track.duration)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-white/60 text-center py-8">Aucune piste dans cette collection</p>
      )}
    </div>
  );
}

// ---- Search view ----
function SearchView({ query, allTracks, collections, onPlayTrackFromSearch, onOpenCollection, currentTrack, moodCategories = [], onSelectMoodCategory }) {
  const normalizeText = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const lower = normalizeText(query);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const matchedTracks = allTracks.filter(
    (t) =>
      normalizeText(t.title).includes(lower) ||
      normalizeText(t.artist).includes(lower)
  );

  const matchedCollections = collections.filter((c) =>
    normalizeText(c.title).includes(lower)
  );

  // Recherche dans les mood categories
  const matchedMoods = moodCategories.filter((m) =>
    normalizeText(m.name).includes(lower)
  );

  const bestTrack = matchedTracks[0];
  const bestMood = matchedMoods[0];

  // Déterminer le meilleur résultat global (track ou mood)
  const hasBestTrack = bestTrack && normalizeText(bestTrack.title).startsWith(lower);
  const hasBestMood = bestMood && normalizeText(bestMood.name).startsWith(lower);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-1 text-xs">
        {["Tout", "Titres", "Moods", "Playlists", "Artistes"].map(
          (tab, idx) => (
            <button
              key={tab}
              className={`px-3 py-1 rounded-full border text-xs ${
                idx === 0
                  ? "bg-white text-black border-transparent"
                  : "border-white/20 text-white/80"
              }`}
            >
              {tab}
            </button>
          )
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6 items-start">
        {/* Best result */}
        <div className="space-y-3">
          <h2 className="text-sm font-bold">Meilleur résultat</h2>
          {bestTrack ? (
            <div
              className="bg-white/5 rounded-xl p-4 flex gap-1 items-center hover:bg-white/10 cursor-pointer"
              onClick={() => onPlayTrackFromSearch(bestTrack)}
            >
              {bestTrack.cover_image ? (
                <img 
                  src={bestTrack.cover_image} 
                  alt={bestTrack.title}
                  className="w-20 h-20 rounded object-cover"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-3xl"
                >
                  {bestTrack.cover_emoji || '🎵'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{bestTrack.title}</div>
                <div className="text-xs text-white/60 truncate">
                  Titre • {bestTrack.artist}
                </div>
              </div>
            </div>
          ) : bestMood ? (
            <div
              className="bg-white/5 rounded-xl p-4 flex gap-1 items-center hover:bg-white/10 cursor-pointer"
              onClick={() => onSelectMoodCategory && onSelectMoodCategory(bestMood)}
            >
              {bestMood.cover_image ? (
                <img 
                  src={bestMood.cover_image} 
                  alt={bestMood.name}
                  className="w-20 h-20 rounded object-cover"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center text-3xl"
                >
                  
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{bestMood.name}</div>
                <div className="text-xs text-white/60 truncate">
                  Mood • {bestMood.mood_count || 0} pistes
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-white/60">Aucun résultat.</p>
          )}

          {/* Section Moods dans les résultats */}
          {matchedMoods.length > 0 && (
            <div className="mt-5">
              <h2 className="text-sm font-bold mb-2"> Moods</h2>
              <div className="flex flex-col gap-1">
                {matchedMoods.slice(0, 4).map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => onSelectMoodCategory && onSelectMoodCategory(mood)}
                    className="flex items-center gap-1 text-left text-sm hover:bg-white/10 rounded px-2 py-1.5"
                  >
                    {mood.cover_image ? (
                      <img 
                        src={mood.cover_image} 
                        alt={mood.name}
                        className="w-10 h-10 rounded object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center text-xl">
                        
                      </div>
                    )}
                    <div>
                      <div className="text-sm">{mood.name}</div>
                      <div className="text-xs text-white/60">{mood.mood_count || 0} pistes</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Section Playlists */}
          {matchedCollections.length > 0 && (
            <div className="mt-5">
              <h2 className="text-sm font-bold mb-2">Playlists</h2>
              <div className="flex flex-col gap-1">
                {matchedCollections.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => onOpenCollection(c)}
                    className="flex items-center gap-1 text-left text-sm hover:bg-white/10 rounded px-2 py-1.5"
                  >
                    {c.cover_image ? (
                      <img 
                        src={c.cover_image} 
                        alt={c.title}
                        className="w-10 h-10 rounded object-cover"
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded bg-gradient-to-br ${c.color || 'from-gray-600 to-gray-800'} flex items-center justify-center text-xl`}
                      >
                        {c.coverEmoji || '🎵'}
                      </div>
                    )}
                    <div>
                      <div className="text-sm">{c.title}</div>
                      <div className="text-xs text-white/60 capitalize">{c.type}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tracks column */}
        <div>
          <h2 className="text-sm font-bold mb-2">Titres</h2>
          <div className="flex flex-col divide-y divide-white/10 text-sm">
            {matchedTracks.slice(0, 10).map((t) => (
              <button
                key={t.id}
                onClick={() => onPlayTrackFromSearch(t)}
                className={`flex items-center gap-1 py-2 hover:bg-white/10 px-2 text-left ${
                  currentTrack?.id === t.id ? 'bg-white/5' : ''
                }`}
              >
                {t.cover_image ? (
                  <img 
                    src={t.cover_image} 
                    alt={t.title}
                    className="w-10 h-10 rounded object-cover"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xl"
                  >
                    {t.cover_emoji || '🎵'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className={`truncate ${currentTrack?.id === t.id ? 'text-emerald-500' : ''}`}>{t.title}</div>
                  <div className="text-xs text-white/60 truncate">
                    {t.artist}
                  </div>
                </div>
                <span className="text-xs text-white/60 tabular-nums">
                  {formatDuration(t.duration)}
                </span>
              </button>
            ))}
            {matchedTracks.length === 0 && (
              <p className="text-xs text-white/60 py-4">Aucun titre trouvé</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Right contextual panel ----
function RightPanel({ currentTrack, currentArtistData, isPlaying, user, userIsPremium = false, onOpenArtist }) {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const artistName = currentArtistData?.name || currentTrack?.artist;

  // Vérifier si l'utilisateur est abonné à cet artiste
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || !artistName) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/artist-subscriptions/check/${encodeURIComponent(artistName)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setIsSubscribed(data.isSubscribed);
      } catch (error) {
        console.error('Erreur vérification abonnement:', error);
      }
    };

    checkSubscription();
  }, [artistName, user, BACKEND_URL]);

  const handleSubscribeToggle = async () => {
    if (!user || isLoading || !artistName) return;
    
    setIsLoading(true);
    try {
      const url = isSubscribed 
        ? `${BACKEND_URL}/api/artist-subscriptions/unsubscribe`
        : `${BACKEND_URL}/api/artist-subscriptions/subscribe`;
      
      // Utilise toujours POST pour compatibilité OVH mutualisé
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ artistName })
      });

      if (response.ok) {
        setIsSubscribed(!isSubscribed);
      }
    } catch (error) {
      console.error('Erreur abonnement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentTrack) return null;

  return (
    <aside className="w-80 xl:w-96 bg-black border-l border-white/10 flex flex-col h-full">
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="font-semibold text-sm">{currentTrack.title}</h3>
          <p className="text-xs text-white/60">{currentTrack.artist}</p>
        </div>

        {/* Track Cover Image */}
        <div className="p-6">
          {currentTrack.cover_image ? (
            <img loading="lazy" decoding="async"
              src={getImageUrl(currentTrack.cover_image)}
              alt={currentTrack.title}
              className="w-full aspect-square object-cover rounded-lg shadow-2xl"
            />
          ) : (
            <div className="w-full aspect-square bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg shadow-2xl flex items-center justify-center text-8xl">
              {currentTrack.cover_emoji || '🎵'}
            </div>
          )}
        </div>

        {/* Track Info */}
        <div className="px-6 pb-4">
          <h2 className="text-2xl font-bold mb-1">{currentTrack.title}</h2>
          <p className="text-white/70 mb-2">{currentTrack.artist}</p>
          
          {/* Catégories multiples */}
          {(currentTrack.categories && currentTrack.categories.length > 0) ? (
            <div className="flex flex-wrap items-center gap-1 mb-2">
              {currentTrack.categories.map(cat => (
                <span key={cat.id} className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/80">
                  {cat.name}
                </span>
              ))}
            </div>
          ) : currentTrack.category_name && (
            <div className="flex items-center gap-1 text-sm text-white/60 mb-2">
              <span className="px-3 py-1 bg-white/10 rounded-full text-xs">
                {currentTrack.category_name}
              </span>
            </div>
          )}
        </div>

        {/* Artist Section */}
        <div className="px-6 pb-6">
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-lg p-4 border border-white/10">
            <div className="flex items-center gap-1 mb-3">
              {currentArtistData?.profile_image ? (
                <img loading="lazy" decoding="async"
                  src={getImageUrl(currentArtistData.profile_image)}
                  alt={currentArtistData.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-2xl">
                  {currentTrack.cover_emoji || '🎤'}
                </div>
              )}
              <div>
                <p className="text-xs text-white/60 uppercase tracking-wide mb-1">Artiste</p>
                <button
                  onClick={() => {
                    // Créer un objet track avec artist_id pour ouvrir la page artiste
                    const trackForArtist = {
                      ...currentTrack,
                      artist: currentArtistData?.name || currentTrack.artist
                    };
                    // Trigger l'ouverture via onOpenArtist passé au composant
                    if (window.openArtistPageGlobal) {
                      window.openArtistPageGlobal(trackForArtist);
                    }
                  }}
                  className="font-bold text-lg hover:underline text-left"
                >
                  {currentArtistData?.name || currentTrack.artist || 'Artiste inconnu'}
                </button>
              </div>
            </div>
            
            <button 
              onClick={handleSubscribeToggle}
              disabled={isLoading}
              className={`w-full px-4 py-2 rounded-full border transition-all text-sm font-semibold ${
                isSubscribed 
                  ? 'border-white bg-white text-black hover:bg-white/90' 
                  : 'border-white/30 hover:border-white hover:scale-105'
              }`}
            >
              {isLoading ? '...' : isSubscribed ? t('artist.subscribed') : t('artist.subscribe')}
            </button>
            
            <p className="text-xs text-white/60 mt-3 text-center">
              {t('artist.followDescription', { artist: currentArtistData?.name || currentTrack.artist || t('artist.thisArtist') })}
            </p>
          </div>
        </div>

        {/* About Track */}
        <div className="px-6 pb-6">
          <h3 className="text-sm font-bold mb-3">À propos</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-white/5">
              <span className="text-white/60">Durée</span>
              <span>{Math.floor(currentTrack.duration / 60)}:{(currentTrack.duration % 60).toString().padStart(2, '0')}</span>
            </div>
            {/* Catégories multiples dans À propos */}
            {(currentTrack.categories && currentTrack.categories.length > 0) ? (
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-white/60">Genre{currentTrack.categories.length > 1 ? 's' : ''}</span>
                <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                  {currentTrack.categories.map(cat => (
                    <span key={cat.id} className="text-xs px-2 py-0.5 bg-white/10 rounded">
                      {cat.name}
                    </span>
                  ))}
                </div>
              </div>
            ) : currentTrack.category_name && (
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-white/60">Genre</span>
                <span>{currentTrack.category_name}</span>
              </div>
            )}
            <div className="flex justify-between py-2">
              <span className="text-white/60">Statut</span>
              <span className={isPlaying ? 'text-pink-400' : 'text-white/60'}>
                {isPlaying ? '▶ En lecture' : '⏸ En pause'}
              </span>
            </div>
          </div>
        </div>

        {/* Publicité sidebar droite - sous les infos artiste */}
        <div className="px-6 pb-6">
          <AdSidebarRight userIsPremium={userIsPremium} />
        </div>

        {/* Espace en bas pour le scroll */}
        <div className="h-20 flex-shrink-0"></div>
      </div>
    </aside>
  );
}

// ---- Player bar ----
// Modale plein écran pour le lecteur mobile
function FullscreenPlayerModal({
  isOpen,
  onClose,
  currentTrack,
  isPlaying,
  progress,
  onTogglePlay,
  onNext,
  onPrev,
  onSeek,
  isShuffleActive,
  onToggleShuffle,
  isRepeatActive,
  onToggleRepeat,
  onOpenArtist,
  userPlaylists,
  userFolders,
  onAddToPlaylist,
  onToggleLike,
  likedTrackIds,
  onShowToast,
  userIsPremium,
  user,
  onOpenSubscription
}) {
  const seekGestureRef = useRef(false);
  const [showTrackMenu, setShowTrackMenu] = useState(false);
  if (!isOpen || !currentTrack) return null;

  const currentTime = formatDuration(Math.round((progress / 100) * currentTrack.duration));
  const totalTime = formatDuration(currentTrack.duration);
  const clampPercent = (value) => Math.min(100, Math.max(0, value));
  const getSeekPercentFromEvent = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width) return null;
    const clientX = e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX ?? e.clientX ?? e.nativeEvent?.clientX;
    if (typeof clientX !== 'number') return null;
    return clampPercent(((clientX - rect.left) / rect.width) * 100);
  };
  const handleSeekFromPointer = (e) => {
    const percent = getSeekPercentFromEvent(e);
    if (percent === null || !Number.isFinite(percent)) return;
    onSeek(percent);
  };

  // Handler pour ouvrir la page de l'artiste ou du mood
  const handleArtistClick = () => {
    if (onOpenArtist && currentTrack) {
      onClose(); // Fermer la modale d'abord
      onOpenArtist(currentTrack); // Ouvre artiste ou mood selon le contenu
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[10000] bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f23] flex flex-col animate-slide-up"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Header avec bouton fermer */}
      <div
        className="flex items-center justify-between px-4 pt-2 pb-4"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      >
        <button 
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className="flex-1 text-center">
          <p className="text-xs text-white/60 uppercase tracking-wider">Lecture en cours</p>
        </div>
        <button
          onClick={() => setShowTrackMenu(true)}
          className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white"
          aria-label="Options"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
        </button>
      </div>

      {/* Pochette + titre (cliquable) */}
      <div className="flex-1 flex items-center justify-center px-8 py-4">
        <button
          onClick={handleArtistClick}
          className="w-full max-w-[320px] text-left"
        >
          <div className="w-full aspect-square rounded-lg overflow-hidden shadow-2xl">
            {currentTrack.cover_image ? (
              <img 
                src={getImageUrl(currentTrack.cover_image)} 
                alt={currentTrack.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-8xl">
                {currentTrack.cover_emoji || '🎵'}
              </div>
            )}
          </div>
          <div className="mt-4">
            <h2 className="text-2xl font-bold text-white truncate">{currentTrack.title}</h2>
            <p className="text-base text-white/60 truncate">
              {currentTrack.is_mood
                ? (currentTrack.category_name || currentTrack.mood_category_name || currentTrack.artist_name || currentTrack.artist || 'Mood')
                : (currentTrack.artist_name || currentTrack.artist)}
            </p>
          </div>
        </button>
      </div>

      {/* Barre de progression */}
      <div className="px-8 mb-3">
        <div 
          className="w-full h-1 bg-white/20 rounded-full cursor-pointer relative"
          onPointerDown={(e) => {
            seekGestureRef.current = true;
            if (e.currentTarget.setPointerCapture) {
              e.currentTarget.setPointerCapture(e.pointerId);
            }
            handleSeekFromPointer(e);
          }}
          onPointerMove={(e) => {
            if (!seekGestureRef.current) return;
            handleSeekFromPointer(e);
          }}
          onPointerUp={(e) => {
            if (!seekGestureRef.current) return;
            handleSeekFromPointer(e);
            seekGestureRef.current = false;
            if (e.currentTarget.releasePointerCapture) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
          }}
          onPointerCancel={(e) => {
            seekGestureRef.current = false;
            if (e.currentTarget.releasePointerCapture) {
              e.currentTarget.releasePointerCapture(e.pointerId);
            }
          }}
        >
          <div 
            className="h-full bg-white rounded-full relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg" />
          </div>
        </div>
        <div className="flex justify-between mt-2 text-sm text-white/60">
          <span className="tabular-nums">{currentTime}</span>
          <span className="tabular-nums">{totalTime}</span>
        </div>
      </div>

      {/* Contrôles de lecture */}
      <div className="px-8 mb-2">
        <div className="flex items-center justify-between">
          {/* Shuffle */}
          <button 
            onClick={onToggleShuffle}
            className={`p-3 ${isShuffleActive ? 'text-pink-400' : 'text-white/60'}`}
          >
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>
            </svg>
          </button>

          {/* Précédent */}
          <button onClick={onPrev} className="p-3 text-white">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
            </svg>
          </button>

          {/* Play/Pause - Grand bouton central */}
          <button
            onClick={onTogglePlay}
            className="w-[60px] h-[60px] md:w-16 md:h-16 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 active:scale-95 transition-transform shadow-lg"
          >
            {isPlaying ? (
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              <svg className="w-6 h-6 md:w-7 md:h-7 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          {/* Suivant */}
          <button onClick={onNext} className="p-3 text-white">
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
            </svg>
          </button>

          {/* Repeat */}
          <button 
            onClick={onToggleRepeat}
            className={`p-3 ${isRepeatActive ? 'text-pink-400' : 'text-white/60'}`}
          >
            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Espace supplémentaire pour le safe area */}
      <div className="h-8" />

      <TrackContextMenu
        track={currentTrack}
        isOpen={showTrackMenu}
        onClose={() => setShowTrackMenu(false)}
        position={{ x: 0, y: 0 }}
        playlists={userPlaylists}
        folders={userFolders}
        onAddToPlaylist={onAddToPlaylist}
        onToggleLike={onToggleLike}
        isLiked={!!(currentTrack && likedTrackIds?.includes(currentTrack.id))}
        onShowToast={onShowToast}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={onOpenSubscription}
      />
    </div>
  );
}

function PlayerBar({ 
  currentTrack, 
  isPlaying, 
  progress, 
  onTogglePlay, 
  onNext, 
  onPrev, 
  onSeek,
  isShuffleActive,
  onToggleShuffle,
  isRepeatActive,
  onToggleRepeat,
  hasBottomNav = true,
  trackSource,
  onNavigateToSource,
  onOpenArtist,
  userPlaylists,
  userFolders,
  onAddToPlaylist,
  onToggleLike,
  likedTrackIds,
  onShowToast,
  userIsPremium,
  user,
  onOpenSubscription,
  volume,
  onVolumeChange
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(volume || 70);
  const isNativePlatform = Capacitor.isNativePlatform();

  // Toggle mute
  const handleToggleMute = () => {
    if (isMuted) {
      onVolumeChange(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      onVolumeChange(0);
      setIsMuted(true);
    }
  };

  // Get volume icon based on level
  const getVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
        </svg>
      );
    } else if (volume < 30) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7 9v6h4l5 5V4l-5 5H7z"/>
        </svg>
      );
    } else if (volume < 70) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
        </svg>
      );
    } else {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
      );
    }
  };
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Sur mobile avec nav: bottom 64px + safe area, sinon bottom 0
  const bottomValue = (isMobile && hasBottomNav)
    ? 'calc(64px + env(safe-area-inset-bottom, 0px))'
    : '0px';
  const mobileBarHeight = isNativePlatform ? 52 : 56;
  const mobileBarHeightCss = isNativePlatform
    ? `${mobileBarHeight}px`
    : `calc(${mobileBarHeight}px + env(safe-area-inset-bottom, 0px))`;
  const mobileBarPadding = isNativePlatform ? '0px' : 'env(safe-area-inset-bottom, 0px)';

  // Handler pour ouvrir la modale (mobile) ou naviguer vers la source (desktop)
  const handleTrackInfoClick = () => {
    if (isMobile && currentTrack) {
      console.log('[NAV][PLAYER] click track info', {
        currentTrack,
        trackSource
      });
      setShowFullscreen(true);
    } else if (!isMobile && onNavigateToSource && trackSource) {
      // Sur desktop, naviguer vers la source de lecture
      onNavigateToSource();
    }
  };
  
  return (
    <>
      {/* Modale plein écran mobile */}
      <FullscreenPlayerModal
        isOpen={showFullscreen}
        onClose={() => setShowFullscreen(false)}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        progress={progress}
        onTogglePlay={onTogglePlay}
        onNext={onNext}
        onPrev={onPrev}
        onSeek={onSeek}
        isShuffleActive={isShuffleActive}
        onToggleShuffle={onToggleShuffle}
        isRepeatActive={isRepeatActive}
        onToggleRepeat={onToggleRepeat}
        onOpenArtist={onOpenArtist}
        userPlaylists={userPlaylists}
        userFolders={userFolders}
        onAddToPlaylist={onAddToPlaylist}
        onToggleLike={onToggleLike}
        likedTrackIds={likedTrackIds}
        onShowToast={onShowToast}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={onOpenSubscription}
      />

      <footer 
        className="mobile-player-bar fixed left-0 right-0 bg-[#0b0b0b] border-t border-white/10 px-3 lg:px-6 flex items-center gap-1 lg:gap-1 text-xs relative lg:pt-1 lg:pb-2"
        style={{ 
    
          bottom: bottomValue,
          height: mobileBarHeightCss,
          paddingBottom: mobileBarPadding,
          backgroundColor: isNativePlatform ? '#0b0b0b' : undefined,
          zIndex: 9999
          
        }}
      >
        {/* Mobile progress bar (always visible) */}
        <div
          className="lg:hidden absolute left-0 right-0 top-0 h-1 cursor-pointer"
          onPointerDown={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = ((e.clientX - rect.left) / rect.width) * 100;
            onSeek(percent);
          }}
        >
          <div className="w-full h-full bg-white/15">
            <div
              className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-fuchsia-400"
              style={{ width: `${currentTrack ? progress : 0}%` }}
            />
          </div>
        </div>
        {/* Zone cliquable - mobile: ouvre modale, desktop: navigue vers source */}
        <div 
          data-testid="player-track-info"
          className={`flex items-center gap-1 w-1/3 min-w-0 p-1 -m-1 rounded-lg transition-all ${
            isMobile 
              ? 'cursor-pointer active:opacity-80' 
              : 'cursor-pointer hover:bg-white/10 active:scale-[0.98]'
          }`}
          onClick={handleTrackInfoClick}
          title={!isMobile && trackSource ? `Aller à: ${
            trackSource.type === 'liked' ? 'Titres likés' 
            : trackSource.type === 'artist' ? trackSource.artist?.name 
            : trackSource.type === 'playlist' || trackSource.type === 'admin-playlist' ? trackSource.name 
            : currentTrack?.artist || 'Page artiste'
          }` : 'Cliquer pour voir la source'}
        >
          <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center text-lg overflow-hidden">
            {currentTrack?.cover_image ? (
              <img loading="lazy" decoding="async" 
                src={getImageUrl(currentTrack.cover_image)} 
                alt={currentTrack.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{currentTrack?.cover_emoji || "♪"}</span>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm truncate">
              {currentTrack ? currentTrack.title : "Rien en lecture"}
            </span>
            <span className="text-[11px] text-white/60 truncate">
              {currentTrack ? currentTrack.artist : "Choisis un titre pour commencer"}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-1 flex-1 max-w-xl" >
          <div className="flex items-center gap-1 text-lg">
            {/* Bouton Shuffle */}
            <button 
              onClick={onToggleShuffle} 
              className={`hidden md:block transition-colors ${isShuffleActive ? 'text-pink-400' : 'text-white/70 hover:text-white'}`}
              title="Lecture aléatoire"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>
              </svg>
            </button>

            {/* Bouton Précédent */}
            <button onClick={onPrev} className="text-white/70 hover:text-white transition-colors" title="Précédent">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
              </svg>
            </button>

            {/* Bouton Play/Pause */}
            <button
              onClick={onTogglePlay}
              className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 active:scale-95"
            >
              {isPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            {/* Bouton Suivant */}
            <button onClick={onNext} className="text-white/70 hover:text-white transition-colors" title="Suivant">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
              </svg>
            </button>

            {/* Bouton Repeat */}
            <button 
              onClick={onToggleRepeat} 
              className={`hidden md:block transition-colors ${isRepeatActive ? 'text-pink-400' : 'text-white/70 hover:text-white'}`}
              title="Répéter"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
              </svg>
            </button>
          </div>
          <div className="hidden lg:flex items-center gap-2 w-full text-[11px] text-white/80 -mt-1" style={{marginBottom: 10}}>
            <span className="tabular-nums">
              {currentTrack
                ? formatDuration(Math.round((progress / 100) * currentTrack.duration))
                : "0:00"}
            </span>
            <div 
              className="flex-1 cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const percent = ((e.clientX - rect.left) / rect.width) * 100;
                onSeek(percent);
              }}
            >
              <div className="w-full h-2 bg-white/15 rounded-full overflow-hidden border border-white/15">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-fuchsia-400"
                  style={{ width: `${currentTrack ? progress : 0}%` }}
                />
              </div>
            </div>
            <span className="tabular-nums">
              {currentTrack ? formatDuration(currentTrack.duration) : "0:00"}
            </span>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-1 w-1/3 justify-end text-white/70">
          <button 
            onClick={handleToggleMute}
            className="hover:text-white transition-colors"
            title={isMuted ? "Activer le son" : "Couper le son"}
          >
            {getVolumeIcon()}
          </button>
          <div className="w-24 group relative">
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                const newVolume = parseInt(e.target.value);
                onVolumeChange(newVolume);
                if (newVolume > 0) setIsMuted(false);
              }}
              className="w-full h-1 bg-white/30 rounded-full appearance-none cursor-pointer accent-white hover:accent-green-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:opacity-0 [&::-webkit-slider-thumb]:group-hover:opacity-100 [&::-webkit-slider-thumb]:transition-opacity"
              style={{
                background: `linear-gradient(to right, ${isMuted ? '#ffffff50' : '#ffffff'} 0%, ${isMuted ? '#ffffff50' : '#ffffff'} ${isMuted ? 0 : volume}%, #ffffff30 ${isMuted ? 0 : volume}%, #ffffff30 100%)`
              }}
            />
          </div>
        </div>
      </footer>
    </>
  );
}

// ---- Root app ----
export default function SpotifyClone({ initialStoryId }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAdmin, logout, updateUser, loading: authLoading, token } = useAuth();
  const [view, setView] = useState("home"); // home | collection | search | artist | playlist | profile | personal-info | address | subscription | cancel-account
  const [showAccountPanel, setShowAccountPanel] = useState(false);
  const [showLanguageSwitcher, setShowLanguageSwitcher] = useState(false);
  const [showPersonalInfoSheet, setShowPersonalInfoSheet] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [blockedTrack, setBlockedTrack] = useState(null);
  const [isAdBlockerActive, setIsAdBlockerActive] = useState(false);
  const [showAdBlockModal, setShowAdBlockModal] = useState(false);
  
  // États pour la version mobile
  const [mobileTab, setMobileTab] = useState("home"); // home | library | account
  const [showMobileLibrary, setShowMobileLibrary] = useState(false);
  
  // États pour l'abonnement et les notifications
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showBanner, setShowBanner] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const adBlockCheckInFlightRef = useRef(false);
  const adBlockLastCheckRef = useRef({ ts: 0, blocked: false });
  const adBlockIntervalRef = useRef(null);
  const openAuthModal = () => {
    setShowAuthModal(true);
  };

  // --- Helper: déterminer le statut premium ---
  // Important : certains backends marquent un abonnement "actif" même pour un compte gratuit.
  // Pour l'affichage des publicités, on considère PREMIUM uniquement si :
  // - l'utilisateur est ADMIN, ou
  // - un flag premium explicite est présent, ou
  // - le type d'abonnement est "premium".
  const computeUserIsPremium = useCallback((u, sub) => {
    if (!u) return false;
    if (u?.status === 'ADMIN') return true;
    if (u?.is_premium === true) return true;
    if (String(u?.subscription_type || '').toLowerCase() === 'premium') return true;
    if (sub?.is_premium === true) return true;
    if (String(sub?.subscription_type || '').toLowerCase() === 'premium') return true;
    return false;
  }, []);
  
  const [allTracks, setAllTracks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [currentArtist, setCurrentArtist] = useState(null);
  const [artistTracks, setArtistTracks] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentArtistData, setCurrentArtistData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // État pour la source de lecture (pour navigation depuis le player)
  // { type: 'playlist'|'artist'|'liked'|'home'|'admin-playlist', id?: number, name?: string, artist?: object }
  const [trackSource, setTrackSource] = useState(null);
  
  // États pour les contrôles du lecteur
  const [isShuffleActive, setIsShuffleActive] = useState(false);
  const [isRepeatActive, setIsRepeatActive] = useState(false);
  const [openStoryIndex, setOpenStoryIndex] = useState(null);
  const isStoryViewerOpen = openStoryIndex !== null;
  const [storyPauseRequested, setStoryPauseRequested] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth < 1024);
  const isNativePlatform = Capacitor.isNativePlatform();
  const nativePlatform = Capacitor.getPlatform ? Capacitor.getPlatform() : 'web';
  const isIOSNative = isNativePlatform && nativePlatform === 'ios';
  const [nativePlayerFailed, setNativePlayerFailed] = useState(false);
  const nativePluginAvailable = isNativePlatform && (
    Capacitor.isPluginAvailable('NativeAudioPlayer') ||
    (NativeAudioPlayer && typeof NativeAudioPlayer.setQueue === 'function')
  );
  const canUseNativePlayer = isNativePlatform && nativePluginAvailable && (!nativePlayerFailed || isIOSNative);
  const [nativeQueue, setNativeQueue] = useState([]);
  const nativeQueueRef = useRef([]);
  const [nativeDurationMs, setNativeDurationMs] = useState(0);
  const [shuffledPlaylist, setShuffledPlaylist] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const isRepeatActiveRef = useRef(false);
  
  // États pour la lecture par catégories (home/recherche)
  const [activeCategoryIds, setActiveCategoryIds] = useState([]);
  const [playedTrackIdsInCategory, setPlayedTrackIdsInCategory] = useState([]);
  const [likedTrackIds, setLikedTrackIds] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [likedTracks, setLikedTracks] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [homeHistory, setHomeHistory] = useState([]);
  const [adminPlaylists, setAdminPlaylists] = useState([]);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [userFolders, setUserFolders] = useState([]);
  const [stories, setStories] = useState([]);
  const [storiesEnabled, setStoriesEnabled] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [createMenuPosition, setCreateMenuPosition] = useState({ x: 0, y: 0 });
  const [toast, setToast] = useState({ isVisible: false, message: '' });
  const [showCreatorStudio, setShowCreatorStudio] = useState(false);
  const [moodCategories, setMoodCategories] = useState([]);

  // États pour les publicités audio
  const [showAudioAd, setShowAudioAd] = useState(false);
  const [currentAudioAd, setCurrentAudioAd] = useState(null);
  const [pendingNextTrack, setPendingNextTrack] = useState(null);
  const geoData = useGeoLocation();
  const [mobileContentPaddingBottom, setMobileContentPaddingBottom] = useState('');
  const mainScrollRef = useRef(null);
  
  // État pour la publicité interstitielle (popup)
  const [showAdInterstitial, setShowAdInterstitial] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // { type: 'artist'|'playlist'|'liked', data: any }
  
  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem('playerVolume');
    return savedVolume ? parseInt(savedVolume) : 70;
  });

  useEffect(() => {
    if (!isNativePlatform) return;
    console.log('🎧 Native audio plugin disponible:', nativePluginAvailable);
  }, [isNativePlatform, nativePluginAvailable]);

  useEffect(() => {
    if (!canUseNativePlayer) return;
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.src = '';
      audio.load();
    } catch (_) {}
  }, [canUseNativePlayer]);

  const shouldPauseStory = isStoryViewerOpen && (isPlaying || storyPauseRequested);
  const isEdgeToEdgeView =
    isMobileViewport && ['artist', 'playlist', 'liked', 'profile'].includes(view);

  useEffect(() => {
    if (!isPlaying) {
      setStoryPauseRequested(false);
    }
  }, [isPlaying]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setIsMobileViewport(!!mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobileScreen(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    nativeQueueRef.current = nativeQueue;
  }, [nativeQueue]);

  useEffect(() => {
    const handleConnectionChange = () => {
      setIsOffline(!navigator.onLine);
    };
    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);
    return () => {
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkConnectivity = async () => {
      if (!navigator.onLine) {
        if (!cancelled) setIsOffline(true);
        return;
      }
      try {
        await fetch('https://www.google.com/generate_204', {
          method: 'GET',
          mode: 'no-cors',
          cache: 'no-store'
        });
        if (!cancelled) setIsOffline(false);
      } catch (e) {
        if (!cancelled) setIsOffline(true);
      }
    };
    checkConnectivity();
    const id = setInterval(checkConnectivity, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const recordStreamIfNeeded = useCallback((durationSeconds) => {
    if (!currentTrack) return;
    if (streamRecordedRef.current) return;
    if (durationSeconds < 30) return;
    streamRecordedRef.current = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tracks/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        trackId: currentTrack.id,
        durationListened: Math.floor(durationSeconds)
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log('✅ Stream enregistré pour:', currentTrack.title);
        } else {
          streamRecordedRef.current = false;
        }
      })
      .catch(error => {
        console.error('Erreur enregistrement stream:', error);
        streamRecordedRef.current = false;
      });
  }, [currentTrack]);

  useEffect(() => {
    if (!canUseNativePlayer) return;
    let stateSub;
    let trackSub;

    const handleState = (payload = {}) => {
      if (typeof payload.isPlaying === 'boolean') {
        setIsPlaying(payload.isPlaying);
      }
      if (typeof payload.position === 'number' && typeof payload.duration === 'number' && payload.duration > 0) {
        setProgress((payload.position / payload.duration) * 100);
        setNativeDurationMs(payload.duration);
        if (payload.position >= 30000) {
          recordStreamIfNeeded(payload.position / 1000);
        }
      }
    };

    const handleTrackChange = (payload = {}) => {
      const index = Number.isFinite(payload.index) ? payload.index : null;
      if (index === null) return;
      const queue = nativeQueueRef.current || [];
      const nextTrack = queue[index];
      if (nextTrack) {
        setCurrentTrack(nextTrack);
        setCurrentTrackIndex(index);
      }
    };

    stateSub = NativeAudioPlayer.addListener('stateChanged', handleState);
    trackSub = NativeAudioPlayer.addListener('trackChanged', handleTrackChange);

    return () => {
      if (stateSub && typeof stateSub.remove === 'function') stateSub.remove();
      if (trackSub && typeof trackSub.remove === 'function') trackSub.remove();
    };
  }, [canUseNativePlayer, recordStreamIfNeeded]);

  useEffect(() => {
    if (storiesEnabled === false) {
      setOpenStoryIndex(null);
      setStories([]);
    }
  }, [storiesEnabled]);

  useEffect(() => {
    const target = mainScrollRef.current;
    if (target && typeof target.scrollTo === 'function') {
      target.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [view]);

  useEffect(() => {
    if (openStoryIndex !== null) {
      if (canUseNativePlayer) {
        NativeAudioPlayer.pause().catch(() => {});
      } else {
        const audio = audioRef.current;
        if (audio && !audio.paused) {
          audio.pause();
        }
      }
    }
  }, [openStoryIndex, canUseNativePlayer]);



  useEffect(() => {
    if (!isMobileViewport || isStoryViewerOpen) {
      setMobileContentPaddingBottom('');
      return;
    }

    const computePadding = () => {
      const playerEl = document.querySelector('.mobile-player-bar');
      const bottomNavEl = document.querySelector('.mobile-bottom-nav');
      const playerHeight =
        currentTrack && !showAudioAd && !showCreatorStudio
          ? (playerEl?.getBoundingClientRect().height || 72)
          : 0;
      const fallbackNavHeight = user && !showCreatorStudio ? 64 : 0;
      const bottomNavHeight = bottomNavEl?.getBoundingClientRect().height || fallbackNavHeight;
      const base = playerHeight + bottomNavHeight + 16;
      setMobileContentPaddingBottom(
        `calc(${base}px + env(safe-area-inset-bottom, 0px))`
      );
    };

    computePadding();
    window.addEventListener('resize', computePadding);
    return () => window.removeEventListener('resize', computePadding);
  }, [isMobileViewport, currentTrack, showAudioAd, isStoryViewerOpen, user, showCreatorStudio]);

  useEffect(() => {
    if (view !== 'home') return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_URL) return;
    fetch(`${API_URL}/api/ads/geo`)
      .then((res) => res.json())
      .then((data) => {
        console.log('Ads Geo Debug:', data);
      })
      .catch(() => {});
  }, [view]);

  const handleStoryPlayRequest = useCallback(() => {
    if (canUseNativePlayer) {
      NativeAudioPlayer.pause().catch(() => {});
      return;
    }
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
    }
  }, [canUseNativePlayer]);
  
  const audioRef = useRef(new Audio());
  const autoPlayTrackIdRef = useRef(null);
  const autoPlayHandledRef = useRef(false);
  const streamRecordedRef = useRef(false);
  const nativeInitRetryRef = useRef(false);

  useEffect(() => {
    if (autoPlayTrackIdRef.current !== null) return;
    const hash = window.location.hash || '';
    const match = hash.match(/playTrack=(\d+)/);
    if (match) {
      autoPlayTrackIdRef.current = Number(match[1]);
    }
  }, []);
  
  // Écouteur pour fermer la bibliothèque mobile
  useEffect(() => {
    const handleCloseMobileLibrary = () => {
      setShowMobileLibrary(false);
    };
    
    window.addEventListener('closeMobileLibrary', handleCloseMobileLibrary);
    return () => {
      window.removeEventListener('closeMobileLibrary', handleCloseMobileLibrary);
    };
  }, []);
  
  // Charger la dernière lecture depuis localStorage
  useEffect(() => {
    // Vérifier d'abord si l'utilisateur est connecté
    if (!user) {
      // Pas connecté : nettoyer localStorage et ne rien restaurer
      localStorage.removeItem('lastPlayedTrack');
      localStorage.removeItem('lastPlayedProgress');
      // Stopper toute lecture en cours
      setIsPlaying(false);
      setProgress(0);
      setTrackSource(null);
      if (canUseNativePlayer) {
        NativeAudioPlayer.pause().catch(() => {});
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setCurrentTrack(null);
      setCurrentArtistData(null);
      return;
    }

    const savedTrack = localStorage.getItem('lastPlayedTrack');
    
    if (savedTrack) {
      try {
        const track = JSON.parse(savedTrack);
        
        // Vérifier si la piste existe toujours dans la base de données
        fetch(`${BACKEND_URL}/api/tracks/${track.id}`)
          .then(response => {
            if (!response.ok) {
              throw new Error('Piste non trouvée');
            }
            return response.json();
          })
          .then(existingTrack => {
            // Déterminer l'URL audio - CDN ou locale
            let audioUrl;
            if (existingTrack.file_path.startsWith('http://') || existingTrack.file_path.startsWith('https://')) {
              audioUrl = existingTrack.file_path;
            } else {
              const audioFilename = existingTrack.file_path.split('/').pop();
              audioUrl = `${BACKEND_URL}/api/audio/${audioFilename}`;
            }
            
            // Vérifier si le fichier audio existe physiquement
            return fetch(audioUrl, { method: 'POST' })
              .then(audioResponse => {
                if (!audioResponse.ok) {
                  throw new Error('Fichier audio non trouvé');
                }
                return { track: existingTrack, audioUrl };
              });
          })
          .then(({ track: validTrack, audioUrl }) => {
            // Tout est OK : restaurer la piste
            setCurrentTrack(validTrack);
            
            if (!canUseNativePlayer) {
              // Préparer l'audio (mais ne pas jouer automatiquement)
              audioRef.current.src = audioUrl;
              
              // Restaurer la progression si disponible
              const savedProgress = localStorage.getItem('lastPlayedProgress');
              if (savedProgress) {
                audioRef.current.addEventListener('loadedmetadata', () => {
                  audioRef.current.currentTime = parseFloat(savedProgress);
                }, { once: true });
              }
            }
            
            // Charger les données de l'artiste si disponible
            if (validTrack.artist_id) {
              fetch(`${BACKEND_URL}/api/artists/${validTrack.artist_id}`)
                .then(response => response.ok ? response.json() : null)
                .then(data => setCurrentArtistData(data?.artist || null))
                .catch(error => console.error('Erreur chargement artiste:', error));
            }
          })
          .catch(error => {
            // Erreur : piste ou fichier n'existe plus
            console.log('Dernière lecture non disponible:', error.message);
            // Nettoyer le localStorage
            localStorage.removeItem('lastPlayedTrack');
            localStorage.removeItem('lastPlayedProgress');
            setCurrentTrack(null);
            setCurrentArtistData(null);
          });
      } catch (error) {
        console.error('Erreur parsing localStorage:', error);
        localStorage.removeItem('lastPlayedTrack');
        localStorage.removeItem('lastPlayedProgress');
      }
    }
  }, [user, canUseNativePlayer]);

  // Charger les informations d'abonnement et les notifications
  useEffect(() => {
    if (!user) return;

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');

    // Charger le statut d'abonnement (sauf pour les admins)
    if (user.status !== 'ADMIN') {
      fetch(`${BACKEND_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => res.json())
        .then(data => {
          setSubscriptionStatus(data);
        })
        .catch(err => console.error('Erreur chargement abonnement:', err));
    }

    // Charger les notifications
    fetch(`${BACKEND_URL}/api/subscription/notifications`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        setNotifications(data.notifications || []);
      })
      .catch(err => console.error('Erreur chargement notifications:', err));

    // Recharger toutes les 60 secondes
    const interval = setInterval(() => {
      if (user.status !== 'ADMIN') {
        fetch(`${BACKEND_URL}/api/subscription/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(res => res.json())
          .then(data => setSubscriptionStatus(data))
          .catch(err => console.error('Erreur:', err));
      }

      fetch(`${BACKEND_URL}/api/subscription/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => setNotifications(data.notifications || []))
        .catch(err => console.error('Erreur:', err));
    }, 60000);

    return () => clearInterval(interval);
  }, [user]);


  const createCollectionsFromTracks = (tracks) => {
    if (!tracks || tracks.length === 0) return [];
    
    // Grouper par catégorie
    const categoriesMap = {};
    tracks.forEach(track => {
      const catName = track.category_name || 'Sans catégorie';
      if (!categoriesMap[catName]) {
        categoriesMap[catName] = {
          id: `cat-${catName.toLowerCase().replace(/\s+/g, '-')}`,
          title: catName,
          subtitle: `${tracks.filter(t => (t.category_name || 'Sans catégorie') === catName).length} titres`,
          badge: 'Playlist',
          color: getColorForCategory(Object.keys(categoriesMap).length),
          coverEmoji: getEmojiForCategory(catName),
          type: 'playlist',
          tracks: []
        };
      }
      categoriesMap[catName].tracks.push(track);
    });
    
    // Créer aussi une collection "Tous les titres"
    const allTracksCollection = {
      id: 'all-tracks',
      title: 'Tous les titres',
      subtitle: `${tracks.length} titres`,
      badge: 'Playlist',
      color: 'from-fuchsia-600 via-purple-600 to-indigo-500',
      coverEmoji: '🎵',
      type: 'playlist',
      tracks: tracks
    };
    
    return [allTracksCollection, ...Object.values(categoriesMap)];
  };

  const getColorForCategory = (index) => {
    const colors = [
      'from-emerald-500 via-teal-500 to-cyan-500',
      'from-orange-500 via-red-500 to-amber-400',
      'from-rose-500 via-purple-500 to-indigo-500',
      'from-yellow-400 via-amber-400 to-orange-400',
      'from-sky-500 via-blue-500 to-indigo-500',
      'from-blue-500 via-sky-400 to-cyan-400',
      'from-purple-700 via-fuchsia-600 to-orange-500',
    ];
    return colors[index % colors.length];
  };

  const getEmojiForCategory = (categoryName) => {
    const emojiMap = {
      'Pop': '🎤',
      'Rock': '🎸',
      'Hip-Hop': '🎙️',
      'Électro': '⚡',
      'Jazz': '🎷',
      'Classique': '🎻',
      'R&B': '🎶',
      'Reggae': '🌴',
    };
    return emojiMap[categoryName] || '🎵';
  };

  // Fonction pour ouvrir la page d'abonnement
  const handleOpenSubscription = () => {
    setView('subscription');
  };

  const refreshStories = async () => {
    try {
      const status = await fetchStoryStatus();
      const enabled = status?.enabled === true;
      setStoriesEnabled(enabled);
      if (!enabled) {
        setStories([]);
        return;
      }
      const storiesData = await fetchStories();
      setStories(storiesData || []);
    } catch (error) {
      console.error('Erreur chargement stories:', error);
    }
  };

  useEffect(() => {
    if (view === 'home' || view === 'profile') {
      refreshStories();
    }
  }, [view]);

  const handleCreateStory = () => {
    if (!user) {
      openAuthModal();
      return;
    }
    if (canUseNativePlayer) {
      NativeAudioPlayer.pause().catch(() => {});
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setShowCreatorStudio(true);
  };

  const handleReportStory = async (storyId, reason) => {
    if (!user) {
      openAuthModal();
      return;
    }
    try {
      await reportStory(storyId, reason);
      setToast({ isVisible: true, message: 'Story signalée. Merci.' });
    } catch (error) {
      console.error('Erreur signalement story:', error);
      setToast({ isVisible: true, message: 'Erreur lors du signalement.' });
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!user) {
      openAuthModal();
      return;
    }
    try {
      await deleteStory(storyId);
      await refreshStories();
      if (openStoryIndex !== null) {
        setOpenStoryIndex(null);
      }
      setToast({ isVisible: true, message: 'Story supprimée.' });
    } catch (error) {
      console.error('Erreur suppression story:', error);
      setToast({ isVisible: true, message: 'Erreur lors de la suppression.' });
    }
  };

  const handleOpenStoryById = useCallback((storyId) => {
    const idx = (stories || []).findIndex((s) => s.id === storyId);
    if (idx !== -1) {
      setOpenStoryIndex(idx);
    }
  }, [stories]);

  useEffect(() => {
    if (!initialStoryId) return;
    if (storiesEnabled !== true) return;
    const idNum = Number(initialStoryId);
    if (!Number.isFinite(idNum)) return;
    const idx = (stories || []).findIndex((s) => Number(s.id) === idNum);
    if (idx !== -1) {
      setView('home');
      setOpenStoryIndex(idx);
    }
  }, [initialStoryId, stories, storiesEnabled]);

  const loadData = async () => {
    try {
      const tracks = await fetchTracks({ include_moods: true });
      setAllTracks(tracks);
      
      // Charger les mood categories
      const moodCats = await fetchMoodCategories().catch(() => []);
      setMoodCategories(Array.isArray(moodCats) ? moodCats : []);

      // Charger les stories publiques
      try {
        const status = await fetchStoryStatus();
        const enabled = status?.enabled === true;
        setStoriesEnabled(enabled);
        if (enabled) {
          const storiesData = await fetchStories();
          setStories(storiesData || []);
        } else {
          setStories([]);
        }
      } catch (error) {
        console.error('Erreur chargement stories:', error);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  // Créer les collections pour la sidebar
  const createUserCollections = () => {
    const collections = [];
    
    // Collection "Titres likés"
    if (likedTracks && likedTracks.length > 0) {
      collections.push({
        id: 'liked-tracks',
        title: 'Titres likés',
        subtitle: `${likedTracks.length} titres`,
        badge: 'Playlist',
        color: 'from-purple-600 via-pink-600 to-purple-500',
        coverEmoji: '💚',
        type: 'liked',
        tracks: likedTracks
      });
    }
    
    // Playlists de l'utilisateur
    if (userPlaylists && userPlaylists.length > 0) {
      userPlaylists.forEach(playlist => {
        collections.push({
          id: `playlist-${playlist.id}`,
          title: playlist.name,
          subtitle: `${playlist.track_count || 0} titres`,
          badge: 'Playlist',
          color: 'from-blue-600 via-cyan-600 to-blue-500',
          coverEmoji: '🎵',
          type: 'playlist',
          playlistId: playlist.id,
          tracks: []
        });
      });
    }
    
    // Musiques les plus écoutées
    if (recentlyPlayed && recentlyPlayed.length > 0) {
      collections.push({
        id: 'most-played',
        title: 'Récemment écouté',
        subtitle: `${recentlyPlayed.length} titres`,
        badge: 'Playlist',
        color: 'from-orange-600 via-red-600 to-orange-500',
        coverEmoji: '🔥',
        type: 'mostplayed',
        tracks: recentlyPlayed
      });
    }
    
    return collections;
  };
  
  // Fonction utilitaire pour dédupliquer les pistes par ID
  const deduplicateTracks = (tracks) => {
    if (!tracks || tracks.length === 0) return [];
    
    const uniqueTracks = [];
    const seenIds = new Set();
    
    for (const track of tracks) {
      if (!seenIds.has(track.id)) {
        seenIds.add(track.id);
        uniqueTracks.push(track);
      }
    }
    
    return uniqueTracks;
  };

  // Dédupliquer les pistes récemment écoutées
  const uniqueRecentlyPlayed = useMemo(
    () => deduplicateTracks(recentlyPlayed),
    [recentlyPlayed]
  );

  // Mettre à jour les collections quand les données changent
  useEffect(() => {
    const newCollections = createUserCollections();
    setCollections(newCollections);
    
    if (newCollections.length > 0 && !currentCollection) {
      setCurrentCollection(newCollections[0]);
    }
  }, [likedTracks, userPlaylists, userFolders, uniqueRecentlyPlayed]);

  const loadPersonalizedData = async () => {
    try {
      // Charger en parallèle
      const [liked, recommendations, recent, library, adminPlaylists, homeHistoryData] = await Promise.all([
        fetchLikedTracksPersonalized().catch(() => []),
        fetchRecommendations(12).catch(() => []),
        fetchRecentlyPlayed(12).catch(() => []),
        fetchUserLibrary().catch(() => ({ playlists: [], folders: [] })),
        fetchAdminPlaylists().catch(() => []),
        fetchHomeHistory(8).catch(() => [])
      ]);
      
      setLikedTracks(liked);
      setLikedTrackIds(liked.map(t => t.id));
      setRecommendations(recommendations);
      setRecentlyPlayed(recent);
      setUserPlaylists(library.playlists || []);
      setUserFolders(library.folders || []);
      setAdminPlaylists(adminPlaylists);
      setHomeHistory(homeHistoryData);
    } catch (error) {
      console.error('Erreur chargement données personnalisées:', error);
    }
  };

  // Load data from backend
  useEffect(() => {
    loadData();
  }, []);
  
  // Charger les données personnalisées quand l'utilisateur se connecte
  useEffect(() => {
    if (user) {
      loadPersonalizedData();
    } else {
      // Réinitialiser les données personnalisées si déconnecté
      setLikedTrackIds([]);
      setLikedTracks([]);
      setRecommendations([]);
      setRecentlyPlayed([]);
    }
  }, [user]);

  const getTrackCategoryIds = (track) => {
    if (!track) return [];
    const ids = new Set();
    if (track.category_id !== undefined && track.category_id !== null) {
      ids.add(String(track.category_id));
    }
    if (Array.isArray(track.categories)) {
      track.categories.forEach((cat) => {
        if (cat && cat.id !== undefined && cat.id !== null) ids.add(String(cat.id));
      });
    }
    if (Array.isArray(track.category_ids)) {
      track.category_ids.forEach((id) => {
        if (id !== undefined && id !== null) ids.add(String(id));
      });
    } else if (typeof track.category_ids === 'string') {
      try {
        const parsed = JSON.parse(track.category_ids);
        if (Array.isArray(parsed)) {
          parsed.forEach((id) => {
            if (id !== undefined && id !== null) ids.add(String(id));
          });
        }
      } catch {
        track.category_ids
          .split(',')
          .map((part) => part.trim())
          .filter((id) => id)
          .forEach((id) => ids.add(String(id)));
      }
    }
    return Array.from(ids);
  };

  const trackMatchesCategoryIds = (track, categoryIds) => {
    if (!track || !Array.isArray(categoryIds) || categoryIds.length === 0) return false;
    const categorySet = new Set(categoryIds.map((id) => String(id)));
    if (track.category_id !== undefined && track.category_id !== null && categorySet.has(String(track.category_id))) {
      return true;
    }
    if (Array.isArray(track.categories) && track.categories.some((cat) => categorySet.has(String(cat.id)))) {
      return true;
    }
    if (Array.isArray(track.category_ids) && track.category_ids.some((id) => categorySet.has(String(id)))) {
      return true;
    }
    if (typeof track.category_ids === 'string') {
      try {
        const parsed = JSON.parse(track.category_ids);
        if (Array.isArray(parsed) && parsed.some((id) => categorySet.has(String(id)))) {
          return true;
        }
      } catch {
        const parsed = track.category_ids
          .split(',')
          .map((part) => part.trim())
          .filter((id) => id);
        if (parsed.some((id) => categorySet.has(String(id)))) return true;
      }
    }
    return false;
  };

  const handlePlayTrack = async (track, collection, source = null) => {
    if (!user) {
      openAuthModal();
      return;
    }
    if (openStoryIndex !== null) {
      setStoryPauseRequested(true);
    }
    if (isNativePlatform && !nativePluginAvailable) {
      console.error('❌ Lecteur natif indisponible sur cette plateforme.');
      setToast({ isVisible: true, message: 'Lecteur natif indisponible sur mobile. Redémarre l’app.' });
      return;
    }

    if (canUseNativePlayer) {
      // Lecture native via plugin Capacitor
      if (currentTrack?.id === track.id) {
        if (isPlaying) {
          await NativeAudioPlayer.pause();
        } else {
          await NativeAudioPlayer.play();
        }
        return;
      }

      setCurrentTrack(track);
      setCurrentCollection(collection);
      setIsPlaying(false);
      setProgress(0);
      streamRecordedRef.current = false;

      if (track.artist_id) {
        fetch(`${BACKEND_URL}/api/artists/${track.artist_id}`)
          .then(response => response.ok ? response.json() : null)
          .then(data => setCurrentArtistData(data?.artist || null))
          .catch(() => setCurrentArtistData(null));
      } else {
        setCurrentArtistData(null);
      }

      if (source) {
        console.log('[NAV][PLAY] source set', source, 'track', track);
        setTrackSource(source);
      } else {
        console.log('[NAV][PLAY] source default=home', 'track', track);
        setTrackSource({ type: 'home' });
      }

      let queueTracks = [];
      if (collection?.tracks?.length) {
        queueTracks = collection.tracks;
        setActiveCategoryIds([]);
        setPlayedTrackIdsInCategory([]);
      } else {
        const categoryIds = getTrackCategoryIds(track);
        if (categoryIds.length > 0) {
          queueTracks = allTracks.filter((t) => trackMatchesCategoryIds(t, categoryIds));
          setActiveCategoryIds(categoryIds);
          setPlayedTrackIdsInCategory([track.id]);
        } else {
          queueTracks = allTracks;
          setActiveCategoryIds([]);
          setPlayedTrackIdsInCategory([]);
        }
      }

      if (!queueTracks || queueTracks.length === 0) {
        queueTracks = [track];
      }

      const isCollectionQueue = Array.isArray(collection?.tracks) && collection.tracks.length > 0;
      let orderedQueue;
      let startIndex;
      if (isCollectionQueue) {
        orderedQueue = queueTracks.slice();
        startIndex = orderedQueue.findIndex((t) => t.id === track.id);
        if (startIndex === -1) {
          orderedQueue = [track, ...queueTracks.filter((t) => t.id !== track.id)];
          startIndex = 0;
        }
      } else {
        orderedQueue = shuffleArray(queueTracks);
        startIndex = orderedQueue.findIndex((t) => t.id === track.id);
        if (startIndex === -1) {
          orderedQueue = [track, ...shuffleArray(queueTracks.filter((t) => t.id !== track.id))];
          startIndex = 0;
        }
      }
      setNativeQueue(orderedQueue);
      setCurrentTrackIndex(startIndex);

      const nativeTracks = orderedQueue.map((t) => {
        let audioUrl;
        if (t.file_path?.startsWith('http://') || t.file_path?.startsWith('https://')) {
          audioUrl = t.file_path;
        } else {
          const audioFilename = t.file_path ? t.file_path.split('/').pop() : '';
          audioUrl = `${BACKEND_URL}/api/audio/${audioFilename}`;
        }
        return {
          id: t.id,
          url: audioUrl,
          title: t.title || '',
          artist: t.artist || t.artist_name || '',
          artwork: t.cover_image ? getImageUrl(t.cover_image) : ''
        };
      });

      const startNativePlayback = async () => {
        await NativeAudioPlayer.setQueue({ tracks: nativeTracks, startIndex });
        await NativeAudioPlayer.play();
        setIsPlaying(true);
      };
      try {
        await startNativePlayback();
        nativeInitRetryRef.current = false;
      } catch (error) {
        console.error('❌ Erreur lecture native:', error);
        const errorText = String(error?.code || error?.message || error || '');
        const isUnimplemented = /UNIMPLEMENTED/i.test(errorText);
        if (!nativeInitRetryRef.current) {
          nativeInitRetryRef.current = true;
          const retryDelays = isUnimplemented ? [400, 800, 1200] : [300];
          for (const delay of retryDelays) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            try {
              await startNativePlayback();
              nativeInitRetryRef.current = false;
              return;
            } catch (err2) {
              console.error('❌ Erreur lecture native (retry):', err2);
            }
          }
        }
        setIsPlaying(false);
        if (!isIOSNative) {
          setNativePlayerFailed(true);
        }
        setToast({ isVisible: true, message: 'Erreur lecteur natif. Redémarre l’app.' });
      }
      return;
    }

    // Note: Les utilisateurs non-premium peuvent écouter les pistes premium
    // Le blocage ne s'applique qu'au téléchargement

    const audio = audioRef.current;
    
    if (currentTrack?.id === track.id) {
      // Même piste : toggle play/pause
      if (isPlaying) {
        audio.pause();
        // L'état sera mis à jour par l'événement 'pause'
      } else {
        try {
          if (audio.ended || (audio.duration && audio.currentTime >= audio.duration - 0.5)) {
            audio.currentTime = 0;
          }
          await audio.play();
          // L'état sera mis à jour par l'événement 'play'
        } catch (error) {
          console.error('Erreur lecture audio:', error);
        }
      }
    } else {
      // Nouvelle piste
      // IMPORTANT: Arrêter complètement l'audio en cours
      try {
        console.log('🛑 Arrêt de l\'audio en cours:', currentTrack?.title);
        audio.pause();
        audio.currentTime = 0;
        // Vider la source pour libérer les ressources
        audio.src = '';
        console.log('✅ Audio arrêté et nettoyé');
      } catch (e) {
        console.log('⚠️ Erreur nettoyage audio:', e);
      }
      
      // Mettre à jour l'état immédiatement
      setCurrentTrack(track);
      setCurrentCollection(collection);
      setIsPlaying(false);
      setProgress(0);
      streamRecordedRef.current = false;
      
      // Stocker la source de lecture - TOUJOURS définir une source
      if (source) {
        setTrackSource(source);
      } else {
        // Fallback : définir la source sur 'home' si non fournie
        setTrackSource({ type: 'home' });
      }
      
      // Gérer le suivi des catégories pour la lecture (home/recherche)
      if (collection) {
        // Si on joue depuis une collection, réinitialiser le suivi de catégorie
        setActiveCategoryIds([]);
        setPlayedTrackIdsInCategory([]);
      } else {
        const categoryIds = getTrackCategoryIds(track);
        if (categoryIds.length > 0) {
          const hasActive = Array.isArray(activeCategoryIds) && activeCategoryIds.length > 0;
          const sameContext = hasActive && categoryIds.some((id) => activeCategoryIds.includes(id));

          if (!sameContext) {
            setActiveCategoryIds(categoryIds);
            setPlayedTrackIdsInCategory([track.id]);
          } else {
            if (!hasActive) {
              setActiveCategoryIds(categoryIds);
            }
            setPlayedTrackIdsInCategory((prev) =>
              prev.includes(track.id) ? prev : [...prev, track.id]
            );
          }
        } else {
          // Pas de catégorie sur cette piste, réinitialiser
          setActiveCategoryIds([]);
          setPlayedTrackIdsInCategory([]);
        }
      }
      
      // Déterminer l'URL audio - si c'est une URL CDN complète, l'utiliser directement
      let newAudioUrl;
      if (track.file_path.startsWith('http://') || track.file_path.startsWith('https://')) {
        // URL CDN complète - utiliser directement
        newAudioUrl = track.file_path;
        console.log('🌐 URL CDN détectée:', newAudioUrl);
      } else {
        // Chemin local - utiliser la route API dédiée
        const audioFilename = track.file_path.split('/').pop();
        newAudioUrl = `${BACKEND_URL}/api/audio/${audioFilename}`;
        console.log('📁 URL locale:', newAudioUrl);
      }
      
      // Récupérer les données de l'artiste si artist_id existe (en parallèle)
      if (track.artist_id) {
        fetch(`${BACKEND_URL}/api/artists/${track.artist_id}`)
          .then(response => response.ok ? response.json() : null)
          .then(data => setCurrentArtistData(data?.artist || null))
          .catch(error => {
            console.error('Erreur chargement artiste:', error);
            setCurrentArtistData(null);
          });
      } else {
        setCurrentArtistData(null);
      }
      
      // Attendre un peu pour s'assurer que l'ancien audio est bien arrêté
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Charger et jouer le nouveau audio
      try {
        console.log('▶️ Chargement de la nouvelle piste:', track.title);
        audio.src = newAudioUrl;
        audio.load();
        await audio.play();
        // L'état isPlaying sera mis à jour par l'événement 'play'
        console.log('✅ Nouvelle piste en lecture:', track.title);
        
        // Sauvegarder dans localStorage
        localStorage.setItem('lastPlayedTrack', JSON.stringify(track));
        
        // Enregistrer l'écoute pour les recommandations (avec source)
        if (user) {
          // Déterminer la source pour l'historique
          let sourceType = 'track';
          let sourceId = null;
          
          if (source?.type === 'playlist' && source?.playlistId) {
            sourceType = 'playlist';
            sourceId = source.playlistId;
          } else if (source?.type === 'admin-playlist' && source?.playlistId) {
            sourceType = 'playlist';
            sourceId = source.playlistId;
          } else if (track.is_mood) {
            sourceType = 'mood';
          }
          
          recordPlay(track.id, sourceType, sourceId)
            .then(() => {
              // Rafraîchir l'historique après enregistrement
              fetchHomeHistory(8).then(data => setHomeHistory(data)).catch(() => {});
            })
            .catch(err => console.error('Erreur enregistrement écoute:', err));
        }
      } catch (error) {
        console.error('❌ Erreur lecture audio:', error);
        setIsPlaying(false);
      }
    }
  };

  useEffect(() => {
    if (autoPlayHandledRef.current) return;
    const targetId = autoPlayTrackIdRef.current;
    if (!targetId) return;
    if (!user) return;
    if (!Array.isArray(allTracks) || allTracks.length === 0) return;
    const track = allTracks.find((t) => Number(t.id) === Number(targetId));
    if (!track) return;
    autoPlayHandledRef.current = true;
    handlePlayTrack(track, { tracks: allTracks }, { type: 'share', id: targetId });
    if (window.location.hash && window.location.hash.includes('playTrack=')) {
      window.history.replaceState(null, '', `${window.location.pathname}#/`);
    }
  }, [allTracks, user]);

  // Fonction appelée quand la publicité audio est terminée ou skippée
  const handleAudioAdComplete = async () => {
    if (currentAudioAd) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const sessionId = localStorage.getItem('ad_session_id');
        await fetch(`${API_URL}/api/ads/complete/${currentAudioAd.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {})
          },
          body: JSON.stringify({ session_id: sessionId })
        });
      } catch (error) {
        console.error('Erreur enregistrement complétion pub:', error);
      }
    }
    
    setShowAudioAd(false);
    setCurrentAudioAd(null);
    
    // Passer à la piste suivante après la pub
    handleNext();
  };

  // Gérer la lecture aléatoire (shuffle)
  const handleToggleShuffle = () => {
    setIsShuffleActive(!isShuffleActive);
    
    // Si on active shuffle et qu'on a une collection, mélanger les tracks
    if (!isShuffleActive && currentCollection && currentCollection.tracks) {
      const tracks = [...currentCollection.tracks];
      // Algorithme de Fisher-Yates pour mélanger le tableau
      for (let i = tracks.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [tracks[i], tracks[j]] = [tracks[j], tracks[i]];
      }
      setShuffledPlaylist(tracks);
      setCurrentTrackIndex(tracks.findIndex(t => t.id === currentTrack?.id));
    }
  };

  // Gérer la répétition
  const handleToggleRepeat = () => {
    const newValue = !isRepeatActive;
    setIsRepeatActive(newValue);
    isRepeatActiveRef.current = newValue;
  };

  const handleNext = () => {
    if (isNativePlatform && !nativePluginAvailable) {
      setToast({ isVisible: true, message: 'Lecteur natif indisponible sur mobile. Redémarre l’app.' });
      return;
    }
    if (canUseNativePlayer) {
      NativeAudioPlayer.next().catch(() => {});
      return;
    }
    // Si shuffle est actif et qu'on a une playlist mélangée
    if (isShuffleActive && shuffledPlaylist.length > 0) {
      const nextIndex = (currentTrackIndex + 1) % shuffledPlaylist.length;
      setCurrentTrackIndex(nextIndex);
      handlePlayTrack(shuffledPlaylist[nextIndex], currentCollection);
      return;
    }

    // Si on a une collection (playlist, artiste), jouer la piste suivante dans l'ordre
    if (currentCollection && currentCollection.tracks && currentCollection.tracks.length > 0) {
      const idx = currentCollection.tracks.findIndex((t) => t.id === currentTrack.id);
      const next = currentCollection.tracks[(idx + 1) % currentCollection.tracks.length];
      handlePlayTrack(next, currentCollection);
      return;
    }
    
    // Page d'accueil / recherche : lecture par catégories (garder le même "type" de contenu)
    const categoryIdsToFollow =
      Array.isArray(activeCategoryIds) && activeCategoryIds.length > 0
        ? activeCategoryIds
        : getTrackCategoryIds(currentTrack);

    if (categoryIdsToFollow.length > 0) {
      console.log(`🎵 Suivi des catégories: ${categoryIdsToFollow.join(', ')}`);

      // Récupérer toutes les pistes qui correspondent à ces catégories
      const categoryTracks = allTracks.filter((t) =>
        trackMatchesCategoryIds(t, categoryIdsToFollow)
      );
      console.log(`📊 Pistes dans les catégories: ${categoryTracks.length}`);

      if (categoryTracks.length === 0) {
        console.log('⏯ Aucune piste disponible dans ces catégories, relance de la piste courante');
        const audio = audioRef.current;
        if (audio && currentTrack) {
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
        return;
      }

      // Filtrer les pistes non encore jouées
      const unplayedTracks = categoryTracks.filter(
        (t) => !playedTrackIdsInCategory.includes(t.id)
      );
      console.log(`✨ Pistes non jouées: ${unplayedTracks.length}`);

      if (unplayedTracks.length > 0) {
        // Sélectionner une piste aléatoire
        const randomIndex = Math.floor(Math.random() * unplayedTracks.length);
        const randomTrack = unplayedTracks[randomIndex];

        console.log(`▶️ Lecture de: ${randomTrack.title}`);

        // Mettre à jour les états AVANT d'appeler handlePlayTrack
        const newPlayedIds = [...playedTrackIdsInCategory, randomTrack.id];
        setActiveCategoryIds(categoryIdsToFollow);
        setPlayedTrackIdsInCategory(newPlayedIds);

        // Jouer la piste
        handlePlayTrack(randomTrack, null);
      } else {
        // Toutes les pistes de ces catégories ont été jouées -> recommencer un nouveau tour
        console.log(`🔁 Toutes les ${categoryTracks.length} pistes ont été jouées, relance d'un nouveau tour`);
        const randomIndex = Math.floor(Math.random() * categoryTracks.length);
        const randomTrack = categoryTracks[randomIndex];

        setActiveCategoryIds(categoryIdsToFollow);
        setPlayedTrackIdsInCategory([randomTrack.id]);

        handlePlayTrack(randomTrack, null);
      }
      return;
    }

    // Pas de catégorie, jouer n'importe quelle piste
    console.log('🎲 Lecture aléatoire sans catégorie');
    const availableTracks = allTracks.filter((t) => t.id !== currentTrack?.id);
    if (availableTracks.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableTracks.length);
      const randomTrack = availableTracks[randomIndex];
      handlePlayTrack(randomTrack, null);
    } else if (currentTrack) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }
    }
  };

  const handlePrev = () => {
    if (isNativePlatform && !nativePluginAvailable) {
      setToast({ isVisible: true, message: 'Lecteur natif indisponible sur mobile. Redémarre l’app.' });
      return;
    }
    if (canUseNativePlayer) {
      NativeAudioPlayer.previous().catch(() => {});
      return;
    }
    if (!currentCollection || !currentTrack) return;

    // Si shuffle est actif
    if (isShuffleActive && shuffledPlaylist.length > 0) {
      const prevIndex = (currentTrackIndex - 1 + shuffledPlaylist.length) % shuffledPlaylist.length;
      setCurrentTrackIndex(prevIndex);
      handlePlayTrack(shuffledPlaylist[prevIndex], currentCollection);
      return;
    }

    // Mode normal
    const idx = currentCollection.tracks.findIndex((t) => t.id === currentTrack.id);
    const prev = currentCollection.tracks[(idx - 1 + currentCollection.tracks.length) % currentCollection.tracks.length];
    handlePlayTrack(prev, currentCollection);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    if (canUseNativePlayer) return;
    const audio = audioRef.current;
    
    return () => {
      // Nettoyer l'audio quand le composant se démonte
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
      }
    };
  }, [canUseNativePlayer]);

  // Audio player logic avec synchronisation de l'état
  useEffect(() => {
    if (canUseNativePlayer) return;
    const audio = audioRef.current;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        
        // Sauvegarder la progression toutes les secondes
        if (currentTrack && Math.floor(audio.currentTime) % 1 === 0) {
          localStorage.setItem('lastPlayedProgress', audio.currentTime.toString());
        }

        // Enregistrer un stream après 30 secondes d'écoute (une seule fois par track)
        if (currentTrack && audio.currentTime >= 30) {
          recordStreamIfNeeded(audio.currentTime);
        }
      }
    };

    const handleEnded = async () => {
      if (currentTrack && !streamRecordedRef.current) {
        const durationFallback = audio.duration && !Number.isNaN(audio.duration)
          ? audio.duration
          : audio.currentTime;
        recordStreamIfNeeded(durationFallback);
      }
      // Si repeat est activé, relancer la même musique
      if (isRepeatActiveRef.current && currentTrack) {
        audio.currentTime = 0;
        audio.play();
      } else {
        // Vérifier si on doit afficher une publicité (utilisateurs gratuits uniquement)
        if (!userIsPremium) {
          try {
            // Incrémenter le compteur de pistes et vérifier si une pub doit être jouée
            await trackAdPlayed();
            
            // Récupérer une pub audio si disponible
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const sessionId = localStorage.getItem('ad_session_id') || 'sess_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('ad_session_id', sessionId);
            
            const countryParam = encodeURIComponent(geoData.country || 'all');
            const languageParam = encodeURIComponent(geoData.language || 'all');
            const sessionParam = encodeURIComponent(sessionId);
            const response = await fetch(`${API_URL}/api/ads/serve/audio/${countryParam}/${languageParam}/${sessionParam}`, {
              headers: localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {}
            });
            const result = await response.json();
            
            if (result.ad && result.ad.audio_url) {
              // Afficher la publicité audio avant de passer à la piste suivante
              setCurrentAudioAd(result.ad);
              setShowAudioAd(true);
              return; // Ne pas passer à la piste suivante tout de suite
            }
          } catch (error) {
            console.error('Erreur vérification pub audio:', error);
          }
        }
        
        // Pas de pub ou utilisateur premium : passer à la suivante
        handleNext();
      }
    };

    // Synchroniser l'état isPlaying avec l'état réel de l'audio
    const handlePlay = () => {
      console.log('🎵 Audio playing - synchronisation état');
      if (audio.currentTime < 1) {
        streamRecordedRef.current = false;
      }
      setIsPlaying(true);
    };

    const handlePause = () => {
      console.log('⏸ Audio paused - synchronisation état');
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error('❌ Erreur audio:', e);
      setIsPlaying(false);
    };

    // Ajouter tous les écouteurs
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
    };
  }, [currentTrack, canUseNativePlayer, recordStreamIfNeeded]);

  const handleTogglePlay = async () => {
    if (!currentTrack) return;
    if (isNativePlatform && !nativePluginAvailable) {
      setToast({ isVisible: true, message: 'Lecteur natif indisponible sur mobile. Redémarre l’app.' });
      return;
    }
    if (canUseNativePlayer) {
      if (isPlaying) {
        await NativeAudioPlayer.pause();
      } else {
        await NativeAudioPlayer.play();
      }
      return;
    }
    const audio = audioRef.current;
    
    if (isPlaying) {
      audio.pause();
      // L'état sera mis à jour par l'événement 'pause'
    } else {
      try {
        if (openStoryIndex !== null) {
          setStoryPauseRequested(true);
        }
        await audio.play();
        // L'état sera mis à jour par l'événement 'play'
      } catch (error) {
        console.error('Erreur lecture audio:', error);
        setIsPlaying(false);
      }
    }
  };

  const handleSeek = (percentage) => {
    if (!Number.isFinite(percentage)) return;
    const safePercent = Math.min(100, Math.max(0, percentage));
    if (isNativePlatform && !nativePluginAvailable) {
      setToast({ isVisible: true, message: 'Lecteur natif indisponible sur mobile. Redémarre l’app.' });
      return;
    }
    if (canUseNativePlayer) {
      const durationMs = nativeDurationMs > 0 ? nativeDurationMs : (currentTrack?.duration ? currentTrack.duration * 1000 : 0);
      if (durationMs > 0) {
        const targetMs = Math.floor((safePercent / 100) * durationMs);
        NativeAudioPlayer.seekTo({ position: targetMs }).catch(() => {});
      }
      return;
    }
    const audio = audioRef.current;
    if (audio.duration) {
      audio.currentTime = (safePercent / 100) * audio.duration;
      setProgress(safePercent);
    }
  };

  // Gestion du volume
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (!canUseNativePlayer) {
      audioRef.current.volume = newVolume / 100;
    }
    localStorage.setItem('playerVolume', newVolume.toString());
  };

  // Appliquer le volume initial à l'audio
  useEffect(() => {
    if (!canUseNativePlayer) {
      audioRef.current.volume = volume / 100;
    }
  }, [canUseNativePlayer]);

  const handleToggleLike = async (trackId) => {
    if (!user) {
      openAuthModal();
      return;
    }
    
    const isLiked = likedTrackIds.includes(trackId);
    
    try {
      if (isLiked) {
        // Unlike
        await unlikeTrack(trackId);
        setLikedTrackIds(prev => prev.filter(id => id !== trackId));
        setLikedTracks(prevTracks => prevTracks.filter(track => track.id !== trackId));
      } else {
        // Like
        await likeTrackPersonalized(trackId);
        setLikedTrackIds(prev => [...prev, trackId]);
        const trackToAdd = allTracks.find(track => track.id === trackId);
        if (trackToAdd) {
          setLikedTracks(prevTracks => [...prevTracks, trackToAdd]);
        }
      }
    } catch (error) {
      console.error('Erreur toggle like:', error);
    }
  };

  const handleCreatePlaylist = async (data) => {
    try {
      console.log('🎵 Création playlist:', data);
      const result = await createPlaylist(data);
      console.log('✅ Playlist créée:', result);
      
      // Recharger IMMÉDIATEMENT la bibliothèque
      const library = await fetchUserLibrary();
      console.log('📚 Bibliothèque rechargée:', library);
      
      setUserPlaylists(library.playlists || []);
      setUserFolders(library.folders || []);
      
      // Force un re-render
      setCollections([...collections]);
      
      console.log('✅ États mis à jour');
    } catch (error) {
      console.error('❌ Erreur création playlist:', error);
      alert('Erreur lors de la création de la playlist');
    }
  };

  const handleCreateFolder = async (data) => {
    try {
      console.log('📁 Création dossier:', data);
      const result = await createFolder(data);
      console.log('✅ Dossier créé:', result);
      
      // Recharger IMMÉDIATEMENT la bibliothèque
      const library = await fetchUserLibrary();
      console.log('📚 Bibliothèque rechargée:', library);
      
      setUserPlaylists(library.playlists || []);
      setUserFolders(library.folders || []);
      
      // Force un re-render
      setCollections([...collections]);
      
      console.log('✅ États mis à jour');
    } catch (error) {
      console.error('❌ Erreur création dossier:', error);
      alert('Erreur lors de la création du dossier');
    }
  };

  const handleUpdateProfileImage = (newImagePath) => {
    // Mettre à jour le contexte utilisateur sans recharger la page
    updateUser({ profile_image: newImagePath });
  };

  const handleUpdateCoverImage = (newImagePath) => {
    updateUser({ cover_image: newImagePath });
  };

  const handleGoToProfile = () => {
    setView("profile");
  };

  const handleAccountPanelSelect = (optionId) => {
    setView(optionId);
  };

  const handleUpdatePersonalInfo = async (formData) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${BACKEND_URL}/api/auth/update-info`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) throw new Error('Erreur lors de la mise à jour');
    
    const data = await response.json();
    updateUser(data.user);
  };


  const handleMarkNotificationAsRead = async (notificationId) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${BACKEND_URL}/api/subscription/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Mettre à jour localement
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Si c'est une notification de nouveau titre, ouvrir la page de l'artiste
    if (notification.type === 'new_track' && notification.artist_name) {
      try {
        // Récupérer le track spécifique de la notification ou n'importe quel track de l'artiste
        let track;
        
        if (notification.track_id) {
          // Essayer de récupérer le track spécifique
          const trackResponse = await fetch(`${BACKEND_URL}/api/tracks/${notification.track_id}`);
          if (trackResponse.ok) {
            track = await trackResponse.json();
          }
        }
        
        // Si on n'a pas le track, chercher n'importe quel track de cet artiste
        if (!track) {
          const response = await fetch(`${BACKEND_URL}/api/tracks`);
          if (response.ok) {
            const allTracks = await response.json();
            track = allTracks.find(t => t.artist === notification.artist_name);
          }
        }
        
        // Utiliser la fonction existante openArtistPage
        if (track) {
          await openArtistPage(track);
        }
      } catch (error) {
        console.error('Erreur ouverture page artiste:', error);
      }
    }
  };


  const handleUpdateAddress = async (formData) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${BACKEND_URL}/api/auth/update-address`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) throw new Error('Erreur lors de la mise à jour');
    
    // Mettre à jour le contexte utilisateur avec les nouvelles données d'adresse
    updateUser(formData);
  };

  const handleSubscribe = async (planId) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ planId })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la création de la session de paiement');
      }

      const { url } = await response.json();
      
      // Rediriger vers Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création de la session de paiement');
    }
  };

  const handleCancelAccount = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      // Utilise POST au lieu de DELETE pour compatibilité OVH mutualisé
      const response = await fetch(`${BACKEND_URL}/api/auth/delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        alert('Votre compte a été supprimé');
        logout();
      } else {
        alert('Erreur lors de la suppression du compte');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du compte');
    }
  };

  const handleAddTrackToPlaylist = async (trackId, playlistId) => {
    try {
      await addTrackToPlaylist(playlistId, trackId);
      
      // Trouver le nom de la playlist pour le message
      const playlist = userPlaylists.find(p => p.id === playlistId);
      const playlistName = playlist ? playlist.name : 'la playlist';
      
      // Afficher le toast de confirmation
      setToast({
        isVisible: true,
        message: `Ajouté à ${playlistName}`
      });

      // Recharger la bibliothèque pour mettre à jour le compteur de pistes
      const library = await fetchUserLibrary();
      setUserPlaylists(library.playlists || []);
    } catch (error) {
      console.error('❌ Erreur ajout piste à la playlist:', error);
      setToast({
        isVisible: true,
        message: error.response?.data?.error || 'Erreur lors de l\'ajout'
      });
    }
  };

  const handleRemoveFromPlaylist = async (trackId, playlistId) => {
    try {
      await removeTrackFromPlaylist(playlistId, trackId);
      
      // Afficher le toast de confirmation
      setToast({
        isVisible: true,
        message: 'Piste retirée de la playlist'
      });

      // Recharger la playlist pour mettre à jour la liste des pistes
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const playlistData = await response.json();
        setCurrentPlaylist(playlistData);
        setPlaylistTracks(playlistData.tracks || []);
      }

      // Recharger aussi la bibliothèque pour mettre à jour le compteur
      const library = await fetchUserLibrary();
      setUserPlaylists(library.playlists || []);
    } catch (error) {
      console.error('❌ Erreur retrait piste de la playlist:', error);
      setToast({
        isVisible: true,
        message: error.response?.data?.error || 'Erreur lors du retrait'
      });
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }
      
      // Afficher le toast de confirmation
      setToast({
        isVisible: true,
        message: 'Playlist supprimée avec succès'
      });

      // Si la playlist supprimée était la playlist actuelle, revenir à l'accueil
      if (currentPlaylist?.id === playlistId) {
        setView('home');
        setCurrentPlaylist(null);
        setPlaylistTracks([]);
      }

      // Recharger la bibliothèque
      const library = await fetchUserLibrary();
      setUserPlaylists(library.playlists || []);
    } catch (error) {
      console.error('❌ Erreur suppression playlist:', error);
      setToast({
        isVisible: true,
        message: 'Erreur lors de la suppression de la playlist'
      });
      throw error; // Re-throw pour que le modal sache qu'il y a eu une erreur
    }
  };

  // Ouvrir une playlist depuis l'historique de la page d'accueil
  const handleOpenPlaylistFromHistory = async (playlistId, isAdmin = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const playlistData = await response.json();
        setCurrentPlaylist(playlistData);
        setPlaylistTracks(playlistData.tracks || []);
        setView('playlist');
      }
    } catch (error) {
      console.error('Erreur ouverture playlist:', error);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (value.trim()) {
      setView("search");
    } else if (view === "search") {
      setView("home");
    }
  };

  const handleCreateClick = (event) => {
    if (!user) {
      openAuthModal();
      return;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;
    
    // Sur mobile, positionner à gauche du bouton
    // Sur desktop, positionner à droite
    setCreateMenuPosition({
      x: isMobile ? Math.max(8, rect.left - 160) : rect.left,
      y: rect.bottom + 8
    });
    setShowCreateMenu(true);
  };

  // Navigation depuis le player vers la source de lecture
  const handleNavigateToSource = async () => {
    if (!trackSource) return;
    
    switch (trackSource.type) {
      case 'liked':
        setView('liked');
        break;
        
      case 'artist':
        if (trackSource.artist) {
          setCurrentArtist(trackSource.artist);
          // Charger les pistes de l'artiste
          try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/artists/${trackSource.artist.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const data = await response.json();
              setArtistTracks(data.tracks || []);
            }
          } catch (error) {
            console.error('Erreur chargement pistes artiste:', error);
          }
          setView('artist');
        }
        break;
        
      case 'playlist':
      case 'admin-playlist':
        if (trackSource.id) {
          try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/${trackSource.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const playlistData = await response.json();
              setCurrentPlaylist(playlistData);
              setPlaylistTracks(playlistData.tracks || []);
              setView('playlist');
            }
          } catch (error) {
            console.error('Erreur chargement playlist:', error);
          }
        }
        break;
        
      case 'home':
      default:
        // Depuis la homepage, rediriger vers la page de l'artiste de la piste en cours
        if (currentTrack) {
          await openArtistPage(currentTrack);
        }
        break;
    }
  };

  const playCollectionFirstTrack = async (collection) => {
    if (!user) {
      openAuthModal();
      return;
    }
    
    setCurrentCollection(collection);
    
    // Si c'est la collection "Titres likés", afficher la vue dédiée
    if (collection.id === 'liked-tracks') {
      setView('liked');
      return;
    }
    
    // Si c'est une playlist utilisateur ou admin, afficher la vue playlist
    if (collection.type === 'user-playlist' || collection.type === 'admin-playlist') {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists/${collection.playlistId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const playlistData = await response.json();
          setCurrentPlaylist(playlistData);
          setPlaylistTracks(playlistData.tracks || []);
          
          // Lancer la première piste avec la source de la playlist
          if (playlistData.tracks && playlistData.tracks.length > 0) {
            const source = {
              type: collection.type === 'admin-playlist' ? 'admin-playlist' : 'playlist',
              id: collection.playlistId,
              name: playlistData.name || collection.title
            };
            handlePlayTrack(playlistData.tracks[0], { tracks: playlistData.tracks }, source);
          }
          
          setView('playlist');
          return;
        } else {
          console.error('Erreur API:', response.status);
        }
      } catch (error) {
        console.error('Erreur chargement playlist:', error);
      }
    }
    
    if (collection.tracks && collection.tracks.length > 0) {
      // Définir la source selon le type de collection
      const source = collection.type === 'admin-playlist' 
        ? { type: 'admin-playlist', id: collection.playlistId, name: collection.title }
        : collection.type === 'user-playlist'
        ? { type: 'playlist', id: collection.playlistId, name: collection.title }
        : { type: 'home' };
      handlePlayTrack(collection.tracks[0], collection, source);
    }
  };

  const openCollectionPage = (collection) => {
    setCurrentCollection(collection);
    setView("collection");
  };

  const openArtistPage = async (track) => {
    if (!user) {
      openAuthModal();
      return;
    }
    
    // Pour les utilisateurs non-premium, afficher le popup interstitiel
    // Ne pas confondre "abonnement actif" (peut inclure le gratuit) avec PREMIUM.
    const isPremium = computeUserIsPremium(user, subscriptionStatus);
    if (!isPremium) {
      // Stocker la navigation en attente et afficher le popup
      setPendingNavigation({ type: track?.is_mood ? 'mood' : 'artist', data: track });
      setShowAdInterstitial(true);
      return;
    }
    
    // Sinon, continuer la navigation directement
    const hasArtistId = !!track?.artist_id;
    let moodCategoryId =
      (trackSource?.type === 'mood-category' && trackSource?.id) ||
      track?.mood_category_id ||
      (Array.isArray(track?.mood_category_ids) && track.mood_category_ids[0]) ||
      track?.category_id ||
      null;
    let resolvedTrack = track;
    if (!hasArtistId && track?.is_mood && !moodCategoryId && track?.id) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/tracks/${track.id}`);
        if (response.ok) {
          const data = await response.json();
          const resolvedId =
            data?.mood_category_id ||
            (Array.isArray(data?.mood_category_ids) && data.mood_category_ids[0]) ||
            data?.category_id ||
            null;
          if (resolvedId) {
            moodCategoryId = resolvedId;
            resolvedTrack = {
              ...track,
              mood_category_id: data?.mood_category_id || resolvedId,
              mood_category_ids: Array.isArray(data?.mood_category_ids) && data.mood_category_ids.length
                ? data.mood_category_ids
                : [resolvedId],
              mood_category_name: data?.mood_category_name || data?.category_name,
              category_name: data?.category_name || data?.mood_category_name
            };
          }
        }
      } catch (error) {
        console.error('Erreur récupération mood category (track details):', error);
      }
    }
    console.log('[NAV][OPEN] openArtistPage', {
      track: resolvedTrack,
      trackSource,
      hasArtistId,
      moodCategoryId
    });

    // Priority: artist if present, otherwise mood category if available
    if (hasArtistId) {
      console.log('[NAV][OPEN] -> artist (by id)');
      await executeArtistNavigation(track);
      return;
    }
    if ((resolvedTrack?.is_mood || moodCategoryId) && moodCategoryId) {
      console.log('[NAV][OPEN] -> mood category', moodCategoryId);
      executeMoodNavigation(
        trackSource?.type === 'mood-category'
          ? { id: trackSource.id, name: trackSource.name }
          : resolvedTrack
      );
      return;
    }
    const hasArtistName = !!(track?.artist || track?.artist_name);
    if (hasArtistName) {
      console.log('[NAV][OPEN] -> artist (by name)');
      await executeArtistNavigation(track);
      return;
    }
    if (moodCategoryId) {
      executeMoodNavigation(
        trackSource?.type === 'mood-category'
          ? { id: trackSource.id, name: trackSource.name }
          : track
      );
      return;
    }
    await executeArtistNavigation(track);
  };
  
  // Fonction helper pour exécuter la navigation vers la page artiste
  const executeArtistNavigation = async (track) => {
    // Si la piste a un artist_id, récupérer les vraies données de l'artiste
    let artist;
    if (track.artist_id) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/artists/${track.artist_id}`);
        if (response.ok) {
          const data = await response.json();
          artist = data.artist; // Extract artist from response
        } else {
          // Fallback si l'API échoue
          artist = {
            name: track.artist || 'Artiste inconnu',
            bio: track.category_name ? `Artiste ${track.category_name}` : 'Artiste',
            emoji: track.cover_emoji || '🎤'
          };
        }
      } catch (error) {
        console.error('Erreur chargement artiste:', error);
        artist = {
          name: track.artist || 'Artiste inconnu',
          bio: track.category_name ? `Artiste ${track.category_name}` : 'Artiste',
          emoji: track.cover_emoji || '🎤'
        };
      }
    } else {
      // Pas d'artist_id : créer un objet artiste basique
      artist = {
        name: track.artist || 'Artiste inconnu',
        bio: track.category_name ? `Artiste ${track.category_name}` : 'Artiste',
        emoji: track.cover_emoji || '🎤'
      };
    }
    
    // Filtrer toutes les pistes du même artiste
    const tracks = allTracks.filter(t => 
      (track.artist_id && t.artist_id === track.artist_id) || 
      (!track.artist_id && t.artist === track.artist)
    );
    
    setCurrentArtist(artist);
    setArtistTracks(tracks);
    setView("artist");
  };

  // Fonction helper pour exécuter la navigation vers la page mood
  const executeMoodNavigation = (trackOrMood) => {
    const moodId =
      trackOrMood?.mood_category_id ||
      (Array.isArray(trackOrMood?.mood_category_ids) && trackOrMood.mood_category_ids[0]) ||
      trackOrMood?.category_id ||
      trackOrMood?.id;
    if (!moodId) return;
    const moodName =
      trackOrMood?.category_name ||
      trackOrMood?.mood_category_name ||
      trackOrMood?.name ||
      'Mood';
    const fullCategory = (moodCategories || []).find((c) => String(c.id) === String(moodId));
    console.log('[NAV][MOOD] executeMoodNavigation', {
      moodId,
      moodName,
      hasFullCategory: !!fullCategory,
      trackOrMood
    });
    const moodPayload = fullCategory || {
      id: moodId,
      name: moodName,
      cover_image:
        trackOrMood?.cover_image ||
        trackOrMood?.mood_cover_image ||
        trackOrMood?.category_image ||
        trackOrMood?.image ||
        null
    };
    setView('home');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openMoodCategory', { detail: moodPayload }));
    }, 50);
  };
  
  // Exposer la fonction openArtistPage globalement pour le RightPanel
  useEffect(() => {
    window.openArtistPageGlobal = openArtistPage;
    return () => {
      delete window.openArtistPageGlobal;
    };
  }, [openArtistPage]);
  
  // Gestion de la fermeture du popup interstitiel
  const handleAdInterstitialClose = async () => {
    setShowAdInterstitial(false);
    
    // Exécuter la navigation en attente
    if (pendingNavigation) {
      const { type, data } = pendingNavigation;
      setPendingNavigation(null);
      
      if (type === 'artist') {
        await executeArtistNavigation(data);
      } else if (type === 'mood') {
        executeMoodNavigation(data);
      } else if (type === 'playlist') {
        // Pour les playlists
        setCurrentPlaylist(data.playlist);
        setPlaylistTracks(data.tracks || []);
        setView('playlist');
      } else if (type === 'liked') {
        setView('liked');
      }
    }
  };

  const isPlayingCollection =
    isPlaying &&
    !!currentCollection &&
    !!currentTrack &&
    currentCollection.tracks && 
    currentCollection.tracks.some((t) => t.id === currentTrack.id);

  // Vérifier si l'utilisateur est PREMIUM (admin ou premium explicite). Les utilisateurs gratuits doivent voir les pubs.
  const userIsPremium = computeUserIsPremium(user, subscriptionStatus);

  // Script EffectiveGateCPM retiré

  const detectDomBait = useCallback(() => {
    if (!document?.body) return false;
    const bait = document.createElement('div');
    bait.className = 'adsbygoogle ad ad-banner adbox pub_300x250 pub_728x90 text-ad textAd text_ads';
    bait.style.cssText = 'position:absolute!important;left:-9999px!important;top:-9999px!important;width:1px!important;height:1px!important;pointer-events:none!important;';
    document.body.appendChild(bait);
    const style = window.getComputedStyle(bait);
    const blocked =
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      bait.offsetParent === null ||
      bait.offsetHeight === 0 ||
      bait.clientHeight === 0;
    bait.remove();
    return blocked;
  }, []);

  const runAdBlockCheck = useCallback(
    async (forceShow = false) => {
      if (!user || userIsPremium) return false;
      if (adBlockCheckInFlightRef.current) return adBlockLastCheckRef.current.blocked;
      adBlockCheckInFlightRef.current = true;
      try {
        const detectLib = await new Promise((resolve) => {
          DetectAdblock((blocked) => resolve(!!blocked));
        });
        const detectDom = detectDomBait();
        const blocked = Boolean(detectLib || detectDom);
        adBlockLastCheckRef.current = { ts: Date.now(), blocked };
        setIsAdBlockerActive(blocked);
        if (blocked && forceShow) setShowAdBlockModal(true);
        if (!blocked && forceShow) setShowAdBlockModal(false);
        return blocked;
      } catch (error) {
        console.error('AdBlock check error:', error);
        adBlockLastCheckRef.current = { ts: Date.now(), blocked: false };
        setIsAdBlockerActive(false);
        if (forceShow) setShowAdBlockModal(false);
        return false;
      } finally {
        adBlockCheckInFlightRef.current = false;
      }
    },
    [user, userIsPremium, detectDomBait]
  );

  useEffect(() => {
    if (!user || userIsPremium) {
      setIsAdBlockerActive(false);
      setShowAdBlockModal(false);
      if (adBlockIntervalRef.current) {
        window.clearInterval(adBlockIntervalRef.current);
        adBlockIntervalRef.current = null;
      }
      return;
    }

    runAdBlockCheck(true);
    if (adBlockIntervalRef.current) {
      window.clearInterval(adBlockIntervalRef.current);
    }
    adBlockIntervalRef.current = window.setInterval(() => {
      runAdBlockCheck(false);
    }, 3000);

    return () => {
      if (adBlockIntervalRef.current) {
        window.clearInterval(adBlockIntervalRef.current);
        adBlockIntervalRef.current = null;
      }
    };
  }, [user, userIsPremium, runAdBlockCheck]);

  useEffect(() => {
    if (!isAdBlockerActive) {
      setShowAdBlockModal(false);
    }
  }, [isAdBlockerActive]);

  useEffect(() => {
    if (!user || userIsPremium) return;
    const handleClickCapture = async (event) => {
      const target = event.target;
      if (target?.closest?.('[data-adblock-allow="true"]')) return;

      // Check sync (DOM bait) first so we can block immediately
      const domBlocked = detectDomBait();
      const cachedBlocked = adBlockLastCheckRef.current.blocked;
      const blockedNow = domBlocked || cachedBlocked;

      if (blockedNow) {
        setShowAdBlockModal(true);
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        // Refresh async check in background
        runAdBlockCheck(false);
        return;
      }

      // If not blocked, still refresh occasionally
      const { ts } = adBlockLastCheckRef.current;
      if (Date.now() - ts > 2000) {
        runAdBlockCheck(false);
      }
    };

    document.addEventListener('click', handleClickCapture, true);
    return () => document.removeEventListener('click', handleClickCapture, true);
  }, [user, userIsPremium, runAdBlockCheck, detectDomBait]);

  let mainContent;
  if (openStoryIndex !== null) {
    mainContent = (
      <div className="h-full flex flex-col">
        <StoryViewer
          stories={stories}
          startIndex={openStoryIndex}
          onClose={() => setOpenStoryIndex(null)}
          resolveProfileImage={getUserImageUrl}
          resolveImageUrl={getImageUrl}
          shouldPause={!!shouldPauseStory}
          onStoryPlay={handleStoryPlayRequest}
          user={user}
          onReportStory={handleReportStory}
          onDeleteStory={handleDeleteStory}
          onRequireAuth={() => openAuthModal()}
        />
      </div>
    );
  } else if (view === "liked") {
    mainContent = (
      <LikedTracksView
        tracks={likedTracks}
        onPlayTrack={(track) => handlePlayTrack(track, { tracks: likedTracks }, { type: 'liked' })}
        onToggleLike={handleToggleLike}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        likedTrackIds={likedTrackIds}
        userPlaylists={userPlaylists}
        userFolders={userFolders}
        onAddToPlaylist={handleAddTrackToPlaylist}
        onShowToast={(msg) => setToast({ isVisible: true, message: msg })}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={handleOpenSubscription}
      />
    );
  } else if (view === "artist") {
    mainContent = (
      <ArtistView
        artist={currentArtist}
        tracks={artistTracks}
        onPlayTrack={(track) => handlePlayTrack(track, { tracks: artistTracks }, { type: 'artist', artist: currentArtist })}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        userPlaylists={userPlaylists}
        userFolders={userFolders}
        onAddToPlaylist={handleAddTrackToPlaylist}
        likedTrackIds={likedTrackIds}
        onToggleLike={handleToggleLike}
        user={user}
        onShowToast={(msg) => setToast({ isVisible: true, message: msg })}
        userIsPremium={userIsPremium}
        onOpenSubscription={handleOpenSubscription}
      />
    );
  } else if (view === "playlist") {
    mainContent = (
      <PlaylistView
        playlist={currentPlaylist}
        tracks={playlistTracks}
        onPlayTrack={(track) => handlePlayTrack(track, { tracks: playlistTracks }, { type: currentPlaylist?.is_admin ? 'admin-playlist' : 'playlist', id: currentPlaylist?.id, name: currentPlaylist?.name })}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        userPlaylists={userPlaylists}
        userFolders={userFolders}
        onAddToPlaylist={handleAddTrackToPlaylist}
        likedTrackIds={likedTrackIds}
        onToggleLike={handleToggleLike}
        onRemoveFromPlaylist={handleRemoveFromPlaylist}
        onShowToast={(msg) => setToast({ isVisible: true, message: msg })}
        userIsPremium={userIsPremium}
        user={user}
        onOpenSubscription={handleOpenSubscription}
      />
    );
  } else if (view === "profile") {
    mainContent = (
      <ProfileView
        user={user}
        userPlaylists={userPlaylists}
        likedTracks={likedTracks}
        stories={stories}
        storiesEnabled={storiesEnabled}
        onOpenStory={handleOpenStoryById}
        onDeleteStory={handleDeleteStory}
        onPlayTrack={(track) => handlePlayTrack(track, { tracks: likedTracks }, { type: 'liked' })}
        onToggleLike={handleToggleLike}
        currentTrack={currentTrack}
        likedTrackIds={likedTrackIds}
        playCollectionFirstTrack={playCollectionFirstTrack}
        onUpdateProfileImage={handleUpdateProfileImage}
        onUpdateCoverImage={handleUpdateCoverImage}
      />
    );
  } else if (view === "personal-info") {
    mainContent = (
      <PersonalInfoView
        user={user}
        onUpdateInfo={handleUpdatePersonalInfo}
        onBack={() => setView("home")}
      />
    );
  } else if (view === "address") {
    mainContent = (
      <AddressView
        user={user}
        onUpdateAddress={handleUpdateAddress}
        onBack={() => setView("home")}
      />
    );
  } else if (view === "subscription") {
    mainContent = (
      <SubscriptionView
        user={user}
        onSubscribe={handleSubscribe}
        onBack={() => setView("home")}
      />
    );
  } else if (view === "cancel-account") {
    mainContent = (
      <CancelAccountView
        onCancel={handleCancelAccount}
        onBack={() => setView("home")}
      />
    );
  } else if (view === "collection") {
    mainContent = (
      <CollectionView
        collection={currentCollection}
        onPlayTrack={handlePlayTrack}
        onTogglePlayCollection={() => currentCollection && playCollectionFirstTrack(currentCollection)}
        isPlayingCollection={isPlayingCollection}
        user={user}
        currentTrack={currentTrack}
      />
    );
  } else if (view === "search" && searchQuery.trim()) {
    mainContent = (
      <SearchView
        query={searchQuery}
        allTracks={allTracks}
        collections={collections}
        onPlayTrackFromSearch={(track) => {
          if (!user) {
            openAuthModal();
            return;
          }
          // Lecture depuis la recherche : rester dans les catégories du titre
          handlePlayTrack(track, null, { type: 'search' });
        }}
        onOpenCollection={openCollectionPage}
        currentTrack={currentTrack}
        moodCategories={moodCategories}
        onSelectMoodCategory={(mood) => {
          // Naviguer vers la page du mood sélectionné
          setView('home');
          // Utiliser un délai pour s'assurer que la vue est changée avant de définir le filtre
          setTimeout(() => {
            // Déclencher un événement personnalisé pour ouvrir le mood
            window.dispatchEvent(new CustomEvent('openMoodCategory', { detail: mood }));
          }, 100);
        }}
      />
    );
  } else {
    mainContent = (
      <HomeView
        allTracks={allTracks}
        recommendations={recommendations}
        likedTracks={likedTracks}
        recentlyPlayed={uniqueRecentlyPlayed}
        homeHistory={homeHistory}
        adminPlaylists={adminPlaylists}
        onPlayTrack={handlePlayTrack}
        onOpenArtist={openArtistPage}
        onToggleLike={handleToggleLike}
        onOpenPlaylist={handleOpenPlaylistFromHistory}
        currentTrack={currentTrack}
        currentCollection={currentCollection}
        likedTrackIds={likedTrackIds}
        playCollectionFirstTrack={playCollectionFirstTrack}
        user={user}
        isPlaying={isPlaying}
        userIsPremium={userIsPremium}
        userPlaylists={userPlaylists}
        userFolders={userFolders}
        onAddToPlaylist={handleAddTrackToPlaylist}
        onShowToast={(msg) => setToast({ isVisible: true, message: msg })}
        onOpenAuthModal={() => openAuthModal()}
        onOpenSubscription={handleOpenSubscription}
        stories={stories}
        storiesEnabled={storiesEnabled}
        onCreateStory={handleCreateStory}
        onReportStory={handleReportStory}
        onDeleteStory={handleDeleteStory}
        openStoryIndex={openStoryIndex}
        setOpenStoryIndex={setOpenStoryIndex}
        shouldPauseStory={shouldPauseStory}
        onStoryPlayRequest={handleStoryPlayRequest}
        onRefreshStories={refreshStories}
      />
    );
  }

  const shouldForceAuthModal = isMobileScreen && !user && !authLoading && !token;
  const shouldShowMobileGate = isMobileScreen && isOffline;

  if (shouldShowMobileGate) {
    const gateSubtitle = isOffline
      ? (!user
          ? 'Connectez-vous pour continuer. Vous êtes hors ligne.'
          : 'Vous êtes hors ligne. Vérifiez votre connexion.')
      : 'Connectez-vous pour continuer.';
    return (
      <div className="w-full h-screen bg-[#0b0b12] text-white flex items-center justify-center px-6 py-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1b1026] via-[#0b0b12] to-black"></div>
        <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_20%_20%,rgba(130,90,255,0.35),transparent_55%),radial-gradient(circle_at_80%_10%,rgba(255,110,199,0.25),transparent_50%),radial-gradient(circle_at_30%_80%,rgba(80,140,255,0.25),transparent_55%)]"></div>
        <div className="absolute inset-0 opacity-15 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI2IiBjeT0iNiIgcj0iMS41IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMyIvPjwvc3ZnPg==')]"></div>

        <div className="relative z-10 w-full max-w-sm rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-6">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-white/80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 8.82a15 15 0 0 1 20 0" />
              <path d="M5 12.06a10 10 0 0 1 14 0" />
              <path d="M8.5 15.3a5 5 0 0 1 7 0" />
              <line x1="12" y1="20" x2="12" y2="20" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold">Connexion</h1>
          <p className="text-white/60 mt-1">{gateSubtitle}</p>

          <button
            onClick={() => openAuthModal()}
            className="mt-6 w-full py-3 rounded-full font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            Se connecter
          </button>

          {isOffline && (
            <div className="mt-4 text-xs text-white/40">
              Activez le Wi-Fi ou les données mobiles pour retrouver tout le contenu.
            </div>
          )}
        </div>

        <AuthModal
          isOpen={showAuthModal || shouldForceAuthModal}
          onClose={() => setShowAuthModal(false)}
          disableClose={shouldForceAuthModal}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-screen bg-black text-white flex flex-col">
      {/* Publicité audio - remplace le lecteur principal */}
      {showAudioAd && currentAudioAd && (
        <AudioAdPlayer
          ad={currentAudioAd}
          onComplete={handleAudioAdComplete}
          onSkip={handleAudioAdComplete}
        />
      )}
      
      <TopBar
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        canGoBack={view !== "home"}
        onBack={() => {
          setShowCreatorStudio(false);
          setSearchQuery("");
          setView("home");
        }}
        user={user}
        onLogin={() => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          openAuthModal();
        }}
        onLogout={() => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          logout();
        }}
        isAdmin={isAdmin()}
        onAdminClick={() => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          navigate('/admin');
        }}
        onProfileClick={() => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          handleGoToProfile();
        }}
        onAccountClick={() => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          if (isMobileScreen) {
            setShowPersonalInfoSheet(true);
          } else {
            setShowAccountPanel(true);
          }
        }}
        onLanguageClick={() => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          setShowLanguageSwitcher(true);
        }}
        notifications={notifications}
        onMarkNotificationAsRead={(notificationId) => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          handleMarkNotificationAsRead(notificationId);
        }}
        onNotificationClick={(notification) => {
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          handleNotificationClick(notification);
        }}
        onGoHome={() => {
          // Revenir à l'accueil + remettre le filtre sur "Tout"
          setShowCreatorStudio(false);
          setOpenStoryIndex(null);
          window.dispatchEvent(new CustomEvent('resetHomeFilter'));
          setSearchQuery("");
          setView("home");
        }}
        navigate={navigate}
        showCreatorStudio={showCreatorStudio}
        onCreatorStudioClick={handleCreateStory}
      />

      {/* Language Switcher Modal */}
      {showLanguageSwitcher && (
        <LanguageSwitcher onClose={() => setShowLanguageSwitcher(false)} />
      )}

      {/* Account Settings Bottom Sheet (mobile) */}
      {showPersonalInfoSheet && (
        <MobileBottomSheet isOpen={showPersonalInfoSheet} onClose={() => setShowPersonalInfoSheet(false)}>
          <AccountSettingsPanel
            isOpen={true}
            onClose={() => setShowPersonalInfoSheet(false)}
            onSelectOption={(optionId) => {
              setShowPersonalInfoSheet(false);
              setView(optionId);
            }}
            isEmbedded
          />
        </MobileBottomSheet>
      )}

      {/* Bandeau d'avertissement d'expiration (sauf pour les admins) */}
      {user && user.status !== 'ADMIN' && subscriptionStatus && subscriptionStatus.is_active && showBanner && (
        <SubscriptionBanner
          daysRemaining={subscriptionStatus.days_remaining}
          onGoToSubscription={() => setView('subscription')}
          onDismiss={() => setShowBanner(false)}
        />
      )}

      {/* Account Settings Panel */}
      <AccountSettingsPanel
        isOpen={showAccountPanel}
        onClose={() => setShowAccountPanel(false)}
        onSelectOption={handleAccountPanelSelect}
      />

      {/* Modal d'expiration d'abonnement */}
      <SubscriptionExpiredModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onGoToSubscription={() => {
          setShowSubscriptionModal(false);
          setView('subscription');
        }}
        subscriptionType={user?.subscription_type}
      />

      <div className="flex-1 flex overflow-hidden">
        {showCreatorStudio ? (
          <section className="flex-1 overflow-hidden p-0 bg-[#0d0d14]">
            <CreatorStudio
              embedded
              onClose={() => setShowCreatorStudio(false)}
              allTracks={allTracks}
              moodCategories={moodCategories}
              onStoryPublished={refreshStories}
              userIsPremium={userIsPremium}
              onOpenSubscription={() => {
                setShowCreatorStudio(false);
                setView('subscription');
              }}
            />
          </section>
        ) : (
          <>
            {/* Sidebar gauche - masquée sur mobile, visible sur desktop */}
            {user && (
              <div
                className={`${showMobileLibrary ? 'fixed left-0 right-0 z-50 md:relative md:top-0 md:bottom-0 md:block h-full' : 'hidden md:block h-full'}`}
                style={
                  showMobileLibrary
                    ? {
                        top: 'var(--topbar-height, 64px)',
                        bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))'
                      }
                    : undefined
                }
              >
                {showMobileLibrary && (
                  <div 
                    className="absolute inset-0 bg-black md:hidden" 
                    onClick={() => setShowMobileLibrary(false)}
                  />
                )}
                <div className={`${showMobileLibrary ? 'relative z-10 h-full' : 'h-full'}`}>
                  <LeftSidebar
                    onSelectCollection={(collection) => {
                      setOpenStoryIndex(null);
                      playCollectionFirstTrack(collection);
                      setShowMobileLibrary(false);
                    }}
                    currentCollectionId={currentCollection && currentCollection.id}
                    onPlayTrack={handlePlayTrack}
                    currentTrack={currentTrack}
                    user={user}
                    onCreateClick={handleCreateClick}
                    likedTracks={likedTracks}
                    userPlaylists={userPlaylists}
                    recentlyPlayed={uniqueRecentlyPlayed}
                    isPlaying={isPlaying}
                    onOpenArtist={(track) => {
                      setOpenStoryIndex(null);
                      openArtistPage(track);
                      setShowMobileLibrary(false);
                    }}
                    onDeletePlaylist={handleDeletePlaylist}
                    userIsPremium={userIsPremium}
                    mobileBottomPadding={showMobileLibrary ? mobileContentPaddingBottom : ''}
                  />
                </div>
              </div>
            )}

            <main className="flex-1 flex overflow-hidden">
              <section
                ref={mainScrollRef}
                className={`
                  flex-1 ${isStoryViewerOpen && isMobileViewport ? 'overflow-hidden' : 'overflow-auto'}
                  ${
                    isStoryViewerOpen && isMobileViewport
                      ? 'px-0 py-0'
                      : isEdgeToEdgeView
                        ? (isMobileViewport ? 'px-0 py-0' : 'px-0 py-4')
                        : 'px-4 md:px-6 py-4'
                  }
                  ${
                    isStoryViewerOpen && isMobileViewport
                      ? 'pb-0'
                      : (currentTrack && !showAudioAd ? 'pb-36 md:pb-24' : 'pb-36 md:pb-4')
                  }
                  bg-gradient-to-b
                  from-[#2a2430]
                  via-[#1c1721]
                  to-[#121212]
                  ${isStoryViewerOpen && isMobileViewport ? 'rounded-none' : 'rounded-tl-2xl rounded-tr-2xl md:rounded-tl-2xl'}
                `}
                style={mobileContentPaddingBottom ? { paddingBottom: mobileContentPaddingBottom } : undefined}
              >
                {mainContent}
              </section>

              {/* Sidebar droite - masquée sur mobile, visible sur desktop */}
              {!isStoryViewerOpen && (
                <div className="hidden lg:block h-full overflow-hidden">
                  <RightPanel currentTrack={currentTrack} currentArtistData={currentArtistData} currentCollection={currentCollection} isPlaying={isPlaying} user={user} userIsPremium={userIsPremium} />
                </div>
              )}
            </main>
          </>
        )}
      </div>

      {/* Player principal - masqué pendant les pubs audio */}
      {currentTrack && !showAudioAd && !showCreatorStudio && !(isStoryViewerOpen && isMobileViewport) && (
        <PlayerBar
          currentTrack={currentTrack}
          isPlaying={isPlaying}
          progress={progress}
          onTogglePlay={handleTogglePlay}
          onNext={handleNext}
          onPrev={handlePrev}
          onSeek={handleSeek}
          isShuffleActive={isShuffleActive}
          onToggleShuffle={handleToggleShuffle}
          isRepeatActive={isRepeatActive}
          onToggleRepeat={handleToggleRepeat}
          hasBottomNav={!!user}
          trackSource={trackSource}
          onNavigateToSource={handleNavigateToSource}
          onOpenArtist={openArtistPage}
          userPlaylists={userPlaylists}
          userFolders={userFolders}
          onAddToPlaylist={handleAddTrackToPlaylist}
          onToggleLike={handleToggleLike}
          likedTrackIds={likedTrackIds}
          onShowToast={(msg) => setToast({ isVisible: true, message: msg })}
          userIsPremium={userIsPremium}
          user={user}
          onOpenSubscription={handleOpenSubscription}
          volume={volume}
          onVolumeChange={handleVolumeChange}
        />
      )}

      <AuthModal
        isOpen={showAuthModal || shouldForceAuthModal}
        onClose={() => setShowAuthModal(false)}
        disableClose={shouldForceAuthModal}
      />
      
      {/* Modal Premium pour les contenus bloqués */}
      <PremiumModal 
        isOpen={showPremiumModal} 
        onClose={() => {
          setShowPremiumModal(false);
          setBlockedTrack(null);
        }}
        track={blockedTrack}
      />

      <AdBlockModal
        isOpen={!!user && !userIsPremium && showAdBlockModal}
        onClose={() => setShowAdBlockModal(false)}
        onCheckAgain={() => {
          runAdBlockCheck(true);
        }}
      />
      
      {showCreateMenu && (
        <CreateMenu
          isOpen={showCreateMenu}
          onClose={() => setShowCreateMenu(false)}
          position={createMenuPosition}
          onCreatePlaylist={handleCreatePlaylist}
          onCreateFolder={handleCreateFolder}
          folders={userFolders}
        />
      )}

      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />

      {/* Publicité Interstitielle (Popup plein écran) */}
      <AdInterstitial
        isOpen={showAdInterstitial}
        onClose={handleAdInterstitialClose}
        userIsPremium={userIsPremium}
      />

      {/* Bottom Tab Navigation - visible uniquement sur mobile */}
      {user && !showCreatorStudio && (
        <nav 
          className="mobile-bottom-nav fixed bottom-0 left-0 right-0 bg-black border-t border-white/10 md:hidden"
          style={{ zIndex: 9998, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex justify-around items-center" style={{ height: '64px' }}>
            <button
              onClick={() => {
                setShowCreatorStudio(false);
                setOpenStoryIndex(null);
                window.dispatchEvent(new CustomEvent('resetHomeFilter'));
                setSearchQuery("");
                setMobileTab("home");
                setView("home");
                setShowMobileLibrary(false);
              }}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                mobileTab === "home" ? "text-white" : "text-white/60"
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              <span className="text-xs font-medium">Accueil</span>
            </button>

            <button
              onClick={() => {
                setOpenStoryIndex(null);
                setMobileTab("library");
                setShowMobileLibrary(true);
              }}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                mobileTab === "library" ? "text-white" : "text-white/60"
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              <span className="text-xs font-medium">Bibliothèque</span>
            </button>

            <button
              onClick={() => {
                setOpenStoryIndex(null);
                setMobileTab("creator");
                setShowMobileLibrary(false);
                handleCreateStory();
              }}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                mobileTab === "creator" ? "text-white" : "text-white/60"
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l2.39 4.84L20 7.6l-4 3.9.94 5.5L12 14.9 7.06 17l.94-5.5-4-3.9 5.61-.76L12 2z" />
              </svg>
              <span className="text-xs font-medium">Creator</span>
            </button>

            <button
              onClick={() => {
                setOpenStoryIndex(null);
                setMobileTab("account");
                setView("profile");
                setShowMobileLibrary(false);
              }}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                mobileTab === "account" ? "text-white" : "text-white/60"
              }`}
            >
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs font-medium">Profil</span>
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
